import { useState, useCallback, useEffect, useRef, Fragment } from "react";
import {
  fetchEntries, insertEntry, deleteEntryById, updateEntry,
  fetchMonthSettings, upsertMonthSettings,
  fetchAdjustments, insertAdjustment, deleteAdjustment, updateAdjustment,
  fetchUnhandledActions, checkDuplicateInvoice,
  fetchSendCycle, insertSendCycle,
  fetchHandledActions, insertHandledAction, deleteHandledAction, fetchDismissalLog,
  // Four confirmation helpers are deliberately NOT imported here, all for the
  // same reason: this app is admin-only as of 2026-09-01 and no longer holds a
  // rep-facing confirm surface. insertMonthConfirmation (legacy, no snapshot —
  // see the note on it in supabase.js), insertCommissionConfirmation,
  // bulkApproveEntries and fetchMonthConfirmation were all reached only through
  // the rep card and the previous-month banner, which are gone. Reps confirm in
  // the toolbox, and the admin on-behalf path lives there too
  // (certags-toolbox ActionCenter.jsx). If you find yourself re-importing one,
  // check first whether you are rebuilding a rep surface in the wrong app.
  fetchAllMonthConfirmations,
  searchEntriesGlobal,
  fetchCommissionConfirmations,
  fetchFlagLedgerContext,
  login, logout, getSession, supabase,
} from "./supabase";
import { buildCommissionSnapshot, diffSnapshots, confirmationStatus, latestConfirmation, CONFIRM_STATE } from "./commissionSnapshot";
import { realInvoiceOf, adjustmentInvoice, computeDifference, defaultPromoteAmount, adjustmentNote, parseAmountInput } from "./duplicateResolution";
import { isPendingAction, dismissPatch, undismissPatch, findDismissalLog, isUniqueViolation } from "./dismissal";
import { hasStatusComment, statusCommentPatch, resolveCommentValue, isCommentDirty, commentAuthorLabel, COMMENT_DEBOUNCE_MS } from "./statusComment";
import { FLAG_TYPE_META, flagTypeOf, isMissingInvoiceFlag, hasAmount, displayText, displayInvoice } from "./commFlagDisplay";

import { inDailyWindow, pacificTodayStr } from "./dailyWindow";
import { bizDaysInMonth, bizDaysElapsedTo } from "./bizDays";

// "October 2026" -> "October 2025" (prior-year label for the same month)
const priorYearLabel=m=>{const [mo,yr]=String(m||"").split(" ");const y=parseInt(yr,10);return Number.isFinite(y)?`${mo} ${y-1}`:m;};

// Admin status is NOT decided here. It comes from the database, via
// current_is_admin() in App() — see the gate there. There was a hardcoded
// ADMIN_EMAILS Set on this line until 2026-09-01, and having two definitions
// of "admin" in one file is the same defect as the two confirmation tables:
// they agree until they do not, and nothing notices when they stop. The
// database answer gates entry AND drives the UI, so there is one definition.
//
// Everything below this point runs only for a verified admin — App() renders
// Tracker for no one else. The rep-facing confirmation banner and commission
// card that used to live here were removed the same day: they were reachable
// only by a non-admin, and no non-admin can load this app. Reps confirm in
// the toolbox (certags.tools MyCommissions).

const REPS = {
  RTR: { name:"Roman",  email:"roman@certags.com",  tiers:{ new:.2,"0-12":.2,"1-2":.15,"2-3":.1,"3plus":.1,key:.1,loaner:0,nocomm:0,zerocomm:0 } },
  ZGC: { name:"Zack",   email:"zack@certags.com",   tiers:{ new:.2,"0-12":.2,"1-2":.15,"2-3":.1,"3plus":.1,key:.1,loaner:0,nocomm:0,zerocomm:0 } },
  AGL: { name:"Andrew", email:"andrew@certags.com", tiers:{ new:.2,"0-12":.2,"1-2":.15,"2-3":.1,"3plus":.1,key:.1,loaner:0,nocomm:0,zerocomm:0 } },
  JMG: { name:"Jose",   email:"jose@certags.com",   tiers:{ new:.2,"0-12":.2,"1-2":.15,"2-3":.1,"3plus":.1,key:.1,loaner:0,nocomm:0,zerocomm:0 } },
  TJR: { name:"Tanner", email:"tanner@certags.com", tiers:{ new:.1,"0-12":.1,"1-2":.075,"2-3":.05,"3plus":.02,key:.05,loaner:0,nocomm:0,zerocomm:0 } },
  DDJ: { name:"Doug",   email:"",                   tiers:{ new:.2,"0-12":.2,"1-2":.2,"2-3":.2,"3plus":.2,key:.2,generated:.2,inherited:.1,flat:.2,loaner:0,nocomm:0,zerocomm:0 } },
  JRC: { name:"Jake",   email:"jake@certags.com",   tiers:{ new:.1,"0-12":.02,"1-2":.02,"2-3":.02,"3plus":.02,key:.02,flat:.02,loaner:0,nocomm:0,zerocomm:0 } },
  CRC: { name:"Chris",  email:"ccollard@certags.com",tiers:{ new:0,"0-12":0,"1-2":0,"2-3":0,"3plus":0,key:0,inherited:0,generated:0,flat:0,loaner:0,nocomm:0,zerocomm:0 } },
};

const TIER_LABELS = { new:"New","0-12":"0–12 mo","1-2":"1–2 yr","2-3":"2–3 yr","3plus":"3+ yr",key:"KEY",inherited:"Inherited",generated:"Generated",flat:"Flat 2%",loaner:"Loaner",nocomm:"No Comm",zerocomm:"Zero Comm" };
const TIER_COLORS = {
  new:{bg:"#EAF3DE",color:"#3B6D11"},"0-12":{bg:"#E6F1FB",color:"#185FA5"},
  "1-2":{bg:"#FAEEDA",color:"#854F0B"},"2-3":{bg:"#FAECE7",color:"#993C1D"},
  "3plus":{bg:"#F1EFE8",color:"#5F5E5A"},key:{bg:"#FBEAF0",color:"#993556"},
  inherited:{bg:"#EEEDFE",color:"#534AB7"},generated:{bg:"#EAF3DE",color:"#3B6D11"},
  flat:{bg:"#F1EFE8",color:"#5F5E5A"},loaner:{bg:"#EEF2FF",color:"#3730A3"},
  nocomm:{bg:"#F1EFE8",color:"#5F5E5A"},zerocomm:{bg:"#FFF8E1",color:"#856404"},
};

const ACTION_TYPE_META = {
  "3rd_party":   { label:"3rd Party",   bg:"#FAECE7", color:"#993C1D", border:"#F0997B" },
  "duplicate":   { label:"Duplicate",   bg:"#E6F1FB", color:"#185FA5", border:"#B5D4F4" },
  "credit_memo": { label:"Credit Memo", bg:"#F5ECFA", color:"#6B21A8", border:"#D8B4FE" },
};

// The status-comment palette. Deliberately outside ACTION_TYPE_META's set — a
// comment says nothing about WHY an item was flagged, and borrowing an action
// type's colour would read as a fourth type.
const COMMENT_META = { bg:"#FDF6E3", color:"#8A6D1F", border:"#E8D9A8" };

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"].map(m=>`${m} 2026`);
const BLANK = { rep:"", invoice:"", customer:"", amount:"", feeAmount:"", tier:"new", isComplyPro:false, isPrinter:false, needs3rdParty:false, flaggedItems:[], isLoaner:false };
// Pacific, not the browser's calendar. Every date this app compares against —
// entries.date_str above all — is a Pacific business date, and the daily-window
// rule is stated in Pacific midnights. The old inline new Date() agreed for a
// Pacific viewer, which is everyone today, and was wrong from anywhere else.

function fmt(n){ return "$"+Number(n).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2}); }
function fmtS(n){ return n>=1000?"$"+(n/1000).toFixed(1)+"K":"$"+Math.round(n); }
function fmtRound(n){ return "$"+Math.round(n/100)*100; }
function getRate(rep,tier){ return REPS[rep]?.tiers[tier]||0; }
function getComm(amt,rep,tier){ return amt*0.9*getRate(rep,tier); }

// True when this entry represents a NEW ACCOUNT (first sale to this customer
// from this rep). For DDJ both new and existing accounts pay 20% on the
// "generated" tier, so tier alone can't tell them apart — the parser
// persists is_new_account from the rep-code suffix (N -> true, E/none ->
// false). For every other rep, tier === "new" is the long-standing signal
// and remains unchanged.
function isNewAccount(e){
  if(e.rep==="DDJ") return e.isNewAccount===true;
  return e.tier==="new";
}

// ── Reversal commission resolver ─────────────────────────────────────────────
// sales_reversal adjustments need a tier to run through getComm. New reversals
// carry tier on the row (saveReversal persists it). Legacy rows (pre-migration)
// had tier dropped on save; resolveReversalTier falls back to the referenced
// original invoice's tier when possible, else returns null. A null tier means
// UNRESOLVED — the reversal is excluded from the headline commission and
// surfaced as a yellow warning rather than silently computed as $0, so a
// missing tier can never quietly turn into a wrong payout.
function resolveReversalTier(adj, monthEntries){
  if(adj.tier) return adj.tier;
  if(adj.invoice_ref){
    const ref = String(adj.invoice_ref).trim();
    const match = (monthEntries||[]).find(e => String(e.invoice) === ref && e.rep === adj.rep);
    if(match && match.tier) return match.tier;
  }
  return null;
}
function reversalCommImpact(adj, monthEntries){
  const tier = resolveReversalTier(adj, monthEntries);
  if(!tier) return null;
  return getComm(parseFloat(adj.amount), adj.rep, tier);
}
// Aggregate a list of sales_reversal adjustments for one rep. Returns the
// formula-applied commission total for resolved reversals plus a list of any
// that couldn't be resolved (so the UI can surface the warning).
function aggregateReversals(adjs, monthEntries){
  let commTotal = 0;
  const unresolved = [];
  for(const a of (adjs||[])){
    const impact = reversalCommImpact(a, monthEntries);
    if(impact === null) unresolved.push(a);
    else commTotal += impact;
  }
  return { commTotal, unresolved };
}

// ── The confirmable state for one rep+month ────────────────────────────────
// ONE definition. Used by the rep's own badge, the admin Confirmation Status
// grid, the dashboard confirm card, and the previous-month banner.
//
// It takes the month's rows as ARGUMENTS rather than closing over the selected
// month's state, and that is the whole reason it exists as a module-level
// function. The banner confirms the PREVIOUS calendar month, which is usually
// not the month the page has loaded — so a snapshot built from component state
// would have recorded September's orders against an August confirmation. No
// error, and undiffable forever after. Pass the rows for the month you are
// actually confirming.
//
// Filters are the page's own, deliberately: a snapshot built off a different
// rule would make a rep stale for something they were never shown, and
// duplicates and credit memos would leak into a confirmation the page never
// rendered. Adjustments and resolved reversals fold into total_commission the
// same way the rep view totals them, so an adjustment-only correction still
// stales the confirmation.
function buildRepSnapshot({rep,month,entries,adjustments}){
  const allMonthEntries=(entries||[]).filter(e=>e.month===month);
  const monthEntries=allMonthEntries.filter(e=>e.actionType!=="duplicate"&&e.actionType!=="credit_memo");
  const salesEntries=allMonthEntries.filter(e=>
    e.tier!=="loaner"&&
    e.tier!=="nocomm"&&
    e.amount!==0&&
    e.actionType!=="duplicate"&&
    e.actionType!=="credit_memo"
  );
  const mine=(adjustments||[]).filter(a=>a.rep===rep&&a.month===month);
  const revs=mine.filter(a=>a.type==="sales_reversal");
  const rest=mine.filter(a=>a.type!=="sales_reversal");
  return buildCommissionSnapshot({
    rep,
    month,
    entries:salesEntries.filter(e=>e.rep===rep),
    commissionFor:e=>getComm(e.amount,e.rep,e.tier),
    adjustmentsCommission:rest.reduce((s,a)=>s+parseFloat(a.amount),0)
      +aggregateReversals(revs,monthEntries).commTotal,
  });
}

function parseMonthYear(label){
  const ms=["January","February","March","April","May","June","July","August","September","October","November","December"];
  const [m,y]=label.split(" ");
  return {month:ms.indexOf(m),year:parseInt(y)};
}

const CP_PRINTER_PARTS=["CP-BP530","CP-TT027","BP530"];
const CP_SUPPLY_REGEX=/\bCP[0-9A-Z][-_]?\w+|EXT-HOLDER|CP-CUSTOM|CPE-CUSTOM\b/i;
function detectComplyPro(emailText,rep){
  if(rep==="DDJ")return{isComplyPro:true,isPrinter:false,confident:true};
  const upper=emailText.toUpperCase();
  const hasPrinter=CP_PRINTER_PARTS.some(p=>upper.includes(p.toUpperCase()));
  const hasSupply=CP_SUPPLY_REGEX.test(emailText);
  const isCP=hasPrinter||hasSupply;
  return{isComplyPro:isCP,isPrinter:hasPrinter,confident:isCP};
}

const THIRD_PARTY_PATTERNS=[
  {pattern:"METAL-LABEL",label:"Metal Labels"},{pattern:"METAL-LABE",label:"Metal Labels"},
  {pattern:"METAL-LAB",label:"Metal Labels"},{pattern:"METAL-TAG",label:"Metal Tags"},
  {pattern:"EMBOSSED-LABEL",label:"Embossable Labels"},{pattern:"EMBOSSED-LAB",label:"Embossable Labels"},
  {pattern:"EMBOSSED-LA",label:"Embossable Labels"},{pattern:"EMBOSSED-",label:"Embossable Labels"},
  {pattern:"CARDSTOCK",label:"Cardstock Tags"},{pattern:"CARD-TAG",label:"Cardstock Tags"},
  {pattern:"CARD-STOCK",label:"Cardstock Tags"},{pattern:"POLY",label:"Poly Tags"},
  {pattern:"FLEX-TAG",label:"Flex Tags"},{pattern:"RIGID-TAG",label:"Rigid Tags"},
  {pattern:"RUBBER-TAG",label:"Rubber Tags"},{pattern:"ECON-VINYL-LAB",label:"Economy Labels"},
  {pattern:"ECON-VINYL-L",label:"Economy Labels"},{pattern:"ECON-VINYL-",label:"Economy Labels"},
  {pattern:"ECON-VINY",label:"Economy Labels"},{pattern:"HEXTAG-12",label:"Hex Tags"},
  {pattern:"LOCK-TAG",label:"Lock Tags"},{pattern:"BREAK-AWAY",label:"Break-Away Tags"},
  {pattern:"BREAK-AWA",label:"Break-Away Tags"},{pattern:"SCAFFOLD",label:"Scaffold Tags"},
  {pattern:"BADGE",label:"Badges"},{pattern:"INSERT",label:"Inserts"},
  {pattern:"MAGNETIC",label:"Magnetic Items"},{pattern:"MAGNET",label:"Magnets"},
  {pattern:"TAMPER-EVIDENT",label:"Tamper-Evident Tags"},
  {pattern:"TAMPER EVIDENT",label:"Tamper-Evident Tags"},
  {pattern:"CUSTOM",label:"Custom Items"},{pattern:"CP-CUSTOM",label:"CP Custom Labels"},
  {pattern:"CATTLE-TAG",label:"Cattle Tags"},{pattern:"CATTLE",label:"Cattle Tags"},
];
// MISC is special-cased in detect3rdParty — bare MISC flags, but stock
// in-house SKUs (MISC:PUNCH, MISC-APPLICATOR, MISC-METAL, …) are excluded so
// legitimate line items don't trip 3rd party.

const ZERO_COMM_PATTERNS=[
  "PLATE-FEE","PLATE FEE","PLATEFEE","CUTTER-FEE","CUTTER FEE","CUTTERFEE",
  "SETUP-FEE","SETUP FEE","SETUPFEE","ARTWORK","ART-FEE","ART FEE",
  "HANDLING-FEE","HANDLING FEE","HANDLING","RUSH-FEE","RUSH FEE","RUSHFEE","REPRINT",
];

function detectLoaner(emailText){
  const upper=emailText.toUpperCase();
  const keptPatterns=["KEPT FROM LOANER","KEEP FROM LOANER","KEEPING LOANER","LOANER KEPT","LOANER KEEP","LOANER TO SALE","LOANER PURCHASE","PURCHASING LOANER","BOUGHT FROM LOANER","BUY FROM LOANER"];
  if(keptPatterns.some(p=>upper.includes(p)))return false;
  return upper.includes("LOANER");
}
function detectNoComm(emailText,rep){
  if(rep==="CRC")return false;
  const upper=emailText.toUpperCase();
  if(upper.includes("LOANER"))return false;
  return upper.includes("NO COMM")||upper.includes("NO COMMS")||upper.includes("NON COMM")||
    upper.includes("NON-COMM")||upper.includes("REPLACEMENT SUPPLY")||upper.includes("SUPPLY REPLACEMENT")||
    upper.includes("REPLACEMENT MATERIAL")||upper.includes("REPLACEMENT SUPPLIES");
}
function detectZeroComm(emailText,rep){
  if(rep==="CRC")return false;
  const upper=emailText.toUpperCase();
  if(upper.includes("LOANER"))return false;
  if(detectNoComm(emailText,rep))return false;
  return ZERO_COMM_PATTERNS.some(p=>upper.includes(p.toUpperCase()));
}
// Mirror Zapier extractLineItems: limit 3rd-party detection to the line-item
// slice (between the first 'Activity' header and the 'SubTotal:' total) so
// Special Instructions text can't false-positive. Falls back to the full body
// when either marker is missing.
function extractLineItems(emailText){
  const start=emailText.indexOf("Activity");
  const end=emailText.indexOf("SubTotal:");
  if(start===-1||end===-1||end<=start)return emailText;
  return emailText.slice(start+"Activity".length,end);
}
function detect3rdParty(emailText){
  const slice=extractLineItems(emailText);
  const upper=slice.toUpperCase();
  const flagged=THIRD_PARTY_PATTERNS.filter(p=>{
    const regex=new RegExp("(^|[^A-Z])"+p.pattern+"([^A-Z]|$)");
    return regex.test(upper);
  }).map(p=>p.label);
  // MISC: standalone keyword flags, but explicit in-house SKUs are excluded.
  if(/\bMISC\b/i.test(slice)&&!/MISC[-:]?(ZIPTAG-APPL|APPLICATOR|PUNCH|CUTTER|PLATE|METAL|ZIP)/i.test(slice)){
    flagged.push("Misc (3rd party)");
  }
  return{needs3rdParty:flagged.length>0,flaggedItems:[...new Set(flagged)]};
}

function Badge({tier,rep}){
  if(rep==="CRC")return<span style={{background:"#F1EFE8",color:"#5F5E5A",padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:500,whiteSpace:"nowrap"}}>Non-comm</span>;
  const c=TIER_COLORS[tier]||{bg:"#eee",color:"#333"};
  return<span style={{background:c.bg,color:c.color,padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:500,whiteSpace:"nowrap"}}>{TIER_LABELS[tier]||tier}</span>;
}
function MetricCard({label,value,sub,highlight,warn,onClick}){
  return(
    <div onClick={onClick} style={{background:highlight?"#111":warn?"#fffbe6":"#f5f5f3",borderRadius:8,padding:"12px 16px",textAlign:"center",cursor:onClick?"pointer":undefined,border:warn?"0.5px solid #f5d060":"none"}}>
      <div style={{fontSize:12,color:highlight?"#aaa":warn?"#b8860b":"#888",marginBottom:4}}>{label}</div>
      <div style={{fontSize:18,fontWeight:500,color:highlight?"#fff":warn?"#7a5c00":"#111"}}>{value}</div>
      {sub&&<div style={{fontSize:12,color:highlight?"#ccc":warn?"#b8860b":"#888",marginTop:3}}>{sub}</div>}
    </div>
  );
}
function ProgressBar({pct,color="#111"}){
  return(
    <div style={{height:6,borderRadius:3,background:"#e8e8e8",overflow:"hidden",marginTop:6}}>
      <div style={{height:"100%",borderRadius:3,background:color,width:`${Math.min(100,Math.max(0,Math.round(pct)))}%`,transition:"width .4s"}}/>
    </div>
  );
}
function Spinner(){return<div style={{display:"inline-block",width:16,height:16,border:"2px solid #e0e0e0",borderTopColor:"#111",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>;}

// ── What changed since a confirmation ───────────────────────────────────────
// One component, rendered inline for the rep and inside a modal for admin, so
// the two cannot describe the same change differently. A rep asked to reconfirm
// and an admin deciding whether to chase them must be looking at the same list.
function ConfirmationDiff({diff,confirmedAt,compact}){
  if(!diff||diff.count===0)return null;
  const row=(bg,color,label,text,key)=>(
    <div key={key} style={{display:"flex",gap:8,alignItems:"baseline",padding:"5px 0",borderBottom:"0.5px solid #f0f0f0",fontSize:12}}>
      <span style={{background:bg,color,padding:"1px 7px",borderRadius:20,fontSize:10,fontWeight:600,whiteSpace:"nowrap",textTransform:"uppercase",letterSpacing:".04em"}}>{label}</span>
      <span style={{color:"#374151",lineHeight:1.5}}>{text}</span>
    </div>
  );
  return(
    <div style={{background:"#FFFBEB",border:"0.5px solid #FDE68A",borderRadius:10,padding:"12px 16px",marginBottom:compact?"1rem":0}}>
      <div style={{fontSize:12,fontWeight:600,color:"#92400E",marginBottom:8}}>
        {diff.count} change{diff.count===1?"":"s"} since {confirmedAt?`your confirmation on ${new Date(confirmedAt).toLocaleDateString()}`:"confirmation"}
      </div>
      {diff.added.map(e=>row("#EAF3DE","#3B6D11","added",
        `#${e.invoice} — ${fmt(e.amount)}, commission ${fmt(e.commission)}`,`a${e.entry_id}`))}
      {diff.removed.map(e=>row("#FEE2E2","#991B1B","removed",
        `#${e.invoice} — was ${fmt(e.amount)}, commission ${fmt(e.commission)}`,`r${e.entry_id}`))}
      {diff.modified.map(m=>row("#E6F1FB","#185FA5","changed",
        `#${m.invoice} — ${m.fields.map(f=>
          f==="tier"?`tier ${m.was.tier||"—"} → ${m.now.tier||"—"}`
                    :`${f} ${fmt(m.was[f])} → ${fmt(m.now[f])}`).join(", ")}`,`m${m.entry_id}`))}
      {diff.totalChanged&&row("#F1EFE8","#5F5E5A","total",
        `Total commission ${fmt(diff.totalChanged.from)} → ${fmt(diff.totalChanged.to)}`,"t")}
    </div>
  );
}

function LoginScreen({onAuth}){
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [error,setError]=useState("");
  const [loading,setLoading]=useState(false);
  const [shake,setShake]=useState(false);

  const attempt=async()=>{
    if(!email||!password)return;
    setLoading(true);setError("");
    try{
      await login(email,password);
      onAuth();
    }catch(e){
      setError("Incorrect email or password");
      setShake(true);setPassword("");
      setTimeout(()=>{setError("");setShake(false);},800);
    }
    setLoading(false);
  };

  return(
    <div style={{minHeight:"100vh",background:"#f5f5f3",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"system-ui,sans-serif"}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}`}</style>
      <div style={{width:320,textAlign:"center"}}>
        <div style={{fontSize:13,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"#111",marginBottom:8}}>Certags</div>
        <div style={{fontSize:13,color:"#aaa",marginBottom:32}}>Sales Tracker</div>
        <div style={{animation:shake?"shake 0.4s ease":"none"}}>
          <input type="email" value={email} onChange={e=>{setEmail(e.target.value);setError("");}}
            onKeyDown={e=>e.key==="Enter"&&attempt()} placeholder="Email address" autoFocus
            style={{width:"100%",padding:"12px 16px",fontSize:15,border:"0.5px solid #ccc",borderRadius:10,background:"#fff",color:"#111",boxSizing:"border-box",textAlign:"center",outline:"none",marginBottom:10}}/>
          <input type="password" value={password} onChange={e=>{setPassword(e.target.value);setError("");}}
            onKeyDown={e=>e.key==="Enter"&&attempt()} placeholder="Password"
            style={{width:"100%",padding:"12px 16px",fontSize:15,border:"0.5px solid #ccc",borderRadius:10,background:"#fff",color:"#111",boxSizing:"border-box",textAlign:"center",outline:"none",marginBottom:10}}/>
          <button onClick={attempt} disabled={loading} style={{width:"100%",padding:"12px",fontSize:14,border:"none",borderRadius:10,background:"#111",color:"#fff",cursor:"pointer",fontWeight:500,opacity:loading?0.7:1}}>
            {loading?"Signing in…":"Continue"}
          </button>
        </div>
        <div style={{height:20,marginTop:8,fontSize:13,color:"#c00"}}>{error}</div>
      </div>
    </div>
  );
}

function buildRepExcel(entries, month, code, adjOverrides){
  const XLSX=window.XLSX;
  const wb=XLSX.utils.book_new();
  const repName=REPS[code]?.name||code;
  const monthParts=month.split(" ");
  const shortMonth=monthParts[0].slice(0,3)+" "+monthParts[1];
  const allEs=entries.filter(e=>e.month===month&&e.rep===code&&e.actionType!=="duplicate"&&e.actionType!=="credit_memo");
  const commEs=allEs.filter(e=>e.tier!=="loaner"&&e.tier!=="nocomm"&&e.amount!==0);
  const nonCommEs=allEs.filter(e=>e.tier==="loaner"||e.tier==="nocomm");
  const adjs=adjOverrides||[];
  const bonuses=adjs.filter(a=>a.type==="bonus");
  const withholdings=adjs.filter(a=>a.type==="withholding"||a.type==="return");
  const salesReversals=adjs.filter(a=>a.type==="sales_reversal");
  const zeroCommAdjs=adjs.filter(a=>a.type==="zero_comm"||a.type==="reduced_comm");
  const bonusTotal=bonuses.reduce((s,a)=>s+parseFloat(a.amount),0);
  const withholdTotal=withholdings.reduce((s,a)=>s+parseFloat(a.amount),0);
  const baseComm=commEs.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
  // Reversal commission via the shared resolver. Same formula as Dashboard +
  // Payroll, so the rep's statement here matches what they're paid.
  // Unresolved reversals (no tier) contribute 0 to commission — same
  // safe-by-default behavior as the in-app views (the in-app UI also surfaces
  // a yellow ⚠ on those rows).
  const {commTotal:reversalCommTotal}=aggregateReversals(salesReversals, allEs);
  const totalComp=baseComm+bonusTotal+withholdTotal+zeroCommAdjs.reduce((s,a)=>s+parseFloat(a.amount),0)+reversalCommTotal;
  const positiveEs=commEs.filter(e=>e.amount>0);
  const totalOrders=positiveEs.length;
  const avgSale=totalOrders>0?positiveEs.reduce((s,e)=>s+e.subTotal,0)/totalOrders:0;
  const totalSalesRaw=commEs.reduce((s,e)=>s+e.subTotal,0);
  const salesRevTotal=salesReversals.reduce((s,a)=>s+parseFloat(a.amount),0);
  const totalSales=totalSalesRaw+salesRevTotal;

  if(code==="JRC"){
    const newEs=commEs.filter(e=>e.tier==="new");
    const existingEs=commEs.filter(e=>e.tier!=="new");
    const newTotal=newEs.reduce((s,e)=>s+e.subTotal,0);
    const existingTotal=existingEs.reduce((s,e)=>s+e.subTotal,0);
    const newComm=newEs.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
    const existingComm=existingEs.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
    const L=[];
    L.push([repName]);
    L.push(["Total Sales:", parseFloat(totalSales.toFixed(2))]);
    L.push(["# of Sales:", totalOrders]);
    L.push([]);
    L.push(["New Sales #", newEs.length]);
    L.push(["New Sales Total:", parseFloat(newTotal.toFixed(2))]);
    L.push(["Commissionable New:", parseFloat((newTotal*0.9).toFixed(2))]);
    L.push(["New Sales Spiff 10%", parseFloat(newComm.toFixed(2))]);
    L.push([]);
    L.push(["Existing Sales Total:", parseFloat(existingTotal.toFixed(2))]);
    L.push(["Commissionable Existing:", parseFloat((existingTotal*0.9).toFixed(2))]);
    L.push(["Existing Sales Spiff 2%", parseFloat(existingComm.toFixed(2))]);
    L.push([]);
    L.push(["Bonuses:", parseFloat(bonusTotal.toFixed(2))]);
    L.push(["Withholdings:", parseFloat(withholdTotal.toFixed(2))]);
    L.push(["Additional Comp:", 0]);
    L.push([]);
    L.push(["Lead Spiff $20", 0]);
    L.push(["Total Spiff", parseFloat(totalComp.toFixed(2))]);
    L.push([]);
    L.push(["Avg Sale:", parseFloat(avgSale.toFixed(2))]);
    withholdings.forEach(a=>{if(a.note)L.push([a.note]);});
    bonuses.forEach(a=>{if(a.note)L.push([a.note]);});
    const R=[];
    R.push(["Invoice","Customer","Amount","New"]);
    commEs.forEach(e=>{R.push(["#"+e.invoice, e.customer, parseFloat(e.subTotal.toFixed(2)), e.tier==="new"?"Y":""]);});
    if(nonCommEs.length>0){R.push([]);R.push(["— Non-Commissioned Items —"]);nonCommEs.forEach(e=>{R.push(["#"+e.invoice, e.customer, e.subTotal, e.tier]);});}
    const maxR=Math.max(L.length, R.length+1);
    const SR=[];
    SR.push([`${repName} — ${month} Commission Statement`,"","","","","",""]);
    SR.push([`Generated: ${new Date().toLocaleDateString()}`]);
    SR.push([]);
    for(let i=0;i<maxR;i++){const Lr=L[i]||[];const Rr=R[i]||[];SR.push([Lr[0]||"", Lr[1]!==undefined?Lr[1]:"", "", ...Rr]);}
    const ws=XLSX.utils.aoa_to_sheet(SR);
    ws["!cols"]=[{wch:24},{wch:14},{wch:3},{wch:10},{wch:32},{wch:12},{wch:6}];
    XLSX.utils.book_append_sheet(wb, ws, shortMonth);
    XLSX.writeFile(wb, `${code} Comms - ${shortMonth}.xlsx`);
    return;
  }

  if(code==="DDJ"){
    const genEs=commEs.filter(e=>e.tier==="generated");
    const newEs=commEs.filter(e=>e.tier==="new");
    const inhEs=commEs.filter(e=>e.tier==="inherited"||(!["generated","new"].includes(e.tier)));
    const genTotal=genEs.reduce((s,e)=>s+e.subTotal,0);
    const newTotal=newEs.reduce((s,e)=>s+e.subTotal,0);
    const inhTotal=inhEs.reduce((s,e)=>s+e.subTotal,0);
    const genComm=genEs.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
    const newComm=newEs.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
    const inhComm=inhEs.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
    const newCount=positiveEs.filter(isNewAccount).length;
    const L=[];
    L.push([repName]);
    L.push(["Total Sales:", parseFloat(totalSales.toFixed(2))]);
    L.push(["# of Sales:", totalOrders]);
    L.push(["New Accts:", newCount]);
    L.push([]);
    if(newEs.length>0){
      L.push(["DDJ New:", parseFloat(newTotal.toFixed(2))]);
      L.push(["Commissionable:", parseFloat((newTotal*0.9).toFixed(2))]);
      L.push(["Comm @ 20%:", parseFloat(newComm.toFixed(2))]);
      L.push([]);
    }
    if(genEs.length>0){
      L.push(["DDJ Generated:", parseFloat(genTotal.toFixed(2))]);
      L.push(["Commissionable:", parseFloat((genTotal*0.9).toFixed(2))]);
      L.push(["Comm @ 20%:", parseFloat(genComm.toFixed(2))]);
      L.push([]);
    }
    if(inhEs.length>0){
      L.push(["DDJ Inherited:", parseFloat(inhTotal.toFixed(2))]);
      L.push(["Commissionable:", parseFloat((inhTotal*0.9).toFixed(2))]);
      L.push(["Comm @ 10%", parseFloat(inhComm.toFixed(2))]);
      L.push([]);
    }
    L.push(["Bonuses:", parseFloat(bonusTotal.toFixed(2))]);
    L.push(["Withholdings:", parseFloat(withholdTotal.toFixed(2))]);
    L.push([]);
    L.push(["Total Comp:", parseFloat(totalComp.toFixed(2))]);
    L.push([]);
    L.push(["Avg Sales:", parseFloat(avgSale.toFixed(2))]);
    withholdings.forEach(a=>{if(a.note)L.push([a.note]);});
    bonuses.forEach(a=>{if(a.note)L.push([a.note]);});
    const R=[];
    R.push(["Invoice","Customer","DDJ Generated","DDJ New","Inherited"]);
    commEs.forEach(e=>{
      const row=["#"+e.invoice, e.customer,"","",""];
      if(e.tier==="generated")row[2]=parseFloat(e.subTotal.toFixed(2));
      else if(e.tier==="new")row[3]=parseFloat(e.subTotal.toFixed(2));
      else row[4]=parseFloat(e.subTotal.toFixed(2));
      R.push(row);
    });
    if(nonCommEs.length>0){
      R.push([]);R.push(["— Non-Commissioned Items —"]);
      nonCommEs.forEach(e=>{R.push(["#"+e.invoice, e.customer, "", "", e.tier]);});
    }
    const maxR=Math.max(L.length, R.length+1);
    const SR=[];
    SR.push([`${repName} — ${month} Commission Statement`,"","","","","",""]);
    SR.push([`Generated: ${new Date().toLocaleDateString()}`]);
    SR.push([]);
    for(let i=0;i<maxR;i++){const Lr=L[i]||[];const Rr=R[i]||[];SR.push([Lr[0]||"", Lr[1]!==undefined?Lr[1]:"", "", ...Rr]);}
    const ws=XLSX.utils.aoa_to_sheet(SR);
    ws["!cols"]=[{wch:22},{wch:14},{wch:3},{wch:10},{wch:32},{wch:14},{wch:10},{wch:14}];
    XLSX.utils.book_append_sheet(wb, ws, shortMonth);
    XLSX.writeFile(wb, `${code} Comms - ${shortMonth}.xlsx`);
    return;
  }

  const isTJR=code==="TJR";
  const TIER_BUCKETS=[
    {key:"new",      label:isTJR?"New 10%":"New 20%",                  rate:getRate(code,"new"),      keys:["new"]},
    {key:"0-12",     label:isTJR?"0-12 10%":"0-12 20%",                rate:getRate(code,"0-12"),     keys:["0-12"]},
    {key:"1-2",      label:isTJR?"1-2 Years 7.5%":"1-2 Years 15%",     rate:getRate(code,"1-2"),      keys:["1-2"]},
    {key:"2-3",      label:isTJR?"2-3 Years / Key 5%":"2-3 Years 10%", rate:getRate(code,"2-3"),      keys:isTJR?["2-3","key"]:["2-3"]},
    {key:"3plus",    label:isTJR?"3+ 2%":"3+ / Top 10 10%",            rate:getRate(code,"3plus"),    keys:isTJR?["3plus"]:["3plus","key"]},
    {key:"inherited",label:"Inherited 10%",                             rate:getRate(code,"inherited"),keys:["inherited"]},
    {key:"generated",label:"Generated 20%",                             rate:getRate(code,"generated"),keys:["generated"]},
    {key:"zerocomm", label:"Zero Comm",                                 rate:0,                        keys:["zerocomm"]},
  ].filter(b=>commEs.some(e=>b.keys.includes(e.tier)));
  const newCountOuter=positiveEs.filter(e=>e.tier==="new").length;
  const L=[];
  L.push([repName]);
  L.push(["Total Sales:", parseFloat(totalSales.toFixed(2))]);
  L.push(["# of Sales:", totalOrders]);
  L.push(["New Accts:", newCountOuter]);
  L.push([]);
  TIER_BUCKETS.forEach(b=>{
    const tEs=commEs.filter(e=>b.keys.includes(e.tier));
    if(!tEs.length)return;
    const tTotal=tEs.reduce((s,e)=>s+e.subTotal,0);
    const tComm=tEs.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
    const tBase=tTotal*0.9;
    L.push([b.label+":", parseFloat(tTotal.toFixed(2))]);
    L.push(["Commissionable:", parseFloat(tBase.toFixed(2))]);
    L.push([`Comm @ ${(b.rate*100).toFixed(1).replace(/\.0$/,"")}%`, parseFloat(tComm.toFixed(2))]);
    L.push([]);
  });
  L.push(["Bonuses:", parseFloat(bonusTotal.toFixed(2))]);
  L.push(["Withholdings:", parseFloat(withholdTotal.toFixed(2))]);
  L.push([]);
  L.push(["Total Comp:", parseFloat(totalComp.toFixed(2))]);
  L.push([]);
  L.push(["Avg Sale:", parseFloat(avgSale.toFixed(2))]);
  withholdings.forEach(a=>{if(a.note)L.push([a.note]);});
  bonuses.forEach(a=>{if(a.note)L.push([a.note]);});
  salesReversals.forEach(a=>{if(a.note)L.push([a.note]);});
  const colHeaders=isTJR
    ?["Invoice","Customer","New 10%","0-12 10%","1-2 Years 7.5%","2-3 Years / Key 5%","3+ 2%","Zero Comm"]
    :["Invoice","Customer","New 20%","0-12 20%","1-2 Years 15%","2-3 Years 10%","3+ / Top 10 10%","Zero Comm"];
  const R=[];
  R.push(colHeaders);
  commEs.forEach(e=>{
    const row=["#"+e.invoice, e.customer,"","","","","",""];
    const colMap={new:2,"0-12":3,"1-2":4,"2-3":5,"3plus":6,key:6,generated:2,inherited:6,zerocomm:7};
    const col=colMap[e.tier];
    if(col!==undefined)row[col]=parseFloat(e.subTotal.toFixed(2));
    R.push(row);
  });
  if(nonCommEs.length>0){
    R.push([]);
    R.push(["— Non-Commissioned Items (no commission paid) —"]);
    R.push(["Invoice","Customer","Type","Note"]);
    nonCommEs.forEach(e=>{R.push(["#"+e.invoice, e.customer, e.tier==="loaner"?"Loaner":"No Comm", e.tier==="loaner"?"Loaner unit — excluded from sales":"Replacement/no-comm item"]);});
  }
  const maxR=Math.max(L.length, R.length+1);
  const SR=[];
  SR.push([`${repName} — ${month} Commission Statement`,"","","","","","","","","","",""]);
  SR.push([`Generated: ${new Date().toLocaleDateString()}`]);
  SR.push([]);
  for(let i=0;i<maxR;i++){const Lr=L[i]||[];const Rr=R[i]||[];SR.push([Lr[0]||"", Lr[1]!==undefined?Lr[1]:"", "", ...Rr]);}
  const ws=XLSX.utils.aoa_to_sheet(SR);
  ws["!cols"]=[{wch:22},{wch:14},{wch:3},{wch:10},{wch:32},{wch:12},{wch:12},{wch:14}];
  XLSX.utils.book_append_sheet(wb, ws, shortMonth);
  XLSX.writeFile(wb, `${code} Comms - ${shortMonth}.xlsx`);
}

function buildAllRepsExcel(entries,month,adjustments){
  const XLSX=window.XLSX; const wb=XLSX.utils.book_new();
  const me=entries.filter(e=>e.month===month&&e.actionType!=="duplicate"&&e.actionType!=="credit_memo");
  const tS=me.reduce((s,e)=>s+e.subTotal,0); const tC=me.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
  const paceRows=[[`${month} — Monthly Commission Summary`],[`Generated: ${new Date().toLocaleDateString()}`],[]];
  paceRows.push(["COMMISSION PAYOUTS"]); paceRows.push(["Rep","Name","Commission Due","Adjustments","Net Commission"]);
  let payoutTotal=0;
  Object.keys(REPS).forEach(code=>{
    const es=me.filter(e=>e.rep===code); if(!es.length)return;
    const com=es.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
    const adjs=(adjustments||[]).filter(a=>a.rep===code&&a.month===month);
    // Split reversals out and apply the formula via the shared resolver.
    // Other adjustments (bonus/withholding/etc.) keep folding in at raw $.
    // The "Adjustments" column in the summary keeps the raw $ total of
    // non-reversal adjustments so the column reads consistently with other
    // rep statements; reversal commission is rolled into Net via the
    // formula above.
    const revAdjs=adjs.filter(a=>a.type==="sales_reversal");
    const nonRevAdjs=adjs.filter(a=>a.type!=="sales_reversal");
    const {commTotal:reversalCommTotal}=aggregateReversals(revAdjs, es);
    const adjTotal=nonRevAdjs.reduce((s,a)=>s+parseFloat(a.amount),0);
    const net=com+adjTotal+reversalCommTotal;
    if(code==="CRC"){paceRows.push([code,REPS[code].name,"Non-commissioned","—","—"]);}
    else{paceRows.push([code,REPS[code].name,parseFloat(com.toFixed(2)),parseFloat(adjTotal.toFixed(2)),parseFloat(net.toFixed(2))]);payoutTotal+=net;}
  });
  paceRows.push(["","TOTAL COMMISSIONS DUE:","","",parseFloat(payoutTotal.toFixed(2))]);
  paceRows.push([]);paceRows.push(["SALES BREAKDOWN"]);
  paceRows.push(["Rep","Name","Total Sales","# Orders","New Accounts","Commission","Avg Sale"]);
  Object.keys(REPS).forEach(code=>{
    const es=me.filter(e=>e.rep===code); if(!es.length)return;
    const tot=es.reduce((s,e)=>s+e.subTotal,0); const com=es.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
    const paidOrders=es.filter(e=>e.amount>0); const avgSale=paidOrders.length>0?paidOrders.reduce((s,e)=>s+e.subTotal,0)/paidOrders.length:0;
    paceRows.push([code,REPS[code].name,parseFloat(tot.toFixed(2)),es.length,es.filter(isNewAccount).length,code==="CRC"?"—":parseFloat(com.toFixed(2)),parseFloat(avgSale.toFixed(2))]);
  });
  paceRows.push([]); paceRows.push(["","TOTAL",parseFloat(tS.toFixed(2)),me.length,"","",parseFloat(tC.toFixed(2))]);
  const ws=XLSX.utils.aoa_to_sheet(paceRows);
  ws["!cols"]=[{wch:6},{wch:14},{wch:14},{wch:10},{wch:14},{wch:14},{wch:12}];
  XLSX.utils.book_append_sheet(wb,ws,"Summary");
  XLSX.writeFile(wb,`Certags_${month.replace(" ","_")}_All_Reps_Summary.xlsx`);
}

// ── Refusal screen for an authenticated non-admin ──────────────────────────
// This app is Chris's and Tracy's. On 2026-09-01 a rep signed in and got the
// entire admin UI — every tab, every control, the confirmation banner, the
// Test mode toggle. RLS meant he saw only his own rows, but being handed the
// admin console at all is the wrong answer.
function NotAuthorized({email,onSignOut}){
  return(
    <div style={{minHeight:"100vh",background:"#f5f5f3",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"system-ui,sans-serif",padding:24}}>
      <div style={{maxWidth:460,background:"#fff",border:"1px solid #e0e0e0",borderRadius:12,padding:"28px 32px",textAlign:"center"}}>
        <div style={{fontSize:32,marginBottom:12}}>🔒</div>
        <h1 style={{fontSize:18,fontWeight:600,margin:"0 0 10px"}}>This tool is for administrators</h1>
        <p style={{fontSize:14,color:"#555",lineHeight:1.6,margin:"0 0 8px"}}>
          Your commissions live in the toolbox, and everything you need — your orders,
          your total, and confirming the month — is there.
        </p>
        <p style={{margin:"0 0 20px"}}>
          <a href="https://certags.tools" style={{color:"#185FA5",fontWeight:600,fontSize:14}}>
            Go to certags.tools →
          </a>
        </p>
        {email&&<div style={{fontSize:12,color:"#999",marginBottom:16}}>Signed in as {email}</div>}
        <button onClick={onSignOut}
          style={{background:"transparent",border:"1px solid #ccc",borderRadius:6,padding:"8px 18px",fontSize:13,cursor:"pointer",color:"#555"}}>
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function App(){
  const [authed,setAuthed]=useState(false);
  const [authChecking,setAuthChecking]=useState(true);
  // "checking" | "admin" | "denied" | "error"
  const [adminState,setAdminState]=useState("checking");
  const [sessionEmail,setSessionEmail]=useState("");

  useEffect(()=>{
    getSession().then(session=>{
      setAuthed(!!session);
      setAuthChecking(false);
    }).catch(()=>setAuthChecking(false));
  },[]);

  // ── The gate ─────────────────────────────────────────────────────────────
  // Asks the DATABASE, not ADMIN_EMAILS. That constant is a client-side Set:
  // it can hide a tab, it cannot refuse a session, and anyone can edit it in
  // devtools. current_is_admin() resolves is_admin from `employees` by the
  // JWT's email, inside a SECURITY DEFINER function with a pinned search_path
  // (080/083), so the answer comes from the same place every RLS policy in the
  // database gets it.
  //
  // ⚠ IT RETURNS NULL, NOT FALSE, when no employees row matches the caller —
  // documented in migration 080 and load-bearing here. `data === true` is
  // deliberate: a truthiness test would still refuse NULL, but `!== false`
  // would ADMIT it, and that is the shape of mistake this check exists to
  // prevent. Anything that is not exactly true is a refusal.
  //
  // FAILS CLOSED. A failed check refuses rather than admits — but it is
  // reported as its own state with a Retry, so a transient error is a nuisance
  // rather than a lockout needing a redeploy.
  const verifyAdmin=useCallback(async()=>{
    setAdminState("checking");
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session){setAdminState("denied");return;}
      setSessionEmail(session.user?.email||"");
      const {data,error}=await supabase.rpc("current_is_admin");
      if(error)throw error;
      setAdminState(data===true?"admin":"denied");
    }catch(err){
      console.error("admin check failed:",err);
      setAdminState("error");
    }
  },[]);

  useEffect(()=>{ if(authed)verifyAdmin(); },[authed,verifyAdmin]);

  const signOut=async()=>{
    try{ await logout(); }catch(err){ console.error("sign out failed:",err); }
    setAuthed(false);
    setAdminState("checking");
  };

  if(authChecking)return(
    <div style={{minHeight:"100vh",background:"#f5f5f3",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"system-ui,sans-serif"}}>
      <Spinner/>
    </div>
  );

  if(!authed)return<LoginScreen onAuth={()=>setAuthed(true)}/>;

  if(adminState==="checking")return(
    <div style={{minHeight:"100vh",background:"#f5f5f3",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"system-ui,sans-serif"}}>
      <Spinner/>
    </div>
  );

  if(adminState==="error")return(
    <div style={{minHeight:"100vh",background:"#f5f5f3",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"system-ui,sans-serif",padding:24}}>
      <div style={{maxWidth:460,background:"#fff",border:"1px solid #e0e0e0",borderRadius:12,padding:"28px 32px",textAlign:"center"}}>
        <h1 style={{fontSize:17,fontWeight:600,margin:"0 0 10px"}}>Could not verify your access</h1>
        <p style={{fontSize:14,color:"#555",lineHeight:1.6,margin:"0 0 20px"}}>
          The permission check did not complete, so the tracker stayed closed. This is
          usually a connection problem rather than an access one.
        </p>
        <button onClick={verifyAdmin}
          style={{background:"#111",color:"#fff",border:"none",borderRadius:6,padding:"9px 20px",fontSize:14,cursor:"pointer",marginRight:8}}>
          Try again
        </button>
        <button onClick={signOut}
          style={{background:"transparent",border:"1px solid #ccc",borderRadius:6,padding:"9px 18px",fontSize:13,cursor:"pointer",color:"#555"}}>
          Sign out
        </button>
      </div>
    </div>
  );

  if(adminState==="denied")return<NotAuthorized email={sessionEmail} onSignOut={signOut}/>;

  return<Tracker onLogout={()=>{setAuthed(false);setAdminState("checking");}}/>;
}

function Tracker({onLogout}){
  const [tab,setTab]              = useState("leaderboard");
  const [month,setMonth]          = useState(()=>{const d=new Date();const months=["January","February","March","April","May","June","July","August","September","October","November","December"];return months[d.getMonth()]+" "+d.getFullYear();});
  const [entries,setEntries]      = useState([]);
  const [adjustments,setAdjs]     = useState([]);
  const [loading,setLoading]      = useState(true);
  const [saving,setSaving]        = useState(false);
  const [form,setForm]            = useState(BLANK);
  const [parsing,setParsing]      = useState(false);
  const [emailText,setEmailText]  = useState("");
  const [parseMsg,setParseMsg]    = useState("");
  const [openReps,setOpenReps]    = useState({});
  const [prevYear,setPrevYear]    = useState(null);
  const [showPrompt,setShowPrompt]= useState(false);
  const [prevInput,setPrevInput]  = useState("");
  const [xlsxReady,setXlsxReady] = useState(false);
  const [showExport,setShowExport]= useState(false);
  const [batching,setBatching]    = useState(false);
  const [exportReviewRep,setExportReviewRep] = useState(null);
  const [exportAdjOverrides,setExportAdjOverrides] = useState([]);
  const [showAdjPanel,setShowAdjPanel] = useState(null);
  const [adjForm,setAdjForm]      = useState({type:"withholding",amount:"",note:"",invoiceRef:""});
  const [testMode,setTestMode]    = useState(false);
  // Live clock (2026-10-30). todayStr used to be a module constant and the
  // business-day fraction read the clock only on render, so a tab left open
  // showed the pace from whenever it was last redrawn — at 2 PM on the last
  // day of October the email carried the 11 AM projection ($724,580 "on
  // pace" with 13 minutes left). One tick a minute keeps elapsed days live.
  const [now,setNow] = useState(()=>new Date());
  useEffect(()=>{const id=setInterval(()=>setNow(new Date()),60000);return()=>clearInterval(id);},[]);
  const todayStr = pacificTodayStr(now);
  const [testDateInput,setTestDateInput] = useState(todayStr);
  const [testDate,setTestDate]    = useState(null);
  const [editingEntry,setEditingEntry] = useState(null);
  const [unhandledActions,setUnhandledActions] = useState([]);
  const [handledActions,setHandledActions]     = useState([]);
  const [showHandled,setShowHandled]           = useState(false);
  const [duplicateWarning,setDuplicateWarning] = useState(null);
  const [parseWarnings,setParseWarnings] = useState([]);
  const [showComm,setShowComm]   = useState(true);
  const [showSales,setShowSales] = useState(true);
  const [sortBy,setSortBy]       = useState("total");
  const [emailCopied,setEmailCopied] = useState(false);
  const [sentEntryIds,setSentEntryIds]   = useState(new Set());
  const [lastSentTime,setLastSentTime]   = useState(null);
  const [lastSentCount,setLastSentCount] = useState(0);
  const [sendCycleLoading,setSendCycleLoading] = useState(false);
  const [lastSubjectLine,setLastSubjectLine]   = useState("");
  const [showNewAccountsModal,setShowNewAccountsModal] = useState(false);
  const [newAccountsCopied,setNewAccountsCopied] = useState(false);
  // ── Action notes ────────────────────────────────────────────────────────
  // Local-only, and correctly so. This is NOT the statusDrafts pattern below
  // with a bug in it, and it is not waiting to be "fixed" by seeding it from
  // entries.action_note — doing that would be a change in meaning, not a
  // repair.
  //
  // action_note is a COMPLETION RECORD. "CRC ordered 8.15" is written at the
  // instant the item is handled, copied into the handled_actions audit log,
  // and read back in the Completed / handled drawer. It was never meant to
  // exist while the item is pending, so there is nothing for a pending row to
  // load: the value has no stored counterpart until the moment it is handled,
  // which is also the moment the row leaves this list. A map that holds it
  // until the button is pressed is exactly the right shape for that.
  //
  // The thing this genuinely could not do — say something about an item that
  // is still open — is status_comment, below. That case is covered; leave
  // this one alone.
  const [actionNotes,setActionNotes] = useState({});
  // ── Status comments ─────────────────────────────────────────────────────
  // The opposite shape from actionNotes above, because the two fields have
  // opposite lifetimes. actionNotes IS the value and holds it only until the
  // item is handled, which is all a completion record needs. A status comment
  // has to be live and shared for as long as the item is OPEN, so the row is
  // the source of truth and statusDrafts holds only in-flight edits; the
  // displayed value falls back to e.statusComment on the row. That
  // fallback is what lets refetch-on-focus work: refetching swaps every row
  // for the server's, so rows nobody is editing pick up the other operator's
  // comment immediately, while a row mid-edit keeps its draft. A key is
  // DELETED when a save settles, never set to "" — "" is a real edit
  // (the operator clearing the field) and undefined is the only "no draft".
  const [statusDrafts,setStatusDrafts]   = useState({});   // id -> in-flight text
  const [statusSaving,setStatusSaving]   = useState({});   // id -> "saving" | "saved" | "error"
  const [openComments,setOpenComments]   = useState({});   // id -> editor revealed on an uncommented row
  // Per-row debounce timers. A ref, not state — retiming one must not re-render
  // the list under the operator mid-keystroke.
  const statusTimers = useRef({});
  // Promote dialog for a captured duplicate. null when closed. See
  // openPromoteDialog for why promoting is a decision and not a button.
  const [promoteModal,setPromoteModal] = useState(null);
  // Live (non-superseded) commission_confirmations for the selected month, for
  // every rep. One fetch serves both the rep badge and the admin grid.
  const [commConfirms,setCommConfirms] = useState([]);
  const [diffModal,setDiffModal] = useState(null);
  const [actionFilter,setActionFilter] = useState("all");
  const [commFlags,setCommFlags] = useState([]);
  const [commFlagsLoading,setCommFlagsLoading] = useState(false);
  // What the LEDGER says, next to what the rep claims. See
  // fetchFlagLedgerContext — the flag row alone has never been checkable.
  const [flagContext,setFlagContext] = useState({byEntryId:{},byInvoice:{}});
  const [reviewNotes,setReviewNotes] = useState({});
  const [showReviewedFlags,setShowReviewedFlags] = useState(false);
  const [currentUserEmail,setCurrentUserEmail] = useState("");

  // Monthly commission confirmation banner state. The banner appears for
  // commissioned reps starting on the 1st of the following month and persists
  // (across all tabs) until they click confirm. Admins / non-rep emails are
  // exempted entirely.
  const [monthConfirmations,setMonthConfirmations] = useState([]);
  // Current rep's confirmation row for the currently-selected month — drives
  // the Dashboard MyCommissions card. Shares its source (month_confirmations)
  // with the admin Confirmation Status grid above.

  const [showPayroll,setShowPayroll]     = useState(false);
  const [showReversalPanel,setShowReversalPanel] = useState(false);
  const [salesLogSearch,setSalesLogSearch] = useState("");
  const [searchAllResults,setSearchAllResults] = useState([]);
  const [searchingAllMonths,setSearchingAllMonths] = useState(false);
  const [reversalForm,setReversalForm] = useState({
    rep:"",amount:"",tier:"0-12",commMode:"auto",manualComm:"",note:"",invoiceRef:""
  });
  const [reversalSaving,setReversalSaving] = useState(false);

  const effectiveDateStr = testDate || todayStr;

  useEffect(()=>{
    supabase.auth.getUser().then(({data})=>{
      setCurrentUserEmail(data?.user?.email||"");
    });
  },[]);



  // Admin-only — load all confirmations for the currently-selected month so
  // the dashboard grid can show who has / hasn't confirmed.
  useEffect(()=>{
    // (legacy month_confirmations grid — still read, no longer written)
    let cancelled=false;
    fetchAllMonthConfirmations(month)
      .then(rows=>{if(!cancelled)setMonthConfirmations(rows);})
      .catch(console.error);
    return()=>{cancelled=true;};
  },[month]);

  // Live commission_confirmations for the month — EVERY rep, loaded for admin
  // and rep alike. Not admin-gated: a rep needs their own snapshot to know
  // whether their confirmation still holds, and the table carries no data a rep
  // cannot already see on their own page.
  useEffect(()=>{
    let cancelled=false;
    fetchCommissionConfirmations(month)
      .then(rows=>{if(!cancelled)setCommConfirms(rows);})
      .catch(err=>{console.error("Error fetching commission confirmations:",err);if(!cancelled)setCommConfirms([]);});
    return()=>{cancelled=true;};
  },[month]);


  useEffect(()=>{
    if(window.XLSX){setXlsxReady(true);return;}
    const s=document.createElement("script");
    s.src="https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js";
    s.onload=()=>setXlsxReady(true);
    document.head.appendChild(s);
  },[]);

  useEffect(()=>{
    fetchUnhandledActions().then(setUnhandledActions).catch(console.error);
    fetchHandledActions().then(setHandledActions).catch(console.error);
  },[]);

  // Needs Action is worked by two people and has no realtime channel — the
  // fetch above runs once, on mount. That was survivable while every field in
  // the tab was write-once-then-the-item-leaves, and is not survivable for a
  // status comment: the whole point of "CRC has eyes on but waiting for AGL
  // feedback" is that the OTHER operator reads it, and until this effect they
  // could not until they happened to reload the page.
  //
  // Two triggers, because either alone leaves a real hole. Opening the tab
  // catches the app having sat on Dashboard for an hour. Window focus catches
  // the more common case — the tab is already open in a background window
  // while the work that produced the comment happens somewhere else.
  //
  // Safe to fire mid-edit: this replaces the row objects, and the textarea
  // reads statusDrafts first (resolveCommentValue), so an in-progress comment
  // is never clobbered by the arriving one.
  useEffect(()=>{
    if(tab!=="action") return;
    const refresh=()=>{
      fetchUnhandledActions().then(setUnhandledActions).catch(console.error);
      fetchHandledActions().then(setHandledActions).catch(console.error);
    };
    refresh();
    window.addEventListener("focus",refresh);
    return()=>window.removeEventListener("focus",refresh);
  },[tab]);

  // Nothing should fire a save into a component that is gone.
  useEffect(()=>{
    const timers=statusTimers.current;
    return()=>{Object.values(timers).forEach(clearTimeout);};
  },[]);
  useEffect(()=>{
  supabase.from('comm_flags').select('*').order('created_at',{ascending:false})
    .then(({data})=>setCommFlags(data||[]))
    .catch(console.error);
  },[]);

  // Resolve every flag against the ledger. Runs off commFlags rather than on
  // mount, so it re-resolves after a flag is filed or resolved without needing
  // its own trigger. A failure here degrades to "could not check" in the UI
  // rather than blocking the tab — the flags themselves are still actionable.
  useEffect(()=>{
    if(!commFlags.length){setFlagContext({byEntryId:{},byInvoice:{}});return;}
    let cancelled=false;
    fetchFlagLedgerContext(commFlags)
      .then(ctx=>{if(!cancelled)setFlagContext(ctx);})
      .catch(err=>{console.error("Flag ledger lookup failed:",err);});
    return()=>{cancelled=true;};
  },[commFlags]);

  // Cross-month Sales Log search. Empty query clears results; a non-empty
  // query debounces 250 ms, then queries entries across all months. Spinner
  // state is held until the latest query resolves.
  useEffect(()=>{
    const q=salesLogSearch.trim();
    if(!q){
      setSearchAllResults([]);
      setSearchingAllMonths(false);
      return;
    }
    setSearchingAllMonths(true);
    let cancelled=false;
    const timer=setTimeout(async()=>{
      try{
        const results=await searchEntriesGlobal(q);
        if(!cancelled)setSearchAllResults(results);
      }catch(err){
        console.error("Cross-month search failed:",err);
        if(!cancelled)setSearchAllResults([]);
      }
      if(!cancelled)setSearchingAllMonths(false);
    },250);
    return()=>{cancelled=true;clearTimeout(timer);};
  },[salesLogSearch]);

  useEffect(()=>{
    let cancelled=false;
    async function load(){
      setLoading(true);setSentEntryIds(new Set());setLastSentTime(null);setLastSentCount(0);setDuplicateWarning(null);
      try{
        const [ents,settings,adjs,sendCycle]=await Promise.all([
          fetchEntries(month),fetchMonthSettings(month),fetchAdjustments(month),fetchSendCycle(month),
        ]);
        if(!cancelled){
          setEntries(ents);setAdjs(adjs);
          setPrevYear(settings?.prior_year_total||null);setShowPrompt(!settings?.prior_year_total);
          if(sendCycle){
            setSentEntryIds(new Set((sendCycle.entry_ids||[]).map(Number)));
            setLastSentTime(new Date(sendCycle.sent_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}));
            setLastSentCount(sendCycle.sent_count||0);
            setLastSubjectLine(sendCycle.subject_line||"");
          }
        }
      }catch(e){console.error("Load error:",e);}
      finally{if(!cancelled)setLoading(false);}
    }
    load(); return()=>{cancelled=true;};
  },[month]);

  const {month:mIdx,year}=parseMonthYear(month);
  const totalBiz  =bizDaysInMonth(year,mIdx);
  const elapsedBiz=bizDaysElapsedTo(year,mIdx,effectiveDateStr,now);
  const allMonthEntries=entries.filter(e=>e.month===month);
  const monthEntries=allMonthEntries.filter(e=>e.actionType!=="duplicate"&&e.actionType!=="credit_memo");

  const salesReversals=adjustments.filter(a=>a.rep&&a.month===month&&a.type==="sales_reversal");
  const salesReversalTotal=salesReversals.reduce((s,a)=>s+parseFloat(a.amount),0);

  const salesEntries=allMonthEntries.filter(e=>
    e.tier!=="loaner"&&
    e.tier!=="nocomm"&&
    e.amount!==0&&
    e.actionType!=="duplicate"&&
    e.actionType!=="credit_memo"
  );
  // ── The confirmable state for one rep, rebuilt from current data ──────────
  // ONE definition, used by the rep's own badge and by the admin grid. If these
  // were computed separately a rep could see "confirmed" while admin saw
  // "changed", and there would be no way to tell which was right.
  //
  // It deliberately reuses `salesEntries` — the same rows the page displays —
  // rather than re-deriving a filter. A snapshot built off a different rule
  // would make a rep stale for something they were never shown, and duplicates
  // and credit memos would leak into a confirmation the page never rendered.
  //
  // Adjustments and resolved reversals fold into total_commission the same way
  // the rep view totals them, so an adjustment-only correction still stales the
  // confirmation. That is the second of the two correction types this exists
  // for; the first is reattribution, which shows up as an added/removed entry.
  // The selected month, from state. buildRepSnapshot re-derives salesEntries
  // and monthEntries from `entries` by the same filters used above, so this is
  // the identical computation it always was — just expressed once, where the
  // banner can reach it for a different month.
  const repCurrentState=useCallback((repCode)=>buildRepSnapshot({
    rep:repCode,month,entries,adjustments,
  }),[entries,adjustments,month]);

  // Status for a rep, given the live confirmations already loaded. Falls back to
  // the pre-snapshot month_confirmations row so an older confirmation reads as
  // confirmed-but-undiffable rather than as never confirmed.
  const repConfirmStatus=useCallback((repCode,legacyAt=null)=>confirmationStatus({
    snapshot:latestConfirmation(commConfirms,repCode,month),
    current:repCurrentState(repCode),
    legacyConfirmedAt:legacyAt,
  }),[commConfirms,month,repCurrentState]);

  const positiveEntries=salesEntries.filter(e=>e.amount>0);
  // Daily window, not a single day: on Monday this reaches back to Saturday and
  // on Sunday to Saturday, because no daily email goes out at the weekend and
  // those entries otherwise appeared in no daily total at all. Ruled
  // 2026-10-12; see ./dailyWindow.js. Drives the "Daily Total:" line in the
  // copied email body and the "Today's total" card.
  const todayEntries=positiveEntries.filter(e=>inDailyWindow(e.dateStr,effectiveDateStr));

  const monthTotal =salesEntries.reduce((s,e)=>s+e.subTotal,0)+salesReversalTotal;
  const todayTotal =todayEntries.reduce((s,e)=>s+e.subTotal,0);
  const newCount   =positiveEntries.filter(isNewAccount).length;
  const dailyAvg   =elapsedBiz>0?monthTotal/elapsedBiz:0;
  // Elapsed days accrue continuously through the 6:30–15:30 PT window; show one decimal.
  const fmtBiz=(d)=>(Math.round(d*10)/10).toFixed(1).replace(/\.0$/,"");
  const pace       =dailyAvg*totalBiz;
  const goalTotal  =prevYear?prevYear*1.25:null;
  const gapToGoal  =goalTotal?goalTotal-monthTotal:null;
  const paceVsGoal =goalTotal?(pace/goalTotal)*100:null;
  const monthVsGoal=goalTotal?(monthTotal/goalTotal)*100:null;

  const savePrev=async()=>{
    const v=parseFloat(prevInput.replace(/[$,]/g,""));
    if(isNaN(v)||v<=0){alert("Enter a dollar amount greater than zero.");return;}
    try{await upsertMonthSettings(month,v);setPrevYear(v);setShowPrompt(false);setPrevInput("");}
    catch(err){alert("Couldn't save prior year: "+(err.message||err));}
  };

  const parseEmail=useCallback(async()=>{
    if(!emailText.trim())return;
    setParsing(true);setParseMsg("");setDuplicateWarning(null);
    try{

      // The prompt no longer travels with the request — it lives in
      // netlify/lib/invoicePrompt.js. The function ignores anything the client
      // sends in its place, so this body carries only the email text.
      //
      // The bearer token is what the function gates on. Without it the endpoint
      // was an open, billable relay to our Anthropic key; getSession() here is
      // the same session the whole app already runs under, so this cannot fail
      // for a user who is looking at this screen.
      const { data:{ session } } = await supabase.auth.getSession();
      const res=await fetch("/.netlify/functions/parse-email",{
        method:"POST",
        headers:{
          "Content-Type":"application/json",
          ...(session?.access_token?{Authorization:`Bearer ${session.access_token}`}:{}),
        },
        body:JSON.stringify({emailText}),
      });
      if(res.status===401||res.status===403){
        throw new Error("Not authorised to run the parser — try signing out and back in.");
      }
      const data=await res.json();
      if(!data.content||!Array.isArray(data.content)){throw new Error("Unexpected API response: "+(data.error?.message||JSON.stringify(data).slice(0,100)));}
      const raw=data.content.filter(c=>c.type==="text").map(c=>c.text).join("");
      // Robust JSON extraction: tolerate prose / markdown fences around the {...} block.
      const jsonMatch=raw.match(/\{[\s\S]*\}/);
      if(!jsonMatch)throw new Error("No JSON object in Claude response");
      const p=JSON.parse(jsonMatch[0]);

      // Backward compat: if Claude returns the old shape with `amount` instead of `subTotal`,
      // fall back to it. New shape has subTotal + feeTotal.
      const subTotalRaw=(p.subTotal!=null)?p.subTotal:p.amount;
      const subTotal=parseFloat(subTotalRaw)||0;
      const feeTotal=parseFloat(p.feeTotal!=null?p.feeTotal:0)||0;
      const commissionableAmount=Number((subTotal-feeTotal).toFixed(2));

      const repCode=String(p.rep||"").toUpperCase().slice(0,3);
      const cpDetect=detectComplyPro(emailText,repCode||"RTR");
      const tpDetect=detect3rdParty(emailText);

      // Use Claude's flags when present (new prompt), otherwise fall back to JS detection.
      const isLoaner=(typeof p.isLoaner==="boolean")?p.isLoaner:detectLoaner(emailText);
      const isNoComm=!isLoaner&&((typeof p.isNoComm==="boolean")?p.isNoComm:detectNoComm(emailText,repCode||"RTR"));
      // zerocomm: when both totals returned, derive from them; else fall back to keyword scan.
      const isZeroComm=!isLoaner&&!isNoComm&&(
        (p.subTotal!=null&&p.feeTotal!=null)
          ?(subTotal!==0&&Math.abs(commissionableAmount)<0.005)
          :detectZeroComm(emailText,repCode||"RTR")
      );

      const parsed={
        rep:repCode||"RTR",
        invoice:p.invoice||"",
        customer:p.customer||"",
        amount:subTotal||"",
        feeAmount:feeTotal>0?feeTotal:"",
        tier:isLoaner?"loaner":isNoComm?"nocomm":isZeroComm?"zerocomm":(p.tier||"new"),
        isComplyPro:isLoaner?false:cpDetect.isComplyPro,
        isPrinter:isLoaner?false:cpDetect.isPrinter,
        _cpUncertain:!cpDetect.confident&&repCode!=="DDJ",
        needs3rdParty:tpDetect.needs3rdParty,
        flaggedItems:tpDetect.flaggedItems,
        isLoaner,
        rationale:p.rationale||null,
      };

      const warnings=[];
      if(!parsed.rep||!Object.keys(REPS).includes(parsed.rep))warnings.push("Rep could not be identified — please select manually");
      if(!parsed.invoice)warnings.push("Invoice # not found");
      if(!parsed.customer||parsed.customer.includes("@"))warnings.push("Customer name looks like an email — please correct");
      if(parsed.amount===0||parsed.amount==="")warnings.push("Amount is $0 — confirm this is correct");
      if(feeTotal<0)warnings.push("Fees came back negative — review before saving");
      if(feeTotal>0&&Math.abs(feeTotal)>Math.abs(subTotal)+0.01)warnings.push(`Fees ($${feeTotal.toFixed(2)}) exceed subtotal ($${subTotal.toFixed(2)}) — review before saving`);
      if(parsed.invoice){const dup=await checkDuplicateInvoice(parsed.invoice);if(dup)setDuplicateWarning(dup);}
      setForm(parsed);setParseWarnings(warnings);
      setParseMsg(warnings.length>0?"Parsed with warnings — review below before adding":"Parsed — review and confirm below.");
    }catch(e){setParseMsg("Parse error: "+e.message);setParseWarnings(["Parse failed — check the email format and try again. Error: "+e.message]);}
    setParsing(false);
  },[emailText]);

  const addEntry=async(forceAdd=false)=>{
    const subTotal=parseFloat(form.amount)||0;
    const feeTotal=parseFloat(form.feeAmount)||0;
    const commissionableAmount=Number((subTotal-feeTotal).toFixed(2));
    if(!form.invoice||!form.customer){alert("Fill in Invoice # and Customer.");return;}
    if(form.amount===""||form.amount===undefined){alert("Fill in Subtotal (use 0 for $0 orders).");return;}
    if(feeTotal<0){alert("Fees cannot be negative.");return;}
    if(Math.abs(feeTotal)>Math.abs(subTotal)+0.01){alert("Fees cannot exceed the subtotal.");return;}
    if(!forceAdd){const dup=await checkDuplicateInvoice(form.invoice);if(dup){setDuplicateWarning(dup);return;}}
    setSaving(true);
    try{
      const entry={...form,
        amount:commissionableAmount,
        subTotal,feeTotal,commissionableAmount,
        isCredit:subTotal<0,
        month,dateStr:effectiveDateStr,
        isZero:subTotal===0,
        isComplyPro:form.isLoaner?false:(form.rep==="DDJ"?true:(form.isComplyPro||false)),
        isPrinter:form.isLoaner?false:(form.isPrinter||false),
        needs3rdParty:form.needs3rdParty||false,flaggedItems:form.flaggedItems||[],isLoaner:form.isLoaner||false,
        actionType:form.needs3rdParty?"3rd_party":null,
        actionNote:null,
      };
      const saved=await insertEntry(entry);
      setEntries(prev=>[...prev,saved]);
      if(isPendingAction(saved))setUnhandledActions(prev=>[saved,...prev]);
      setForm(BLANK);setEmailText("");setParseMsg("");setDuplicateWarning(null);setParseWarnings([]);
      setTab("leaderboard");
    }catch(e){alert("Save error: "+e.message);}
    setSaving(false);
  };

  const deleteEntry=async(id)=>{
    if(!window.confirm("Remove this entry?"))return;
    await deleteEntryById(id);
    setEntries(prev=>prev.filter(e=>e.id!==id));
    setUnhandledActions(prev=>prev.filter(e=>e.id!==id));
  };

  const startEdit=(entry)=>{
    setEditingEntry(entry);
    // Pre-populate form with subTotal in the "Subtotal" field and fees in the new field
    // (falling back to amount for old/manual rows where subTotal == amount).
    setForm({rep:entry.rep,invoice:entry.invoice,customer:entry.customer,
      amount:entry.subTotal!=null?entry.subTotal:entry.amount,
      feeAmount:entry.feeTotal||"",
      tier:entry.tier,isComplyPro:entry.isComplyPro,isPrinter:entry.isPrinter,needs3rdParty:entry.needs3rdParty||false,flaggedItems:entry.flaggedItems||[]});
    setParseWarnings([]);setParseMsg("");setDuplicateWarning(null);setTab("parser");window.scrollTo(0,0);
  };

  const saveEdit=async()=>{
    const subTotal=parseFloat(form.amount)||0;
    const feeTotal=parseFloat(form.feeAmount)||0;
    const commissionableAmount=Number((subTotal-feeTotal).toFixed(2));
    if(!form.invoice||!form.customer){alert("Fill in Invoice # and Customer.");return;}
    if(feeTotal<0){alert("Fees cannot be negative.");return;}
    if(Math.abs(feeTotal)>Math.abs(subTotal)+0.01){alert("Fees cannot exceed the subtotal.");return;}
    setSaving(true);
    try{
      const updated=await updateEntry(editingEntry.id,{
        ...form,
        amount:commissionableAmount,
        subTotal,feeTotal,commissionableAmount,
        isCredit:subTotal<0,
        isZero:subTotal===0,
        isComplyPro:form.rep==="DDJ"?true:(form.isComplyPro||false),
        isPrinter:form.isPrinter||false,
        needs3rdParty:form.needs3rdParty||false,
        flaggedItems:form.flaggedItems||[],
        actionType:form.needs3rdParty?"3rd_party":null,
        actionNote:null,
      });
      setEntries(prev=>prev.map(e=>e.id===editingEntry.id?{...e,...updated}:e));
      if(isPendingAction(updated)){setUnhandledActions(prev=>{const exists=prev.find(e=>e.id===editingEntry.id);if(exists)return prev.map(e=>e.id===editingEntry.id?{...e,...updated}:e);return[updated,...prev];});}
      else{setUnhandledActions(prev=>prev.filter(e=>e.id!==editingEntry.id));}
      setEditingEntry(null);setForm(BLANK);setParseWarnings([]);setDuplicateWarning(null);setTab("dashboard");
    }catch(e){alert("Save error: "+e.message);}
    setSaving(false);
  };

  const cancelEdit=()=>{setEditingEntry(null);setForm(BLANK);setParseWarnings([]);setParseMsg("");setDuplicateWarning(null);};

  const addAdjustment=async(rep)=>{
    const amt=parseFloat(adjForm.amount);if(!amt){alert("Enter an amount.");return;}
    const adj={rep,month,type:adjForm.type,amount:adjForm.type==="withholding"?-Math.abs(amt):Math.abs(amt),note:adjForm.note,invoice_ref:adjForm.invoiceRef};
    const saved=await insertAdjustment(adj);
    setAdjs(prev=>[...prev,saved]);
    setAdjForm({type:"withholding",amount:"",note:"",invoiceRef:""});setShowAdjPanel(null);
  };

  // Inline tier-set for legacy sales_reversal rows that pre-date the tier
  // column. One-click writes adjustments.tier so the commission resolver
  // stops surfacing the row as unresolved.
  const updateReversalTier=async(adjId, tier)=>{
    try{
      const updated=await updateAdjustment(adjId, {tier});
      setAdjs(prev=>prev.map(a=>a.id===adjId?{...a, tier: updated.tier}:a));
    }catch(e){alert("Couldn't set tier: "+e.message);}
  };

  const removeAdjustment=async(id)=>{
    if(!window.confirm("Remove this adjustment?"))return;
    await deleteAdjustment(id);setAdjs(prev=>prev.filter(a=>a.id!==id));
  };

  const saveReversal=async()=>{
    const salesAmt=parseFloat(reversalForm.amount);
    if(!salesAmt||!reversalForm.rep){alert("Select a rep and enter an amount.");return;}
    if(!reversalForm.note.trim()){alert("Please add a note describing this reversal.");return;}
    setReversalSaving(true);
    try{
      const negSales=-Math.abs(salesAmt);
      const salesAdj={
        rep:reversalForm.rep,month,type:"sales_reversal",
        amount:negSales,
        // tier drives the reversal's commission impact via amount × 0.9 × rate.
        // Required at save so Dashboard/Payroll can compute without a fallback
        // invoice lookup. Pre-migration the form's tier was silently dropped —
        // the migration + this write are what fix that.
        tier:reversalForm.tier||null,
        note:reversalForm.note+(reversalForm.invoiceRef?` (ref #${reversalForm.invoiceRef})`:""),
        invoice_ref:reversalForm.invoiceRef||null,
      };
      const savedSales=await insertAdjustment(salesAdj);
      setAdjs(prev=>[...prev,savedSales]);
      setReversalForm({rep:"",amount:"",tier:"0-12",commMode:"auto",manualComm:"",note:"",invoiceRef:""});
      setShowReversalPanel(false);
      alert(`Reversal saved. Sales reduced by ${fmt(Math.abs(salesAmt))}.`);
    }catch(e){alert("Save error: "+e.message);}
    setReversalSaving(false);
  };

  // ── Duplicate resolution (parser v51) ────────────────────────────────────
  // A duplicate row is a record that an invoice arrived twice. Until v51 the
  // parser discarded the second email silently; it now stores it for review.
  // Two outcomes, and markActioned does NOT cover them: it clears actionType,
  // which for a duplicate IS the promote path, so using it to "mark handled"
  // would quietly turn a duplicate into a counted sale.
  //
  // The stored invoice carries a -DUP / -DUP2 suffix because entries has
  // UNIQUE (invoice_normalized, rep) and invoice_normalized is generated from
  // invoice. Stripping it is what makes the row a real entry again — and what
  // makes it collide with the original, which is the case that must be shown
  // rather than swallowed.
  // realInvoiceOf now lives in ./duplicateResolution — it also has to strip
  // -ADD, so an adjustment row displays under its base invoice too.

  // Dismiss: the second email was genuinely redundant. Stamp dismissed_at, log
  // it as handled, and LEAVE actionType='duplicate' so every total keeps
  // excluding it.
  //
  // dismissed_at is the whole fix. Before it, the only durable writes were the
  // handled_actions row and action_note — neither of which the Needs Action
  // query reads — so a dismissal survived exactly as long as the React state
  // did. Entry 1620 was dismissed three times and came back every reload.
  const dismissDuplicate=async(id)=>{
    try{
      const entry=unhandledActions.find(e=>e.id===id);
      if(!entry) return;
      const patch=dismissPatch(actionNotes[id],new Date().toISOString());

      // Log once per entry. Re-dismissing is only reachable when a dismissal
      // half-landed or another tab got there first, and neither deserves a
      // second row in the audit log — 1620 accumulated three.
      let logged=findDismissalLog(handledActions,id);
      if(!logged){
        try{
          logged=await insertHandledAction(entry,entry.flaggedItems||[],currentUserEmail,patch.actionNote);
        }catch(err){
          // The partial unique index fired: a concurrent dismissal won. Adopt
          // its row — the work happened, it just wasn't this tab that did it.
          if(!isUniqueViolation(err)) throw err;
          logged=await fetchDismissalLog(id);
        }
        if(logged)setHandledActions(prev=>prev.some(h=>h.id===logged.id)?prev:[logged,...prev]);
      }

      // actionType deliberately untouched — clearing it is the promote path.
      await updateEntry(id,patch);
      setEntries(prev=>prev.map(e=>e.id===id?{...e,...patch}:e));
      setUnhandledActions(prev=>prev.filter(e=>e.id!==id));
      setActionNotes(prev=>{const n={...prev};delete n[id];return n;});
    }catch(err){alert("Error dismissing duplicate: "+err.message);}
  };

  // Promote opens a dialog rather than acting immediately. A duplicate has two
  // genuinely different meanings and the amount decides which:
  //
  //   FULL       the original was not a real sale (the $0 demo case). The whole
  //              captured amount is the sale. Restore the invoice number.
  //   ADJUSTMENT the original WAS a real sale and has already been commissioned.
  //              The invoice was then increased and QB re-sent the whole thing.
  //              Promoting the full amount double-pays the rep on the part they
  //              were already paid for — invoice 25012 on 2026-08-03: original
  //              $4,530 commissioned in July, re-sent at $6,030. Only the
  //              $1,500 delta is new revenue, and it belongs to the CURRENT
  //              month, not July's.
  //
  // The difference is pre-computed and offered as the default because that is
  // the case that silently overpays, and a dialog that defaults to the wrong
  // number is worse than no dialog.
  const openPromoteDialog=async(id)=>{
    const entry=unhandledActions.find(e=>e.id===id);
    if(!entry) return;
    const base=realInvoiceOf(entry.invoice);
    const captured=Number(entry.subTotal||entry.amount||0);
    setPromoteModal({entry,base,captured,existing:null,loading:true,mode:null,amount:"",busy:false});
    let existing=null;
    try{
      // The row this was flagged against. checkDuplicateInvoice already skips
      // duplicate / credit_memo rows and returns the REAL entry, which is
      // exactly what a full promote would collide with on
      // (invoice_normalized, rep). Snake_case — it does not go through dbToEntry.
      const found=await checkDuplicateInvoice(base);
      if(found&&found.rep===entry.rep) existing=found;
    }catch(err){ /* fall through — the dialog still works without the comparison */ }
    const existingAmt=existing?Number(existing.sub_total??existing.amount??0):null;
    const diff=computeDifference(captured,existingAmt);
    setPromoteModal(prev=>prev&&prev.entry.id===id
      ?{...prev,existing,existingAmt,diff,loading:false,
        // Default to the delta when there is one and it is positive; that is the
        // overpay case. Otherwise the captured amount.
        amount:String(defaultPromoteAmount(captured,diff))}
      :prev);
  };

  const closePromoteDialog=()=>setPromoteModal(null);

  // Shared by both modes. `fields` decides invoice + amounts; everything else —
  // clearing actionType, moving the row out of Needs Action — is identical.
  const applyPromote=async(fields,noteText)=>{
    const m=promoteModal; if(!m) return;
    const id=m.entry.id;
    setPromoteModal(prev=>prev?{...prev,busy:true}:prev);
    try{
      const updated=await updateEntry(id,{...fields,actionType:null,actionNote:noteText||null});
      setEntries(prev=>prev.map(e=>e.id===id?{...e,...updated}:e));
      setUnhandledActions(prev=>prev.filter(e=>e.id!==id));
      setActionNotes(prev=>{const n={...prev};delete n[id];return n;});
      setPromoteModal(null);
      return true;
    }catch(err){
      setPromoteModal(prev=>prev?{...prev,busy:false}:prev);
      throw err;
    }
  };

  // (a) Full amount — the v51 behaviour, unchanged including its collision prompt.
  const promoteFull=async()=>{
    const m=promoteModal; if(!m) return;
    const amt=parseAmountInput(m.amount);
    if(amt==null){alert("Enter a valid amount.");return;}
    try{
      await applyPromote(
        {invoice:m.base,subTotal:amt,feeTotal:0,commissionableAmount:amt},
        actionNotes[m.entry.id]||null);
    }catch(err){
      const msg=String(err&&err.message||err);
      if(msg.includes("23505")||msg.toLowerCase().includes("duplicate key")){
        alert(
          `Can't promote #${m.base} at full amount — ${m.entry.rep} already has an entry `+
          `with that invoice number, which is the one this was flagged against.\n\n`+
          `If the invoice was INCREASED and re-sent, use "Promote as adjustment" instead — `+
          `it records only the difference and leaves the original untouched.\n\n`+
          `Otherwise decide which is the real sale:\n`+
          `• If THIS entry is correct (e.g. the original was a $0 demo), delete or renumber `+
          `the original #${m.base} in the Sales Log, then promote again.\n`+
          `• If the ORIGINAL is correct, dismiss this one instead.\n\n`+
          `Nothing has been changed.`
        );
        return;
      }
      alert("Error promoting duplicate: "+msg);
    }
  };

  // (b) Adjustment — keeps a suffixed invoice so it never collides with the
  // original, which is the whole point: the original row stays exactly as it
  // was and was already paid. Commission falls out of the standard formula on
  // the entered amount via the entry's own tier — no special-case math.
  const promoteAdjustment=async()=>{
    const m=promoteModal; if(!m) return;
    const amt=parseAmountInput(m.amount);
    if(amt==null){alert("Enter a valid amount.");return;}
    if(amt===0&&!window.confirm("Record a $0 adjustment? It will count as a sale with no commission.")) return;
    const note=(actionNotes[m.entry.id]||"").trim()||adjustmentNote(m.base,m.existingAmt,m.captured,fmt);
    // -ADD, then -ADD2... Same escalation the parser uses for -DUP, and for the
    // same reason: (invoice_normalized, rep) is unique and a second increase to
    // the same invoice is a real thing that happens.
    for(let n=1;n<=20;n++){
      const inv=adjustmentInvoice(m.base,n);
      try{
        await applyPromote({invoice:inv,subTotal:amt,feeTotal:0,commissionableAmount:amt},note);
        return;
      }catch(err){
        const msg=String(err&&err.message||err);
        if(msg.includes("23505")||msg.toLowerCase().includes("duplicate key")) continue;
        alert("Error recording adjustment: "+msg);
        return;
      }
    }
    alert(`Could not find a free adjustment number for #${m.base} after 20 attempts. Nothing has been changed.`);
  };

  // ── Status comment ────────────────────────────────────────────────────────
  // "CRC has eyes on but waiting for AGL feedback." The channel for an item
  // that has been SEEN and is deliberately parked — a state the queue could
  // not previously express, since an untouched item and a understood-and-
  // waiting item rendered identically.
  //
  // It is not action_note and must never behave like it. action_note answers
  // "what did you do to close this" and is consumed when the item leaves the
  // list. This answers "what is the situation" and is live the whole time the
  // item is open. The patch touches three columns and nothing else — no
  // actionType, no dismissedAt, no amounts — so commenting can never move an
  // item in or out of the queue or change a dollar figure. See
  // src/statusComment.js.
  const saveStatusComment=async(id,rawText)=>{
    const entry=unhandledActions.find(e=>e.id===id);
    if(!entry) return;

    // There is no Save button, so blur fires this on every field the operator
    // so much as tabs through. Skip the round trip when nothing changed.
    if(!isCommentDirty(entry,rawText)){
      setStatusDrafts(prev=>{const n={...prev};delete n[id];return n;});
      return;
    }

    const patch=statusCommentPatch(rawText,new Date().toISOString(),currentUserEmail);
    setStatusSaving(prev=>({...prev,[id]:"saving"}));
    try{
      await updateEntry(id,patch);
      setUnhandledActions(prev=>prev.map(e=>e.id===id?{...e,...patch}:e));
      setEntries(prev=>prev.map(e=>e.id===id?{...e,...patch}:e));
      // Drop the draft only if the operator has not typed since this save
      // started — otherwise clearing it would revert their newer keystrokes to
      // the text that just landed.
      setStatusDrafts(prev=>{
        if(prev[id]!==rawText) return prev;
        const n={...prev};delete n[id];return n;
      });
      setStatusSaving(prev=>({...prev,[id]:"saved"}));
      setTimeout(()=>setStatusSaving(prev=>{
        if(prev[id]!=="saved") return prev;
        const n={...prev};delete n[id];return n;
      }),2000);
    }catch(err){
      // Deliberately not an alert(). The operator is mid-sentence somewhere in
      // a list; a modal would be worse than the inline "Not saved" marker, and
      // the draft is kept so the text is still on screen to retry or copy.
      console.error("Error saving comment:",err);
      setStatusSaving(prev=>({...prev,[id]:"error"}));
    }
  };

  // Debounced on change, flushed on blur. The debounce alone would lose a
  // comment typed and then abandoned by closing the tab; blur alone would lose
  // one typed into a field that never loses focus. Both, and neither happens.
  const onStatusCommentChange=(id,text)=>{
    setStatusDrafts(prev=>({...prev,[id]:text}));
    if(statusTimers.current[id]) clearTimeout(statusTimers.current[id]);
    statusTimers.current[id]=setTimeout(()=>{
      delete statusTimers.current[id];
      saveStatusComment(id,text);
    },COMMENT_DEBOUNCE_MS);
  };

  const onStatusCommentBlur=(id,text)=>{
    if(statusTimers.current[id]){
      clearTimeout(statusTimers.current[id]);
      delete statusTimers.current[id];
    }
    saveStatusComment(id,text);
  };

  const markActioned=async(id)=>{
    try{
      const entry=unhandledActions.find(e=>e.id===id);
      const flaggedItems=entry?.flaggedItems||[];
      const note=actionNotes[id]||"";
      const loggedEntry=entry||{id,invoice:"",customer:"",rep:"",amount:0,month,actionType:"3rd_party"};
      const logged=await insertHandledAction(loggedEntry,flaggedItems,currentUserEmail,note);
      setHandledActions(prev=>[logged,...prev]);
      await updateEntry(id,{
        needs3rdParty:false,
        flaggedItems:[],
        actionType:null,
        actionNote:note||null,
      });
      setEntries(prev=>prev.map(e=>e.id===id?{...e,needs3rdParty:false,flaggedItems:[],actionType:null,actionNote:note||null}:e));
      setUnhandledActions(prev=>prev.filter(e=>e.id!==id));
      setActionNotes(prev=>{const n={...prev};delete n[id];return n;});
    }catch(err){alert("Error: "+err.message);}
  };

  // Undo puts the item back in front of the operator. For a dismissed duplicate
  // that means clearing dismissed_at — restoring actionType alone would leave
  // the stamp in place and the row would stay out of Needs Action, which is
  // undo appearing to do nothing. Deleting the log row is what re-arms the
  // once-per-entry guard in dismissDuplicate.
  const undoHandledAction=async(ha)=>{
    try{
      const patch=undismissPatch(ha);
      await updateEntry(ha.entry_id,patch);
      const restored=await fetchUnhandledActions();
      setUnhandledActions(restored);
      setEntries(prev=>prev.map(e=>e.id===ha.entry_id?{...e,...patch}:e));
      await deleteHandledAction(ha.id);
      setHandledActions(prev=>prev.filter(h=>h.id!==ha.id));
    }catch(err){alert("Undo error: "+err.message);}
  };

  const isHandledVisible=(ha)=>{
    const handledDate=new Date(ha.handled_at);
    const cutoff=new Date(handledDate.getFullYear(),handledDate.getMonth()+2,0);
    return new Date()<=cutoff;
  };

  const openExportReview=async(code)=>{
    if(!xlsxReady){alert("Excel library still loading.");return;}
    try{
      const freshAdjs=await fetchAdjustments(month);
      setAdjs(freshAdjs);
      const repAdjs=freshAdjs.filter(a=>a.rep===code&&a.month===month);
      setExportAdjOverrides(repAdjs.map(a=>({...a,amount:parseFloat(a.amount)})));
    }catch(e){
      const repAdjs=adjustments.filter(a=>a.rep===code&&a.month===month);
      setExportAdjOverrides(repAdjs.map(a=>({...a,amount:parseFloat(a.amount)})));
    }
    setExportReviewRep(code);
  };

  const doExport=(code,adjOverrides)=>{
    if(!xlsxReady){alert("Excel library still loading.");return;}
    const check=entries.filter(e=>e.month===month&&e.rep===code);
    if(!check.length){alert("No entries for this rep this month.");return;}
    buildRepExcel(entries,month,code,adjOverrides);
  };

  const doAllRepsSummary=()=>{
    if(!xlsxReady){alert("Excel library still loading.");return;}
    buildAllRepsExcel(entries,month,adjustments);setShowExport(false);
  };

  const batchExport=async()=>{
    if(!xlsxReady){alert("Excel library still loading.");return;}
    const repsWithData=Object.keys(REPS).filter(code=>code!=="CRC"&&entries.some(e=>e.month===month&&e.rep===code));
    if(!repsWithData.length){alert("No entries to export.");return;}
    setBatching(true);
    for(let i=0;i<repsWithData.length;i++){
      const code=repsWithData[i];
      const adjs=adjustments.filter(a=>a.rep===code&&a.month===month);
      buildRepExcel(entries,month,code,adjs);
      await new Promise(r=>setTimeout(r,600));
    }
    setBatching(false);setShowExport(false);
  };

  // EXACT effective day, deliberately — NOT the daily window. "Unsent" means
  // orders from this day that have not been emailed yet, and it drives the
  // subject line; widening it to the weekend would re-offer Saturday's orders
  // every Monday as though they were new. Ruled 2026-10-12: leave this alone.
  const unsentEntries=salesEntries.filter(e=>e.dateStr===effectiveDateStr&&!sentEntryIds.has(Number(e.id)));

  const lbData=Object.keys(REPS).map(code=>{
    const es=salesEntries.filter(e=>e.rep===code);if(!es.length)return null;
    // Same window as the team figure above, so the per-rep "Today:" column
    // cannot contradict the "Daily Total:" it sits under.
    const todayEs=es.filter(e=>inDailyWindow(e.dateStr,effectiveDateStr));
    const unsentEs=unsentEntries.filter(e=>e.rep===code);
    const repRevAmt=salesReversals.filter(a=>a.rep===code).reduce((s,a)=>s+parseFloat(a.amount),0);
    const repTotal=es.reduce((s,e)=>s+e.subTotal,0)+repRevAmt;
    return{code,total:repTotal,count:es.length,newC:es.filter(isNewAccount).length,
      todayTotal:todayEs.reduce((s,e)=>s+e.subTotal,0),todayNewC:todayEs.filter(isNewAccount).length,hasToday:todayEs.length>0,
      unsentTotal:unsentEs.reduce((s,e)=>s+e.subTotal,0),unsentNewC:unsentEs.filter(isNewAccount).length,hasUnsent:unsentEs.length>0};
  }).filter(Boolean).sort((a,b)=>b.total-a.total);

  const subjectLine=(()=>{
    const repData=lbData.filter(d=>d.hasUnsent).map(d=>{
      const es=unsentEntries.filter(e=>e.rep===d.code);if(!es.length)return null;
      const totalCount=es.length;const bigNew=es.filter(e=>isNewAccount(e)&&e.subTotal>=1000);
      const bigNonNew=es.filter(e=>!isNewAccount(e)&&e.tier!=="zerocomm"&&e.subTotal>=1000);
      const smallNewC=es.filter(e=>isNewAccount(e)&&e.subTotal<1000).length;
      const newCount=bigNew.length+smallNewC;const bigNewVal=bigNew.reduce((s,e)=>s+e.subTotal,0);
      const bigNonNewMax=bigNonNew.length>0?Math.max(...bigNonNew.map(e=>e.subTotal)):0;const totalVal=es.reduce((s,e)=>s+e.subTotal,0);
      const allSmallNew=smallNewC===totalCount&&bigNew.length===0&&bigNonNew.length===0;
      let str;
      if(allSmallNew){str=d.code+" "+totalCount+" New";}
      else{str=d.code+" "+totalCount+" Sale"+(totalCount!==1?"s":"");if(smallNewC>0)str+=", "+smallNewC+" New";bigNew.forEach(e=>{str+=", "+fmtRound(e.subTotal)+" New";});bigNonNew.forEach(e=>{str+=", "+fmtRound(e.subTotal);});}
      str+="!";return{str,newCount,bigNewVal,bigNonNewMax,totalCount,totalVal};
    }).filter(Boolean);
    repData.sort((a,b)=>{
      if(b.newCount!==a.newCount)return b.newCount-a.newCount;if(b.bigNewVal!==a.bigNewVal)return b.bigNewVal-a.bigNewVal;
      if(b.bigNonNewMax!==a.bigNonNewMax)return b.bigNonNewMax-a.bigNonNewMax;if(b.totalCount!==a.totalCount)return b.totalCount-a.totalCount;
      return b.totalVal-a.totalVal;
    });
    return repData.map(d=>d.str).join("  ")||"No new orders since last send.";
  })();

  const markAsSent=async()=>{
    const newSentIds=new Set([...sentEntryIds,...unsentEntries.map(e=>Number(e.id))]);
    const timeStr=new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
    const count=unsentEntries.length;
    setSentEntryIds(newSentIds);setLastSentTime(timeStr);setLastSentCount(count);
    setLastSubjectLine(subjectLine);
    setSendCycleLoading(true);
    try{await insertSendCycle(month,[...newSentIds],count,subjectLine);}
    catch(e){console.error("Send cycle save error:",e);}
    setSendCycleLoading(false);
  };

  const buildNewAccountsReport=()=>{
    const newEs=salesEntries.filter(isNewAccount);
    if(!newEs.length)return null;
    const byRep=Object.keys(REPS).map(code=>{
      const es=newEs.filter(e=>e.rep===code);
      if(!es.length)return null;
      return{code,name:REPS[code].name,count:es.length,total:es.reduce((s,e)=>s+e.subTotal,0)};
    }).filter(Boolean).sort((a,b)=>b.total-a.total);
    const totalCount=byRep.reduce((s,r)=>s+r.count,0);
    const totalVal=byRep.reduce((s,r)=>s+r.total,0);
    const lines=byRep.map(r=>`${r.code}   ${r.count} account${r.count!==1?"s":""}   ${fmt(r.total)}`).join("\n");
    return `New Accounts - ${month}\n\n${lines}\n\n---------------------\nTotal: ${totalCount} account${totalCount!==1?"s":""} - ${fmt(totalVal)}`;
  };

  const maxLB=lbData[0]?.total||1;
  const actionCount=unhandledActions.length;
  const pendingFlagsCount=commFlags.filter(f=>f.status==="pending").length;

  const adjTypeLabel=(type)=>{
    if(type==="bonus")return"Bonus";if(type==="zero_comm")return"Zero comm";
    if(type==="reduced_comm")return"Reduced comm";if(type==="return")return"Return/credit";
    if(type==="sales_reversal")return"Sales reversal";return"Withholding";
  };

  const markFlagReviewed=async(flag)=>{
  const adminNote=reviewNotes[flag.id]||"";
  try{
    await supabase.from('comm_flags').update({status:'reviewed',admin_note:adminNote||null}).eq('id',flag.id);
    setCommFlags(prev=>prev.map(f=>f.id===flag.id?{...f,status:'reviewed',admin_note:adminNote||null}:f));
    setReviewNotes(prev=>{const n={...prev};delete n[flag.id];return n;});
  }catch(e){alert("Error: "+e.message);}
  };
  const handleLogout=async()=>{
    await logout();
    onLogout();
  };

  const C={
    card:{background:"#fff",border:"0.5px solid #e0e0e0",borderRadius:12,padding:"1.25rem",marginBottom:"1rem"},
    inp:{width:"100%",padding:"8px 10px",fontSize:14,border:"0.5px solid #ccc",borderRadius:8,background:"#fafafa",color:"#111",boxSizing:"border-box"},
    btn:{padding:"8px 14px",fontSize:13,border:"0.5px solid #ccc",borderRadius:8,background:"transparent",cursor:"pointer",color:"#111"},
    btnPri:{padding:"8px 16px",fontSize:13,border:"none",borderRadius:8,background:"#111",color:"#fff",cursor:"pointer"},
    btnSm:{padding:"5px 12px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,background:"transparent",cursor:"pointer",color:"#555"},
    btnDanger:{padding:"5px 10px",fontSize:12,border:"0.5px solid #f5c6c6",borderRadius:8,background:"transparent",cursor:"pointer",color:"#c00"},
    btnRed:{padding:"6px 14px",fontSize:13,border:"none",borderRadius:8,background:"#c00",color:"#fff",cursor:"pointer"},
    tab:a=>({padding:"8px 16px",fontSize:14,cursor:"pointer",border:"none",background:"transparent",color:a?"#111":"#888",borderBottom:a?"2px solid #111":"2px solid transparent",marginBottom:-1}),
    sec:{fontSize:12,fontWeight:500,color:"#888",textTransform:"uppercase",letterSpacing:".04em",marginBottom:".75rem"},
    row:{display:"flex",gap:8,alignItems:"center",marginBottom:8},
    rowLbl:{fontSize:12,color:"#888",minWidth:100,flexShrink:0},
  };

  if(loading)return(<div style={{maxWidth:920,margin:"80px auto",textAlign:"center",fontFamily:"system-ui,sans-serif"}}><Spinner/><div style={{marginTop:12,color:"#888",fontSize:14}}>Loading {month} data…</div></div>);

  const newAccountsReport=buildNewAccountsReport();

  return(
    <div style={{maxWidth:920,margin:"0 auto",padding:"1rem",fontFamily:"system-ui,sans-serif",color:"#111"}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Admin view of exactly what the rep is being asked to re-review — the
          same ConfirmationDiff component, so the two cannot diverge. */}
      {diffModal&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}
          onClick={()=>setDiffModal(null)}>
          <div onClick={ev=>ev.stopPropagation()}
            style={{background:"#fff",borderRadius:14,padding:"1.5rem",width:620,maxWidth:"95vw",maxHeight:"85vh",overflowY:"auto",boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
              <div style={{fontSize:16,fontWeight:600}}>{diffModal.name} — {month}</div>
              <button style={C.btn} onClick={()=>setDiffModal(null)}>Close</button>
            </div>
            <div style={{fontSize:12,color:"#888",marginBottom:14}}>
              What has changed since this rep confirmed. They see the same list on their own page.
            </div>
            <ConfirmationDiff diff={diffModal.diff} confirmedAt={diffModal.confirmedAt}/>
          </div>
        </div>
      )}

      {/* ── Promote a captured duplicate ─────────────────────────────────
          Two outcomes with very different money. Full amount is right when the
          original was not a real sale. Adjustment is right when the invoice was
          increased and re-sent — promoting the full amount there pays the rep a
          second time for the part already commissioned. */}
      {promoteModal&&(()=>{
        const m=promoteModal;
        const rate=getRate(m.entry.rep,m.entry.tier);
        const amt=parseAmountInput(m.amount);
        const commPreview=amt!=null?getComm(amt,m.entry.rep,m.entry.tier):null;
        const hasDiff=m.diff!=null;
        return(
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}
            onClick={()=>{if(!m.busy)closePromoteDialog();}}>
            <div onClick={ev=>ev.stopPropagation()}
              style={{background:"#fff",borderRadius:14,padding:"1.5rem",width:540,maxWidth:"95vw",boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
              <div style={{fontSize:16,fontWeight:600,marginBottom:4}}>Promote #{m.base}</div>
              <div style={{fontSize:13,color:"#888",marginBottom:14}}>
                {m.entry.rep} · {m.entry.customer||"—"} · captured {m.entry.month}
              </div>

              {m.loading
                ?<div style={{fontSize:13,color:"#888",padding:"1rem 0"}}>Looking up the original entry…</div>
                :<>
                  <div style={{background:"#f9f9f8",border:"0.5px solid #e8e8e4",borderRadius:10,padding:"12px 14px",marginBottom:14,fontSize:13,lineHeight:1.8}}>
                    {m.existing
                      ?<>
                        <div>Original <span style={{color:"#888"}}>({m.existing.month})</span>: <strong>{fmt(m.existingAmt)}</strong></div>
                        <div>This capture: <strong>{fmt(m.captured)}</strong></div>
                        <div style={{borderTop:"0.5px solid #e0e0e0",marginTop:6,paddingTop:6}}>
                          Difference: <strong style={{color:m.diff>0?"#3B6D11":m.diff<0?"#991B1B":"#555"}}>{fmt(m.diff)}</strong>
                        </div>
                      </>
                      :<>
                        <div>This capture: <strong>{fmt(m.captured)}</strong></div>
                        <div style={{color:"#888",fontSize:12}}>
                          No live entry found for #{m.base} under {m.entry.rep} — nothing to compare
                          against, so there is no difference to offer.
                        </div>
                      </>}
                  </div>

                  {hasDiff&&m.diff>0&&(
                    <div style={{background:"#FEF6E7",border:"0.5px solid #F5D9A0",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:12,color:"#7A5A11",lineHeight:1.6}}>
                      #{m.base} has already been commissioned at {fmt(m.existingAmt)}. Promoting the
                      full {fmt(m.captured)} would pay {m.entry.rep} a second time on that amount —
                      only the {fmt(m.diff)} increase is new.
                    </div>
                  )}

                  <div style={{marginBottom:6,fontSize:12,color:"#888",fontWeight:500}}>Amount to record</div>
                  <input type="number" step="0.01" value={m.amount} disabled={m.busy}
                    onChange={ev=>setPromoteModal(prev=>prev?{...prev,amount:ev.target.value}:prev)}
                    style={{...C.inp,marginBottom:6}} />
                  <div style={{fontSize:12,color:"#888",marginBottom:16}}>
                    Commission preview: {commPreview!=null?<strong>{fmt(commPreview)}</strong>:"—"}
                    {" "}<span style={{color:"#aaa"}}>
                      ({TIER_LABELS[m.entry.tier]||m.entry.tier} · {(rate*100).toFixed(1).replace(/\.0$/,"")}% · amount × 0.9 × rate)
                    </span>
                  </div>

                  <div style={{display:"flex",gap:8,justifyContent:"flex-end",flexWrap:"wrap"}}>
                    <button onClick={closePromoteDialog} disabled={m.busy} style={C.btn}>Cancel</button>
                    <button onClick={promoteFull} disabled={m.busy}
                      title={`Restore invoice #${m.base} and count the full amount. Collides if the original still exists.`}
                      style={{...C.btn,borderColor:"#3B6D11",color:"#3B6D11"}}>
                      Promote full amount
                    </button>
                    <button onClick={promoteAdjustment} disabled={m.busy}
                      title={`Record only this amount as a new sale in ${m.entry.month}, as #${m.base}-ADD. Leaves the original untouched.`}
                      style={{background:"#EAF3DE",color:"#3B6D11",border:"0.5px solid #3B6D11",borderRadius:8,padding:"8px 14px",fontSize:13,fontWeight:500,cursor:m.busy?"not-allowed":"pointer"}}>
                      {m.busy?"Working…":"Promote as adjustment"}
                    </button>
                  </div>
                  <div style={{fontSize:11,color:"#aaa",marginTop:10,lineHeight:1.6}}>
                    <strong>Full amount</strong> restores #{m.base} — use it when the original was not a
                    real sale. <strong>Adjustment</strong> records the amount above as #{m.base}-ADD in{" "}
                    {m.entry.month} and leaves the original exactly as it is.
                  </div>
                </>}
            </div>
          </div>
        );
      })()}

      {showNewAccountsModal&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#fff",borderRadius:14,padding:"1.5rem",width:480,maxWidth:"95vw",boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div style={{fontSize:15,fontWeight:500}}>New Accounts — {month}</div>
              <button style={{...C.btnSm,fontSize:11}} onClick={()=>{setShowNewAccountsModal(false);setNewAccountsCopied(false);}}>Close</button>
            </div>
            {newAccountsReport
              ? <>
                  <pre style={{fontFamily:"system-ui,sans-serif",fontSize:13,lineHeight:1.7,background:"#f5f5f3",borderRadius:8,padding:"1rem",whiteSpace:"pre-wrap",wordBreak:"break-word",marginBottom:16,color:"#111",maxHeight:400,overflowY:"auto"}}>{newAccountsReport}</pre>
                  <button
                    style={{...C.btnPri,width:"100%",background:newAccountsCopied?"#3B6D11":"#111",transition:"background 0.3s"}}
                    onClick={()=>{navigator.clipboard.writeText(newAccountsReport);setNewAccountsCopied(true);setTimeout(()=>setNewAccountsCopied(false),2000);}}>
                    {newAccountsCopied?"Copied ✓":"Copy"}
                  </button>
                </>
              : <div style={{fontSize:13,color:"#888",textAlign:"center",padding:"2rem 0"}}>No new accounts this month yet.</div>
            }
          </div>
        </div>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1.25rem"}}>
        <div>
          <div style={{fontSize:18,fontWeight:500}}>Certags Sales Tracker</div>
          <div style={{fontSize:13,color:"#888",display:"flex",alignItems:"center",gap:8}}>
            {month}
            {testDate&&<span style={{background:"#fffbe6",border:"0.5px solid #f5d060",borderRadius:20,padding:"2px 10px",fontSize:11,color:"#7a5c00",fontWeight:500}}>TEST MODE — {testDate}</span>}
            {saving&&<span style={{fontSize:12,color:"#888",display:"flex",alignItems:"center",gap:4}}><Spinner/> Saving…</span>}
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <select value={month} onChange={e=>setMonth(e.target.value)} style={{...C.inp,width:"auto",padding:"6px 10px",fontSize:13}}>
            {MONTHS.map(m=><option key={m}>{m}</option>)}
          </select>
          <button style={testDate?{...C.btnSm,background:"#fffbe6",borderColor:"#f5d060",color:"#7a5c00"}:C.btnSm} onClick={()=>setTestMode(v=>!v)}>{testDate?`Test: ${testDate}`:"Test mode"}</button>
          <button style={{...C.btnSm,fontSize:11,color:"#bbb",borderColor:"#eee"}} onClick={handleLogout}>Lock</button>
        </div>
      </div>

      {testMode&&(
        <div style={{background:"#fffbe6",border:"0.5px solid #f5d060",borderRadius:10,padding:"1rem 1.25rem",marginBottom:"1rem"}}>
          <div style={{fontSize:13,fontWeight:500,marginBottom:4}}>Test mode — simulate a different date</div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginTop:8}}>
            <input type="date" value={testDateInput} onChange={e=>setTestDateInput(e.target.value)} style={{...C.inp,width:180,fontSize:13}}/>
            <button style={C.btnPri} onClick={()=>{setTestDate(testDateInput);setTestMode(false);}}>Apply</button>
            {testDate&&<button style={{...C.btn,color:"#c00",borderColor:"#f5c6c6"}} onClick={()=>{setTestDate(null);setTestMode(false);}}>Clear</button>}
          </div>
        </div>
      )}


      <div style={{display:"flex",gap:4,borderBottom:"0.5px solid #e0e0e0",marginBottom:"1.5rem",flexWrap:"wrap"}}>
        {[["leaderboard","Leaderboard"],["dashboard","Dashboard"],["saleslog","Sales Log"],["complypro","ComplyPro"],["parser","Email parser"],["action","Needs Action"],["commreview","Comm Review"]].map(([id,label])=>(
          <button key={id} style={C.tab(tab===id)} onClick={()=>setTab(id)}>
            {label}
            {id==="action"&&actionCount>0&&<span style={{marginLeft:6,background:"#993C1D",color:"#fff",borderRadius:20,fontSize:10,fontWeight:500,padding:"1px 6px"}}>{actionCount}</span>}
            {id==="commreview"&&pendingFlagsCount>0&&<span style={{marginLeft:6,background:"#92400E",color:"#fff",borderRadius:20,fontSize:10,fontWeight:500,padding:"1px 6px"}}>{pendingFlagsCount}</span>}
          </button>
        ))}
      </div>

      {tab==="parser"&&(
        <div>
          <div style={C.card}>
            <div style={C.sec}>Paste invoice email</div>
            <textarea value={emailText} onChange={e=>{setEmailText(e.target.value);setParseWarnings([]);setParseMsg("");setDuplicateWarning(null);}}
              placeholder="Paste the full QuickBooks order email here..."
              style={{...C.inp,height:180,resize:"vertical",fontFamily:"monospace",fontSize:13}}/>
            <div style={{display:"flex",gap:8,marginTop:10,alignItems:"center"}}>
              <button style={C.btnPri} onClick={parseEmail} disabled={parsing}>{parsing?"Parsing…":"Parse with Claude"}</button>
              {parseMsg&&<span style={{fontSize:13,color:parseMsg.startsWith("Parse error")?"#c00":"#3B6D11"}}>{parseMsg}</span>}
            </div>
          </div>
          <div style={C.card}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:".75rem"}}>
              <div style={{...C.sec,marginBottom:0}}>{editingEntry?"Edit entry":"Review & confirm"}</div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                {editingEntry&&<span style={{fontSize:12,color:"#185FA5",background:"#E6F1FB",padding:"3px 10px",borderRadius:6,border:"0.5px solid #B5D4F4",fontWeight:500}}>Editing #{editingEntry.invoice}</span>}
                {testDate&&!editingEntry&&<span style={{fontSize:12,color:"#7a5c00",background:"#fffbe6",padding:"3px 8px",borderRadius:6,border:"0.5px solid #f5d060"}}>Stamped {testDate}</span>}
              </div>
            </div>

            {duplicateWarning&&(
              <div style={{background:"#FFF8E1",border:"0.5px solid #FFC107",borderRadius:8,padding:"12px 14px",marginBottom:12}}>
                <div style={{fontWeight:500,fontSize:13,color:"#E65100",marginBottom:6}}>⚠ Duplicate invoice detected</div>
                <div style={{fontSize:12,color:"#BF360C",marginBottom:10}}>Invoice <strong>#{form.invoice}</strong> already exists in <strong>{duplicateWarning.month}</strong> — entered for <strong>{duplicateWarning.rep}</strong> ({duplicateWarning.customer}, {fmt(duplicateWarning.sub_total!=null?duplicateWarning.sub_total:duplicateWarning.amount)}).</div>
                <div style={{display:"flex",gap:8}}>
                  <button style={{...C.btnPri,background:"#E65100",fontSize:12,padding:"5px 12px"}} onClick={()=>addEntry(true)}>Add anyway (confirmed duplicate)</button>
                  <button style={{...C.btnSm,borderColor:"#FFC107",color:"#E65100"}} onClick={()=>{setDuplicateWarning(null);setForm(BLANK);setEmailText("");setParseMsg("");}}>Cancel — discard this entry</button>
                </div>
              </div>
            )}

            {parseWarnings.length>0&&(
              <div style={{background:parseWarnings[0].startsWith("Parse failed")?"#FCEBEB":"#FFF3E0",border:`0.5px solid ${parseWarnings[0].startsWith("Parse failed")?"#F7C1C1":"#FFB74D"}`,borderRadius:8,padding:"10px 14px",marginBottom:12}}>
                <div style={{fontWeight:500,fontSize:13,color:parseWarnings[0].startsWith("Parse failed")?"#A32D2D":"#E65100",marginBottom:6}}>{parseWarnings[0].startsWith("Parse failed")?"Parse failed — try again":"Review required before adding"}</div>
                {parseWarnings.map((w,i)=>(<div key={i} style={{fontSize:12,color:parseWarnings[0].startsWith("Parse failed")?"#A32D2D":"#BF360C",display:"flex",alignItems:"flex-start",gap:6,marginBottom:3}}><span style={{marginTop:1}}>⚠</span><span>{w}</span></div>))}
              </div>
            )}

            {[
              {label:"Rep",el:<select value={form.rep} onChange={e=>setForm(f=>({...f,rep:e.target.value,tier:e.target.value==="CRC"?"flat":f.tier}))} style={{...C.inp,color:form.rep?"#111":"#aaa"}}>
                <option value="" disabled>Select rep…</option>{Object.keys(REPS).map(c=><option key={c} value={c}>{c} – {REPS[c].name}</option>)}</select>},
              {label:"Invoice #",el:<input style={C.inp} value={form.invoice} onChange={e=>{setForm(f=>({...f,invoice:e.target.value}));setDuplicateWarning(null);}} placeholder="Invoice #"/>},
              {label:"Customer",el:<input style={C.inp} value={form.customer} onChange={e=>setForm(f=>({...f,customer:e.target.value}))} placeholder="Customer company name"/>},
              {label:"Subtotal ($)",el:<input style={C.inp} type="number" step="0.01" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} placeholder="0.00"/>},
              {label:"Fees ($)",el:<input style={C.inp} type="number" step="0.01" value={form.feeAmount} onChange={e=>setForm(f=>({...f,feeAmount:e.target.value}))} placeholder="0.00 (PLATE-FEE, SETUP-FEE, etc. — excluded from commission)"/>},
              {label:"Account type",el:form.rep==="CRC"
                ?<div style={{...C.inp,background:"#f5f5f3",color:"#888",display:"flex",alignItems:"center",gap:8,cursor:"default"}}><span style={{fontSize:13}}>Non-commissioned</span><span style={{background:"#F1EFE8",color:"#5F5E5A",padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:500}}>Counts toward total sales only</span></div>
                :<select value={form.tier} onChange={e=>setForm(f=>({...f,tier:e.target.value}))} style={C.inp}>
                  <option value="new">New account</option><option value="0-12">0–12 months</option>
                  <option value="1-2">1–2 years</option><option value="2-3">2–3 years</option>
                  <option value="3plus">3+ years</option><option value="key">KEY account</option>
                  <option value="inherited">Inherited (DDJ)</option><option value="generated">Generated (DDJ default)</option>
                  <option value="flat">Flat 2% (JRC)</option>
                  <option value="zerocomm">Zero Comm (counts toward totals, $0 commission)</option>
                  <option value="loaner">Loaner (excluded from everything)</option>
                  <option value="nocomm">No Comm (excluded from totals)</option>
                </select>},
            ].map(({label,el})=>(<div key={label} style={C.row}><span style={C.rowLbl}>{label}</span><div style={{flex:1}}>{el}</div></div>))}

            <div style={{...C.row,marginTop:4}}>
              <span style={C.rowLbl}>ComplyPro</span>
              <div style={{display:"flex",gap:12,alignItems:"center",flex:1,flexWrap:"wrap"}}>
                <label style={{display:"flex",alignItems:"center",gap:6,fontSize:14,cursor:"pointer"}}>
                  <input type="checkbox" checked={form.isComplyPro||form.rep==="DDJ"} disabled={form.rep==="DDJ"} onChange={e=>setForm(f=>({...f,isComplyPro:e.target.checked,isPrinter:e.target.checked?f.isPrinter:false}))}/>ComplyPro order
                </label>
                {(form.isComplyPro||form.rep==="DDJ")&&(<label style={{display:"flex",alignItems:"center",gap:6,fontSize:14,cursor:"pointer"}}><input type="checkbox" checked={form.isPrinter||false} onChange={e=>setForm(f=>({...f,isPrinter:e.target.checked}))}/>Printer sale</label>)}
                {form.rep==="DDJ"&&<span style={{fontSize:12,color:"#888"}}>DDJ always ComplyPro</span>}
              </div>
            </div>

            <div style={{...C.row,marginTop:4}}>
              <span style={C.rowLbl}>3rd party</span>
              <div style={{display:"flex",gap:12,alignItems:"center",flex:1,flexWrap:"wrap"}}>
                <label style={{display:"flex",alignItems:"center",gap:6,fontSize:14,cursor:"pointer"}}>
                  <input type="checkbox" checked={form.needs3rdParty||false}
                    onChange={e=>setForm(f=>({...f,needs3rdParty:e.target.checked,flaggedItems:e.target.checked?(f.flaggedItems.length>0?f.flaggedItems:["Misc (3rd party)"]):[]}))}/>
                  Needs 3rd party action
                </label>
                {form.needs3rdParty&&(
                  <input
                    value={(form.flaggedItems||[]).join(", ")}
                    onChange={e=>setForm(f=>({...f,flaggedItems:e.target.value.split(",").map(s=>s.trim()).filter(Boolean)}))}
                    placeholder="e.g. Misc (3rd party), Metal Tags"
                    style={{...C.inp,flex:1,minWidth:200,fontSize:12}}/>
                )}
              </div>
            </div>

            {form.tier==="zerocomm"&&(
              <div style={{background:"#FFF8E1",border:"0.5px solid #856404",borderRadius:8,padding:"10px 12px",fontSize:12,marginBottom:8}}>
                <div style={{fontWeight:500,color:"#856404",marginBottom:2}}>Zero commission — counts toward sales totals, $0 commission paid</div>
                <div style={{color:"#856404",fontSize:11}}>Will appear in leaderboard and totals but commission will be $0.</div>
              </div>
            )}
            {form.tier==="nocomm"&&(<div style={{background:"#F1EFE8",border:"0.5px solid #ccc",borderRadius:8,padding:"10px 12px",fontSize:12,marginBottom:8}}><div style={{fontWeight:500,color:"#5F5E5A",marginBottom:2}}>No commission — excluded from sales totals</div><div style={{color:"#888",fontSize:11}}>Will not appear in totals, leaderboard, or subject line.</div></div>)}
            {form.isLoaner&&(<div style={{background:"#EEF2FF",border:"0.5px solid #A5B4FC",borderRadius:8,padding:"10px 12px",fontSize:12,marginBottom:8}}><div style={{fontWeight:500,color:"#3730A3",marginBottom:2}}>⚠ Loaner detected — excluded from everything</div><div style={{color:"#4338CA",fontSize:11}}>Verify this is correct before adding.</div></div>)}
            {form.needs3rdParty&&form.flaggedItems.length>0&&(<div style={{background:"#FAECE7",border:"0.5px solid #F0997B",borderRadius:8,padding:"10px 12px",fontSize:12,marginBottom:8}}><div style={{fontWeight:500,color:"#993C1D",marginBottom:4}}>⚠ 3rd party items detected</div><div style={{color:"#993C1D"}}>{form.flaggedItems.join(", ")}</div><div style={{color:"#993C1D",marginTop:4,fontSize:11}}>Alert email will fire to ccollard + tracy@certags.com via Zapier.</div></div>)}

            <div style={{...C.row,marginTop:4}}>
              <span style={C.rowLbl}>Commission</span>
              <span style={{fontSize:14,fontWeight:500}}>
                {(()=>{
                  const sub=parseFloat(form.amount)||0;
                  const fee=parseFloat(form.feeAmount)||0;
                  const commissionable=sub-fee;
                  if(form.rep==="CRC")return<span style={{color:"#888"}}>$0.00 — non-commissioned</span>;
                  if(form.tier==="zerocomm")return<span style={{color:"#856404"}}>$0.00 — zero commission item</span>;
                  if(!form.amount)return"—";
                  return(<>
                    {fmt(getComm(commissionable,form.rep,form.tier))} ({(getRate(form.rep,form.tier)*100).toFixed(1)}% × 90%)
                    {fee>0&&<span style={{fontSize:12,color:"#888",marginLeft:8}}>· on {fmt(commissionable)} after {fmt(fee)} fees</span>}
                  </>);
                })()}
              </span>
            </div>
            <div style={{marginTop:14,display:"flex",gap:8}}>
              {editingEntry
                ?<><button style={C.btnPri} onClick={saveEdit} disabled={saving}>{saving?<span style={{display:"flex",alignItems:"center",gap:6}}><Spinner/>Saving…</span>:"Save changes"}</button><button style={C.btn} onClick={cancelEdit}>Cancel edit</button></>
                :<button style={{...C.btnPri,opacity:(parseWarnings.length>0||duplicateWarning)?0.7:1}} onClick={()=>addEntry(false)} disabled={saving||!!duplicateWarning}>{saving?<span style={{display:"flex",alignItems:"center",gap:6}}><Spinner/>Saving…</span>:parseWarnings.length>0?"Add anyway (warnings above)":"Add to tracker"}</button>
              }
            </div>
          </div>
        </div>
      )}

      {tab==="dashboard"&&(
        <div>
          {(()=>{
            const newEntries=salesEntries.filter(isNewAccount);const newValue=newEntries.reduce((s,e)=>s+e.subTotal,0);const newPct=monthTotal>0?(newValue/monthTotal)*100:0;
            return(<div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:"1rem"}}>
              <MetricCard label="Monthly total" value={fmt(monthTotal)}/><MetricCard label="Orders" value={salesEntries.length}/><MetricCard label="New accounts" value={newCount}/>
              <div style={{background:"#f5f5f3",borderRadius:8,padding:"12px 16px",textAlign:"center"}}><div style={{fontSize:12,color:"#888",marginBottom:4}}>New account value</div><div style={{fontSize:18,fontWeight:500}}>{fmt(newValue)}</div><div style={{fontSize:12,color:"#888",marginTop:4}}>{newPct.toFixed(1)}% of total</div></div>
            </div>);
          })()}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.75rem"}}>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <div style={{display:"flex",gap:6}}>
                <button onClick={()=>setShowSales(v=>!v)} style={{padding:"5px 12px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,cursor:"pointer",background:showSales?"#111":"#fff",color:showSales?"#fff":"#888"}}>Sales View</button>
                <button onClick={()=>setShowComm(v=>!v)} style={{padding:"5px 12px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,cursor:"pointer",background:showComm?"#111":"#fff",color:showComm?"#fff":"#888"}}>Comm View</button>
              </div>
              <button style={C.btnSm} onClick={()=>setShowExport(v=>!v)}>{showExport?"Cancel":"Export Excel ↓"}</button>
              <button style={C.btnSm} onClick={()=>setShowPayroll(true)}>Payroll</button>
            </div>
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{padding:"5px 10px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,background:"#fff",color:"#111",cursor:"pointer"}}>
              <option value="total">Sort: Total sales</option><option value="new_value">Sort: New acct value</option><option value="new_count">Sort: # New Accts</option><option value="orders">Sort: # of orders</option>
            </select>
          </div>
          {showExport&&<ExportPanel entries={entries} month={month} adjustments={adjustments} exportReviewRep={exportReviewRep} setExportReviewRep={setExportReviewRep} exportAdjOverrides={exportAdjOverrides} setExportAdjOverrides={setExportAdjOverrides} openExportReview={openExportReview} doExport={doExport} doAllRepsSummary={doAllRepsSummary} batchExport={batchExport} batching={batching} xlsxReady={xlsxReady} setShowExport={setShowExport} setAdjs={setAdjs} C={C}/>}
          {showPayroll&&(()=>{
            const PAYROLL_ORDER=["ZGC","JRC","DDJ","AGL","RTR","TJR","JMG"];
            // Same formula path the Dashboard rep card uses, so the two views
            // match by construction. Reversal commission is formula-applied
            // (amount × 0.9 × rate via aggregateReversals); pre-fix this added
            // the raw negative dollar amount and produced bogus negative
            // payouts on small months (e.g. TJR May → -$270.66).
            const lines=PAYROLL_ORDER.filter(code=>salesEntries.some(e=>e.rep===code)).map(code=>{
              const es=salesEntries.filter(e=>e.rep===code);
              const allRepAdjs=adjustments.filter(a=>a.rep===code&&a.month===month);
              const repReversals=allRepAdjs.filter(a=>a.type==="sales_reversal");
              const repNonRevAdjs=allRepAdjs.filter(a=>a.type!=="sales_reversal");
              const baseComm=es.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
              const nonRevAdjTotal=repNonRevAdjs.reduce((s,a)=>s+parseFloat(a.amount),0);
              const {commTotal:reversalCommTotal, unresolved:unresolvedRevs}=aggregateReversals(repReversals, monthEntries);
              const comm=baseComm+nonRevAdjTotal+reversalCommTotal;
              return{code,comm,unresolvedCount:unresolvedRevs.length};
            });
            const payrollText=lines.map(l=>`${l.code}\t${fmt(l.comm)}`).join("\n");
            const anyUnresolved=lines.some(l=>l.unresolvedCount>0);
            return(
              <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
                <div style={{background:"#fff",borderRadius:12,padding:"1.5rem",width:340,boxShadow:"0 8px 32px rgba(0,0,0,0.15)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                    <div style={{fontSize:14,fontWeight:500}}>Payroll — {month}</div>
                    <button style={{...C.btnSm,fontSize:11}} onClick={()=>setShowPayroll(false)}>Close</button>
                  </div>
                  <div style={{fontFamily:"monospace",fontSize:14,lineHeight:2,marginBottom:16}}>
                    {lines.map(l=>(
                      <div key={l.code} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontWeight:500}}>
                          {l.code}
                          {l.unresolvedCount>0&&<span title={`${l.unresolvedCount} reversal(s) need tier — set in Sales Log`} style={{marginLeft:6,fontSize:11,color:"#92400E"}}>⚠</span>}
                        </span>
                        <span>{fmt(l.comm)}</span>
                      </div>
                    ))}
                  </div>
                  {anyUnresolved&&(
                    <div style={{fontSize:11,color:"#92400E",background:"#FEF3C7",border:"0.5px solid #FDE68A",borderRadius:6,padding:"6px 10px",marginBottom:12}}>
                      ⚠ marked reps have reversals missing a tier — those reversals are excluded from these totals. Open Sales Log to set them.
                    </div>
                  )}
                  <button style={{...C.btnPri,width:"100%"}} onClick={()=>navigator.clipboard.writeText(payrollText)}>Copy ↓</button>
                </div>
              </div>
            );
          })()}
          {Object.keys(REPS).sort((a,b)=>{
            const esA=salesEntries.filter(e=>e.rep===a);const esB=salesEntries.filter(e=>e.rep===b);
            if(sortBy==="total")return esB.reduce((s,e)=>s+e.subTotal,0)-esA.reduce((s,e)=>s+e.subTotal,0);
            if(sortBy==="new_value")return esB.filter(isNewAccount).reduce((s,e)=>s+e.subTotal,0)-esA.filter(isNewAccount).reduce((s,e)=>s+e.subTotal,0);
            if(sortBy==="new_count")return esB.filter(isNewAccount).length-esA.filter(isNewAccount).length;
            if(sortBy==="orders")return esB.length-esA.length;return 0;
          }).map(code=>{
            const es=salesEntries.filter(e=>e.rep===code);const tot=es.reduce((s,e)=>s+e.subTotal,0);
            const repAdjs=adjustments.filter(a=>a.rep===code&&a.month===month);const adjTotal=repAdjs.reduce((s,a)=>s+parseFloat(a.amount),0);
            // Reversal commission via the shared resolver — formula-applied,
            // not raw $. Non-reversal adjustments still fold in at raw $.
            const repReversals=repAdjs.filter(a=>a.type==="sales_reversal");
            const {commTotal:reversalCommTotal, unresolved:unresolvedRevs}=aggregateReversals(repReversals, monthEntries);
            const com=es.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0)+repAdjs.filter(a=>a.type!=="sales_reversal").reduce((s,a)=>s+parseFloat(a.amount),0)+reversalCommTotal;
            const repRevAmt=repReversals.reduce((s,a)=>s+parseFloat(a.amount),0);
            const adjSalesTotal=tot+repRevAmt;
            return(
              <div key={code} style={{border:"0.5px solid #e0e0e0",borderRadius:12,overflow:"hidden",marginBottom:"0.75rem"}}>
                <div onClick={()=>setOpenReps(o=>({...o,[code]:!o[code]}))} style={{padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",background:"#f9f9f8",cursor:"pointer",userSelect:"none"}}>
                  <span style={{fontWeight:500}}>{REPS[code].name} <span style={{fontSize:12,color:"#888",fontWeight:400}}>{code}</span></span>
                  <span style={{fontSize:13,color:"#888",display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                    {showSales&&<span>{fmt(adjSalesTotal)} · {es.length} orders{repRevAmt<0&&<span style={{color:"#c00",fontSize:11,marginLeft:4}}> ({fmt(repRevAmt)} reversal)</span>}</span>}
                    {showComm&&<span style={{color:"#555"}}>comm {fmt(com)}{unresolvedRevs.length>0&&<span title={`${unresolvedRevs.length} reversal(s) need tier — set in Sales Log`} style={{marginLeft:4,fontSize:11,color:"#92400E"}}>⚠</span>}</span>}
                    {(()=>{const newEs=es.filter(isNewAccount);const newTot=newEs.reduce((s,e)=>s+e.subTotal,0);if(!newEs.length)return null;return<span style={{color:"#1a7a3a",fontWeight:500}}>{newEs.length} new · {fmt(newTot)}</span>;})()}
                  </span>
                </div>
                {openReps[code]&&(
                  <div style={{padding:"1rem"}}>
                    {es.length===0?<div style={{fontSize:13,color:"#888"}}>No orders this month</div>
                      :<>
                        <div style={{display:"grid",gridTemplateColumns:showSales?"85px 1fr 100px 140px 52px":"85px 1fr 140px 32px",gap:8,fontSize:11,color:"#888",fontWeight:500,textTransform:"uppercase",paddingBottom:6,borderBottom:"0.5px solid #e0e0e0"}}>
                          <span>Invoice</span><span>Customer</span>{showSales&&<span>Amount</span>}<span>Type</span><span/>
                        </div>
                        {es.map(e=>{
                          const hasFees=(e.feeTotal||0)>0;
                          const amountColor=e.isZero?"#aaa":(e.isCredit?"#c00":"inherit");
                          return(
                          <div key={e.id} style={{display:"grid",gridTemplateColumns:showSales?"85px 1fr 100px 140px 52px":"85px 1fr 140px 32px",gap:8,padding:"7px 0",borderBottom:"0.5px solid #f0f0f0",fontSize:13,alignItems:"center",opacity:e.isZero?0.6:1}}>
                            <span style={{fontFamily:"monospace",fontSize:12}}>#{e.invoice}</span>
                            <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.customer}{e.rationale&&<span title={e.rationale} style={{marginLeft:6,color:"#aaa",fontSize:11,cursor:"help"}}>ⓘ</span>}</span>
                            {showSales&&<span style={{color:amountColor}}>{e.isZero
                              ?<span style={{display:"flex",alignItems:"center",gap:4}}><span>$0.00</span><span style={{fontSize:10,background:"#f1efe8",color:"#888",padding:"1px 5px",borderRadius:10}}>no comm</span></span>
                              :hasFees
                                ?<span style={{display:"flex",flexDirection:"column",lineHeight:1.2}} title={`Sub ${fmt(e.subTotal)} − Fees ${fmt(e.feeTotal)} = Commissionable ${fmt(e.commissionableAmount)}`}>
                                    <span>{fmt(e.subTotal)}{e.isCredit&&<span style={{marginLeft:4,fontSize:10,background:"#FEE2E2",color:"#991B1B",padding:"1px 5px",borderRadius:10,fontWeight:500}}>CR</span>}</span>
                                    <span style={{fontSize:10,color:"#888"}}>−{fmt(e.feeTotal)} fees · {fmt(e.commissionableAmount)} comm</span>
                                  </span>
                                :<span>{fmt(e.subTotal)}{e.isCredit&&<span style={{marginLeft:4,fontSize:10,background:"#FEE2E2",color:"#991B1B",padding:"1px 5px",borderRadius:10,fontWeight:500}}>CR</span>}</span>
                            }</span>}
                            <span style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                              <Badge tier={e.tier} rep={e.rep}/>
                              {e.rep==="DDJ"&&isNewAccount(e)&&<span style={{background:"#EAF3DE",color:"#3B6D11",padding:"2px 6px",borderRadius:20,fontSize:10,fontWeight:500}}>New</span>}
                              {e.needs3rdParty&&<span style={{background:"#FAECE7",color:"#993C1D",padding:"2px 6px",borderRadius:20,fontSize:10,fontWeight:500}}>3P</span>}
                              {e.isComplyPro&&<span style={{background:"#E6F1FB",color:"#185FA5",padding:"2px 6px",borderRadius:20,fontSize:10,fontWeight:500}}>CP</span>}
                              {e.isLoaner&&<span style={{background:"#EEF2FF",color:"#3730A3",padding:"2px 6px",borderRadius:20,fontSize:10,fontWeight:500}}>Loaner</span>}
                            </span>
                            <div style={{display:"flex",gap:2}}>
                              <button onClick={()=>startEdit(e)} title="Edit entry" style={{background:"none",border:"none",color:"#aaa",cursor:"pointer",fontSize:12,padding:"2px 4px",lineHeight:1}}>✏</button>
                              <button onClick={()=>deleteEntry(e.id)} title="Delete entry" style={{background:"none",border:"none",color:"#aaa",cursor:"pointer",fontSize:13,padding:"2px 4px"}}>✕</button>
                            </div>
                          </div>
                          );
                        })}
                        <div style={{marginTop:10,fontSize:13,color:"#666",display:"flex",gap:16,flexWrap:"wrap",alignItems:"center"}}>
                          <span>New: <strong>{es.filter(isNewAccount).length}</strong></span>
                          {showComm&&<span>Commission: <strong>{fmt(com)}</strong></span>}
                          {showComm&&repAdjs.length>0&&<span style={{color:"#854F0B"}}>Adj: <strong>{fmt(adjTotal)}</strong></span>}
                          {showComm&&<button style={{...C.btnSm,marginLeft:"auto"}} onClick={()=>setShowAdjPanel(showAdjPanel===code?null:code)}>+ Adjustment</button>}
                        </div>
                        {showAdjPanel===code&&(
                          <div style={{marginTop:12,background:"#f9f9f8",borderRadius:8,padding:"12px",border:"0.5px solid #e0e0e0"}}>
                            <div style={{fontSize:12,fontWeight:500,marginBottom:8}}>Add commission adjustment for {REPS[code].name}</div>
                            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
                              <select value={adjForm.type} onChange={e=>setAdjForm(f=>({...f,type:e.target.value}))} style={{...C.inp,width:"auto",fontSize:13}}>
                                <option value="withholding">Withholding (–)</option><option value="bonus">Bonus (+)</option>
                                <option value="zero_comm">Zero commission override</option><option value="reduced_comm">Reduced commission</option><option value="return">Return / credit (–)</option>
                              </select>
                              <input style={{...C.inp,width:120}} type="number" step="0.01" value={adjForm.amount} onChange={e=>setAdjForm(f=>({...f,amount:e.target.value}))} placeholder="Amount"/>
                              <input style={{...C.inp,flex:1,minWidth:150}} value={adjForm.note} onChange={e=>setAdjForm(f=>({...f,note:e.target.value}))} placeholder="Note (e.g. pricing issue on #23602)"/>
                            </div>
                            <div style={{display:"flex",gap:8}}>
                              <button style={C.btnPri} onClick={()=>addAdjustment(code)}>Save</button>
                              <button style={C.btn} onClick={()=>setShowAdjPanel(null)}>Cancel</button>
                            </div>
                            {repAdjs.length>0&&(
                              <div style={{marginTop:10}}>
                                {repAdjs.map(a=>(<div key={a.id} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#666",padding:"4px 0",borderTop:"0.5px solid #f0f0f0"}}>
                                  <span style={{display:"flex",alignItems:"center",gap:6}}>
                                    {a.type==="sales_reversal"&&<span style={{background:"#FEE2E2",color:"#991B1B",padding:"1px 6px",borderRadius:10,fontSize:10,fontWeight:500}}>REVERSAL</span>}
                                    {adjTypeLabel(a.type)}: {a.note||"—"}
                                  </span>
                                  <span style={{display:"flex",gap:8,alignItems:"center"}}><span style={{color:parseFloat(a.amount)>0?"#3B6D11":"#c00",fontWeight:500}}>{fmt(parseFloat(a.amount))}</span><button style={C.btnDanger} onClick={()=>removeAdjustment(a.id)}>✕</button></span>
                                </div>))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    }
                  </div>
                )}
              </div>
            );
          })}

          {(
            <div style={{marginTop:32}}>
              <h3 style={{fontSize:16,fontWeight:600,marginBottom:16}}>{month} Confirmation Status</h3>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))",gap:16}}>
                {/* Reads BOTH tables, via the same repConfirmStatus the rep's
                    own badge and the summary at the leaderboard tab use.

                    It used to read month_confirmations alone, and on 2026-09-01
                    that became visibly wrong: Andrew, Jose and Roman confirmed
                    through the toolbox — which writes commission_confirmations
                    via confirm_commission_month() — and this grid showed all
                    three as "Not confirmed" while Zack, who had used the older
                    path, showed green. Three reps were told they had not done
                    something they had done.

                    The comment at the leaderboard summary already said it: if
                    these two ever disagreed there would be no way to tell which
                    was right. They disagreed because this one was computed
                    differently. Now it is not. */}
                {Object.keys(REPS).filter(code=>code!=="CRC").map(code=>{
                  const legacyAt=(monthConfirmations||[]).find(c=>c.rep===code&&c.month===month)?.confirmed_at||null;
                  const status=repConfirmStatus(code,legacyAt);
                  // STALE is confirmed-but-changed-since, and it must not render
                  // as either green or red. Green would hide that the numbers
                  // moved after the rep signed off; red would claim they never
                  // signed off at all.
                  const isStale=status.state===CONFIRM_STATE.STALE;
                  const isConfirmed=status.state===CONFIRM_STATE.CONFIRMED||status.state===CONFIRM_STATE.LEGACY;
                  const bg=isStale?"#FFF3CD":isConfirmed?"#D4EDDA":"#F8D7DA";
                  const bd=isStale?"#FFC107":isConfirmed?"#C3E6CB":"#F5C6CB";
                  const fg=isStale?"#856404":isConfirmed?"#155724":"#721C24";
                  return(
                    <div key={code} style={{background:bg,border:`1px solid ${bd}`,borderRadius:8,padding:12}}>
                      <div style={{fontWeight:600,marginBottom:4}}>{REPS[code].name}</div>
                      <div style={{fontSize:13,color:fg}}>
                        {isStale
                          ? `⚠️ Changed since ${new Date(status.confirmedAt).toLocaleDateString()}`
                          : isConfirmed
                            ? `✓ Confirmed ${new Date(status.confirmedAt).toLocaleDateString()}`
                            : "⚠️ Not confirmed"}
                        {status.state===CONFIRM_STATE.LEGACY&&(
                          <span style={{fontSize:11,opacity:.75,marginLeft:6}}
                            title="Confirmed before snapshots were recorded — cannot be checked for changes.">pre-snapshot</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {tab==="leaderboard"&&(
        <div>
          {showPrompt&&(
            <div style={{background:"#fffbe6",border:"0.5px solid #f5d060",borderRadius:10,padding:"1rem 1.25rem",marginBottom:"1rem"}}>
              <div style={{fontSize:14,fontWeight:500,marginBottom:8}}>Enter {month} total from last year</div>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <input style={{...C.inp,width:200}} value={prevInput} onChange={e=>setPrevInput(e.target.value)} placeholder="e.g. 380000" onKeyDown={e=>e.key==="Enter"&&savePrev()}/>
                <button style={C.btnPri} onClick={savePrev}>Set goal</button><button style={C.btn} onClick={()=>setShowPrompt(false)}>Skip</button>
              </div>
              <div style={{fontSize:12,color:"#888",marginTop:6}}>Sets a +25% growth goal and enables pace tracking.</div>
            </div>
          )}
          <div style={C.card}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1rem"}}>
              <div style={C.sec}>Pace & goal — {month}</div>
              <button style={C.btnSm} onClick={()=>{setShowPrompt(true);setPrevInput(prevYear?String(prevYear):"");}}>{prevYear?"Edit prior year":"+ Add prior year"}</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:12}}>
              <MetricCard label="Today's total" value={fmt(todayTotal)} sub={`${todayEntries.length} order${todayEntries.length!==1?"s":""} today`} warn={!!testDate}/>
              <MetricCard label="Monthly total" value={fmt(monthTotal)} sub={`${salesEntries.length} orders`}/>
              <MetricCard label="Projected pace" value={fmt(pace)} sub={`${fmt(dailyAvg)}/day avg`} highlight/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
              <MetricCard label="Business days" value={`${fmtBiz(elapsedBiz)} / ${totalBiz}`} sub={`${fmtBiz(totalBiz-elapsedBiz)} remaining`} warn={!!testDate}/>
              {prevYear?<MetricCard label={`Prior year (${priorYearLabel(month)})`} value={fmt(prevYear)}/>
                :<div onClick={()=>setShowPrompt(true)} style={{background:"#f5f5f3",borderRadius:8,padding:"12px 16px",textAlign:"center",cursor:"pointer",border:"0.5px dashed #ccc"}}><div style={{fontSize:12,color:"#888",marginBottom:4}}>Prior year (same month)</div><div style={{fontSize:14,color:"#bbb"}}>Click to add ↗</div></div>}
              {goalTotal?<MetricCard label="25% growth goal" value={fmt(goalTotal)} sub={gapToGoal>0?`${fmt(gapToGoal)} to go`:"Goal reached!"}/>
                :<div style={{background:"#f5f5f3",borderRadius:8,padding:"12px 16px",textAlign:"center"}}><div style={{fontSize:12,color:"#888",marginBottom:4}}>25% growth goal</div><div style={{fontSize:14,color:"#bbb"}}>Add prior year to unlock</div></div>}
            </div>
            {goalTotal&&(
              <div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#888",marginBottom:4}}><span>Monthly progress vs goal</span><span style={{fontWeight:500,color:monthVsGoal>=100?"#3B6D11":"#185FA5"}}>{monthVsGoal.toFixed(1)}%</span></div>
                <ProgressBar pct={monthVsGoal} color={monthVsGoal>=100?"#3B6D11":"#185FA5"}/>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#888",marginTop:12,marginBottom:4}}><span>Pace projection vs goal</span><span style={{fontWeight:500,color:paceVsGoal>=100?"#3B6D11":"#854F0B"}}>{paceVsGoal.toFixed(1)}%</span></div>
                <ProgressBar pct={paceVsGoal} color={paceVsGoal>=100?"#3B6D11":"#854F0B"}/>
              </div>
            )}
          </div>

          <div style={C.card}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:".75rem"}}>
              <div>
                <div style={C.sec}>Subject line — unsent orders</div>
                {lastSentTime&&<div style={{fontSize:12,color:"#888",marginTop:-4}}>Last sent: <strong>{lastSentTime}</strong> · {lastSentCount} order{lastSentCount!==1?"s":""} included {sendCycleLoading&&<Spinner/>}</div>}
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                {unsentEntries.length>0&&(<button style={{...C.btnPri,fontSize:12,padding:"5px 12px"}} onClick={()=>{navigator.clipboard.writeText(subjectLine);markAsSent();}}>Copy & mark sent ✓</button>)}
                <button style={C.btnSm} onClick={()=>navigator.clipboard.writeText(subjectLine)}>Copy only</button>
                {lastSubjectLine&&<button style={C.btnSm} onClick={()=>navigator.clipboard.writeText(lastSubjectLine)} title="Re-copy last sent subject line">↩ Re-copy last</button>}
              </div>
            </div>
            <div style={{background:"#f5f5f3",borderRadius:8,padding:"1rem",fontFamily:"monospace",fontSize:14,lineHeight:1.9,minHeight:40,whiteSpace:"pre-wrap",color:"#111"}}>{subjectLine}</div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6}}>
              <div style={{fontSize:12,color:"#888"}}>Unsent orders only · rounded to nearest $100 · "New" flag on new accounts</div>
              {unsentEntries.length>0&&<div style={{fontSize:12,color:"#185FA5",fontWeight:500}}>{unsentEntries.length} unsent order{unsentEntries.length!==1?"s":""}</div>}
            </div>
          </div>

          <div style={C.card}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:".75rem"}}>
              <div><div style={C.sec}>Email body</div></div>
              <button style={{...C.btnSm,background:emailCopied?"#EAF3DE":"transparent",color:emailCopied?"#3B6D11":"#555",borderColor:emailCopied?"#3B6D11":"#ccc",transition:"all 0.3s"}}
              onClick={()=>{
                const projGrowth=prevYear?((pace-prevYear)/prevYear)*100:0;
                const row=(label,value,style="font-size:17px;color:#333")=>`<table width="400" cellpadding="0" cellspacing="0" style="margin:0 0 4px;width:400px"><tr><td style="${style}">${label}</td><td align="right" style="${style}">${value}</td></tr></table>`;
                const html=`<div style="width:400px;font-family:Calibri,Arial,sans-serif">`+[row("Monthly Total:",""+fmt(monthTotal),"font-size:24px;font-weight:bold;color:#1a7a3a;padding-bottom:2px"),row("Daily Total:",""+fmt(todayTotal),"font-size:17px;color:#185FA5"),row("On Pace For:",""+fmt(pace),"font-size:17px;font-weight:bold;color:#1a7a3a;padding-bottom:12px"),prevYear?row("Prior Year Total:",""+fmt(prevYear),"font-size:16px;color:#1a7a3a;font-style:italic;padding-top:8px"):"",goalTotal?row("Flex Day Target (25%):",""+fmt(goalTotal),"font-size:16px;color:#1a7a3a;font-style:italic"):"",goalTotal?`<table width="400" cellpadding="0" cellspacing="0" style="width:400px;margin:0 0 8px;background:#fff176"><tr><td style="font-size:16px;font-weight:bold;font-style:italic;color:#1a7a3a;padding:2px 4px">Projected Growth:</td><td align="right" style="font-size:16px;font-weight:bold;font-style:italic;color:#1a7a3a;padding:2px 4px">${projGrowth.toFixed(1)}%</td></tr></table>`:"",row("Daily Avg.",""+fmt(dailyAvg),"font-size:16px;color:#555;padding-top:4px")].filter(Boolean).join("")+`</div>`;
                const blob=new Blob([html],{type:"text/html"});
                const plain=["Monthly Total:  "+fmt(monthTotal),"Daily Total:    "+fmt(todayTotal),"On Pace For:    "+fmt(pace),"",prevYear?"Prior Year Total:  "+fmt(prevYear):"",goalTotal?"25% Growth Target: "+fmt(goalTotal):"",goalTotal?"Projected Growth:  "+projGrowth.toFixed(1)+"%":"","","Daily Avg:      "+fmt(dailyAvg)].filter(Boolean).join("\n");
                const plainBlob=new Blob([plain],{type:"text/plain"});
                try{navigator.clipboard.write([new ClipboardItem({"text/html":blob,"text/plain":plainBlob})]);}catch(e){navigator.clipboard.writeText(plain);}
                setEmailCopied(true);setTimeout(()=>setEmailCopied(false),2000);
              }}>{emailCopied?"Copied ✓":"Copy"}</button>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}><span style={{fontSize:18,fontWeight:500,color:"#1a7a3a"}}>Monthly Total:</span><span style={{fontSize:18,fontWeight:500,color:"#1a7a3a"}}>{fmt(monthTotal)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}><span style={{fontSize:14,color:"#185FA5"}}>Daily Total:</span><span style={{fontSize:14,color:"#185FA5"}}>{fmt(todayTotal)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:16}}><span style={{fontSize:14,fontWeight:500,color:"#1a7a3a"}}>On Pace For:</span><span style={{fontSize:14,fontWeight:500,color:"#1a7a3a"}}>{fmt(pace)}</span></div>
            {prevYear&&(
              <div style={{borderTop:"0.5px solid #e8e8e8",paddingTop:12,marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#854F0B",marginBottom:4}}><span>Prior Year Total ({priorYearLabel(month)}):</span><span>{fmt(prevYear)}</span></div>
                {goalTotal&&<div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#854F0B",marginBottom:4}}><span>25% Growth Target:</span><span>{fmt(goalTotal)}</span></div>}
                {goalTotal&&(()=>{const projGrowth=prevYear?((pace-prevYear)/prevYear)*100:0;const onTrack=projGrowth>=25;return<div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:500,padding:"4px 8px",borderRadius:4,background:"#fff176",color:onTrack?"#1a7a3a":"#c00",fontStyle:"italic"}}><span>Projected Growth:</span><span>{projGrowth.toFixed(1)}%</span></div>;})()}
              </div>
            )}
            <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#555"}}><span>Daily Avg.</span><span>{fmt(dailyAvg)}</span></div>
          </div>

          <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginBottom:"0.75rem"}}>
            <button
              style={{...C.btnSm,color:"#3B6D11",borderColor:newCount>0?"#3B6D11":"#ccc",opacity:newCount>0?1:0.5}}
              onClick={()=>setShowNewAccountsModal(true)}>
              New Accounts{newCount>0?` (${newCount})`:""}
            </button>
            <button style={C.btnSm} onClick={()=>setShowExport(v=>!v)}>{showExport?"Cancel":"Export Excel ↓"}</button>
          </div>
          {showExport&&<ExportPanel entries={entries} month={month} adjustments={adjustments} exportReviewRep={exportReviewRep} setExportReviewRep={setExportReviewRep} exportAdjOverrides={exportAdjOverrides} setExportAdjOverrides={setExportAdjOverrides} openExportReview={openExportReview} doExport={doExport} doAllRepsSummary={doAllRepsSummary} batchExport={batchExport} batching={batching} xlsxReady={xlsxReady} setShowExport={setShowExport} setAdjs={setAdjs} C={C}/>}

          {lbData.length>0&&(
            <div style={C.card}>
              <div style={C.sec}>Individual rep breakdown</div>
              {lbData.map((d,i)=>{
                const repEs=salesEntries.filter(e=>e.rep===d.code);
                const repAdjs=adjustments.filter(a=>a.rep===d.code&&a.month===month);
                // Same resolver as Dashboard/Payroll/Excel — keeps the
                // Leaderboard's commission column in sync with the other views.
                const repReversals=repAdjs.filter(a=>a.type==="sales_reversal");
                const {commTotal:reversalCommTotal}=aggregateReversals(repReversals, monthEntries);
                const com=repEs.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0)+repAdjs.filter(a=>a.type!=="sales_reversal").reduce((s,a)=>s+parseFloat(a.amount),0)+reversalCommTotal;
                return(
                  <div key={d.code} style={{marginBottom:20,paddingBottom:20,borderBottom:i<lbData.length-1?"0.5px solid #f0f0f0":"none"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span style={{fontSize:13,color:"#bbb",fontWeight:500,minWidth:20}}>#{i+1}</span>
                        <span style={{fontWeight:500,fontSize:15}}>{REPS[d.code].name}</span>
                        <span style={{fontSize:12,color:"#888"}}>{d.code}</span>
                        {d.newC>0&&<span style={{background:"#EAF3DE",color:"#3B6D11",padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:500}}>{d.newC} new</span>}
                      </div>
                      <div style={{textAlign:"right",fontSize:13,lineHeight:1.6}}>
                        <div style={{fontWeight:500,fontSize:16}}>{fmt(d.total)}</div>
                        <div style={{color:"#888"}}>{d.count} orders · comm {fmt(com)}</div>
                      </div>
                    </div>
                    <ProgressBar pct={(d.total/maxLB)*100}/>
                    <div style={{display:"flex",gap:20,marginTop:7,fontSize:12,color:"#888",flexWrap:"wrap"}}>
                      <span>Today: <strong style={{color:d.hasToday?"#3B6D11":"#555"}}>{d.hasToday?fmtS(d.todayTotal):"—"}</strong></span>
                      <span>MTD: <strong style={{color:"#555"}}>{fmt(d.total)}</strong></span>
                      <span>Avg sale: <strong style={{color:"#555"}}>{fmt(d.count>0?d.total/d.count:0)}</strong></span>
                      <span>New: <strong style={{color:"#555"}}>{d.newC}</strong></span>
                      <span>Eff. comm: <strong style={{color:"#555"}}>{((com/(d.total||1))*100||0).toFixed(1)}%</strong></span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab==="saleslog"&&(()=>{
        const sorted=[...monthEntries].sort((a,b)=>{if(b.dateStr!==a.dateStr)return b.dateStr.localeCompare(a.dateStr);return parseInt(b.invoice)-parseInt(a.invoice);});
        const sortedSales=sorted.filter(e=>e.tier!=="loaner"&&e.tier!=="nocomm");
        const revRows=salesReversals.map(a=>({
          _isReversal:true,id:"rev-"+a.id,adjId:a.id,
          rep:a.rep,customer:"—",invoice:a.invoice_ref||"—",
          amount:parseFloat(a.amount),note:a.note,dateStr:a.created_at?a.created_at.slice(0,10):month,
          // Full adjustment row passes through so the renderer can read tier
          // (or fall back to inv_ref → entry lookup via the resolver) and
          // show either the formula-applied commission impact or an inline
          // tier picker for unresolved legacy rows.
          adj:a,
        }));
        const q=salesLogSearch.trim();
        // Search mode pulls from the cross-month query and ignores the month
        // selector + sales reversals (reversals live in adjustments, not the
        // entries table the search hits).
        const inSearch=!!q;
        const searchSorted=inSearch
          ?[...searchAllResults].sort((a,b)=>{
            if((b.dateStr||"")!==(a.dateStr||""))return(b.dateStr||"").localeCompare(a.dateStr||"");
            return parseInt(b.invoice||"0")-parseInt(a.invoice||"0");
          })
          :[];
        const visibleEntries=inSearch?searchSorted:sorted;
        const visibleRevs   =inSearch?[]:revRows;
        const totalMatches=visibleEntries.length;
        const colTemplate=inSearch
          ?"85px 95px 110px 60px 1fr 100px 100px 40px 40px 72px"
          :"85px 95px 60px 1fr 100px 100px 40px 40px 72px";
        return(
          <div>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
              <div style={{position:"relative",flex:1,maxWidth:420}}>
                <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"#aaa",fontSize:13,pointerEvents:"none"}}>🔍</span>
                <input
                  value={salesLogSearch}
                  onChange={e=>setSalesLogSearch(e.target.value)}
                  placeholder="Search all months — invoice, customer, or rep…"
                  style={{...C.inp,padding:"7px 32px 7px 30px",fontSize:13}}
                />
                {salesLogSearch&&(
                  <button onClick={()=>setSalesLogSearch("")} title="Clear" style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"#aaa",cursor:"pointer",fontSize:14,padding:"2px 6px",lineHeight:1}}>✕</button>
                )}
              </div>
              {inSearch&&searchingAllMonths&&<span style={{fontSize:12,color:"#888",display:"flex",alignItems:"center",gap:6}}><Spinner/>Searching…</span>}
            </div>

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:13,color:inSearch?"#5B21B6":"#888"}}>
                {inSearch
                  ?<><strong>Searching all months:</strong> {totalMatches} result{totalMatches!==1?"s":""} found <span style={{fontSize:11,color:"#888",marginLeft:8}}>(month selector ignored)</span></>
                  :<>{sortedSales.length} orders · {fmt(sortedSales.reduce((s,e)=>s+e.subTotal,0)+salesReversalTotal)} total
                    {salesReversalTotal<0&&<span style={{fontSize:11,color:"#c00",marginLeft:6}}>({fmt(salesReversalTotal)} reversals applied)</span>}
                    {sorted.length!==sortedSales.length?<span style={{fontSize:11,color:"#aaa"}}> ({sorted.length-sortedSales.length} loaner/no-comm not shown)</span>:""}</>
                }
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <button style={{...C.btnPri,fontSize:12,padding:"5px 12px"}} onClick={()=>{
                  setForm(BLANK);setEmailText("");setParseMsg("");setDuplicateWarning(null);setParseWarnings([]);
                  setEditingEntry(null);setTab("parser");window.scrollTo(0,0);
                }}>+ Manual Entry</button>
                <button style={{...C.btnRed,fontSize:12,padding:"5px 12px"}} onClick={()=>setShowReversalPanel(v=>!v)}>
                  {showReversalPanel?"Cancel":"+ Add Reversal"}
                </button>
                <button style={C.btnSm} onClick={()=>{
                  const rows=[["Invoice #","Date","Rep","Customer","Amount","Tier","ComplyPro","3rd Party","Locked"]];
                  sorted.forEach(e=>rows.push([e.invoice,e.dateStr,e.rep,e.customer,e.subTotal.toFixed(2),TIER_LABELS[e.tier]||e.tier,e.isComplyPro?"Yes":"",e.needs3rdParty?"Yes":"",e.approvedAt?"Yes":""]));
                  revRows.forEach(r=>rows.push([r.invoice||"—",r.dateStr,r.rep,"REVERSAL: "+r.note,r.amount.toFixed(2),"reversal","","",""]));
                  const csv=rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
                  const blob=new Blob([csv],{type:"text/csv"});const url=URL.createObjectURL(blob);const a=document.createElement("a");
                  a.href=url;a.download=`Certags_SalesLog_${month.replace(" ","_")}.csv`;a.click();URL.revokeObjectURL(url);
                }}>Export CSV ↓</button>
              </div>
            </div>

            {showReversalPanel&&<ReversalPanel reversalForm={reversalForm} setReversalForm={setReversalForm} saveReversal={saveReversal} reversalSaving={reversalSaving} setShowReversalPanel={setShowReversalPanel} C={C}/>}

            <div style={{display:"grid",gridTemplateColumns:colTemplate,gap:8,fontSize:11,color:"#888",fontWeight:500,textTransform:"uppercase",padding:"0 0 8px",borderBottom:"0.5px solid #e0e0e0",marginBottom:4}}>
              <span>Invoice</span><span>Date</span>{inSearch&&<span>Month</span>}<span>Rep</span><span>Customer</span><span>Amount</span><span>Type</span><span>CP</span><span>3P</span><span></span>
            </div>

            {visibleEntries.length===0&&visibleRevs.length===0
              ?<div style={{textAlign:"center",color:"#888",fontSize:14,padding:"2rem"}}>
                  {inSearch?(searchingAllMonths?"Searching…":"No matches for that search."):"No orders this month yet."}
                </div>
              :<>
                {visibleEntries.map(e=>{
                  const locked=!!e.approvedAt;
                  return(
                  <div key={e.id} style={{display:"grid",gridTemplateColumns:colTemplate,gap:8,padding:"8px 0",borderBottom:"0.5px solid #f0f0f0",fontSize:13,alignItems:"center"}}>
                    <span style={{fontFamily:"monospace",fontSize:12,display:"flex",alignItems:"center",gap:4}}>
                      {locked&&<span title={`Locked — approved ${new Date(e.approvedAt).toLocaleDateString()}`} style={{color:"#888",fontSize:11}}>🔒</span>}
                      #{e.invoice}
                    </span>
                    <span style={{fontSize:12,color:"#888"}}>{e.dateStr}</span>
                    {inSearch&&<span style={{fontSize:11,color:"#5B21B6",fontWeight:500}}>{e.month}</span>}
                    <span style={{fontWeight:500}}>{e.rep}</span>
                    <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.customer}</span>
                    <span style={{color:e.isZero?"#aaa":(e.isCredit?"#c00":"#111")}}>{e.isZero?"$0.00":<>{fmt(e.subTotal)}{e.isCredit&&<span style={{marginLeft:4,fontSize:10,background:"#FEE2E2",color:"#991B1B",padding:"1px 5px",borderRadius:10,fontWeight:500}}>CR</span>}{(e.feeTotal||0)>0&&<span style={{marginLeft:4,fontSize:10,color:"#888"}} title={`Sub ${fmt(e.subTotal)} − Fees ${fmt(e.feeTotal)} = Comm ${fmt(e.commissionableAmount)}`}>−{fmt(e.feeTotal)} fees</span>}</>}</span>
                    <span><Badge tier={e.tier} rep={e.rep}/>{e.rep==="DDJ"&&isNewAccount(e)&&<span style={{background:"#EAF3DE",color:"#3B6D11",padding:"2px 6px",borderRadius:20,fontSize:10,fontWeight:500}}>New</span>}</span>
                    <span>{e.isComplyPro&&<span style={{background:"#E6F1FB",color:"#185FA5",padding:"2px 6px",borderRadius:20,fontSize:10,fontWeight:500}}>CP</span>}</span>
                    <span>{e.needs3rdParty&&<span style={{background:"#FAECE7",color:"#993C1D",padding:"2px 6px",borderRadius:20,fontSize:10,fontWeight:500}}>3P</span>}</span>
                    <div style={{display:"flex",gap:2,alignItems:"center",justifyContent:"flex-end"}}>
                      <button onClick={()=>!locked&&startEdit(e)} disabled={locked} title={locked?"Locked — approved entries can't be edited":"Edit"} style={{background:"none",border:"none",color:locked?"#ddd":"#aaa",cursor:locked?"not-allowed":"pointer",fontSize:12,padding:"2px 4px"}}>✏</button>
                      <button onClick={()=>!locked&&deleteEntry(e.id)} disabled={locked} title={locked?"Locked — approved entries can't be deleted":"Delete"} style={{background:"none",border:"none",color:locked?"#ddd":"#aaa",cursor:locked?"not-allowed":"pointer",fontSize:13,padding:"2px 4px"}}>✕</button>
                    </div>
                  </div>
                  );
                })}
                {visibleRevs.map(r=>{
                  // Resolve tier (own row or invoice_ref lookup). When tier
                  // is resolved we show the formula-applied commission impact
                  // inline; otherwise we show a yellow ⚠ + a tier picker that
                  // patches adjustments.tier so the resolver succeeds next
                  // render.
                  const resolvedTier = resolveReversalTier(r.adj, monthEntries);
                  const impact = reversalCommImpact(r.adj, monthEntries);
                  return(
                  <div key={r.id} style={{display:"grid",gridTemplateColumns:colTemplate,gap:8,padding:"8px 0",borderBottom:"0.5px solid #f0f0f0",fontSize:13,alignItems:"center",background:"#FFF5F5"}}>
                    <span style={{fontFamily:"monospace",fontSize:12,color:"#c00"}}>#{r.invoice}</span>
                    <span style={{fontSize:12,color:"#c00"}}>{r.dateStr}</span>
                    <span style={{fontWeight:500,color:"#c00"}}>{r.rep}</span>
                    <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:"#888",fontStyle:"italic"}}>{r.note}</span>
                    <span style={{color:"#c00",fontWeight:500}}>{fmt(r.amount)}</span>
                    <span>
                      {resolvedTier?(
                        <span style={{display:"inline-flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
                          <Badge tier={resolvedTier} rep={r.rep}/>
                          <span style={{fontSize:11,color:"#c00",fontWeight:500}}>{fmt(impact)}</span>
                        </span>
                      ):(
                        <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                          <span title="No tier on this reversal — commission impact can't be computed. Pick a tier to apply." style={{background:"#FEF3C7",color:"#92400E",padding:"2px 6px",borderRadius:10,fontSize:10,fontWeight:600,whiteSpace:"nowrap"}}>⚠ tier</span>
                          <select
                            defaultValue=""
                            onChange={e=>{if(e.target.value)updateReversalTier(r.adjId,e.target.value);}}
                            style={{fontSize:11,padding:"2px 4px",border:"0.5px solid #ccc",borderRadius:4,background:"#fff",maxWidth:78}}
                          >
                            <option value="" disabled>Set…</option>
                            <option value="new">New</option>
                            <option value="0-12">0–12 mo</option>
                            <option value="1-2">1–2 yr</option>
                            <option value="2-3">2–3 yr</option>
                            <option value="3plus">3+ yr</option>
                            <option value="key">KEY</option>
                            <option value="inherited">Inherited</option>
                            <option value="generated">Generated</option>
                            <option value="flat">Flat 2%</option>
                            <option value="zerocomm">Zero comm</option>
                          </select>
                        </span>
                      )}
                    </span>
                    <span/><span/>
                    <div style={{display:"flex",justifyContent:"flex-end"}}>
                      <button onClick={()=>removeAdjustment(r.adjId)} title="Delete reversal" style={{background:"none",border:"none",color:"#f5c6c6",cursor:"pointer",fontSize:13,padding:"2px 4px"}}>✕</button>
                    </div>
                  </div>
                  );
                })}
              </>
            }

            {!inSearch&&(
              <div style={{marginTop:12,fontSize:13,color:"#888",borderTop:"0.5px solid #e0e0e0",paddingTop:10,display:"flex",gap:20,flexWrap:"wrap"}}>
                <span>New accounts: <strong style={{color:"#1a7a3a"}}>{sortedSales.filter(isNewAccount).length}</strong></span>
                <span>CP orders: <strong style={{color:"#185FA5"}}>{sortedSales.filter(e=>e.isComplyPro).length}</strong></span>
                <span>3rd party: <strong style={{color:"#993C1D"}}>{sorted.filter(e=>e.needs3rdParty).length}</strong></span>
                {sorted.some(e=>e.approvedAt)&&<span>Locked: <strong style={{color:"#555"}}>{sorted.filter(e=>e.approvedAt).length}</strong></span>}
                {salesReversalTotal<0&&<span>Reversals: <strong style={{color:"#c00"}}>{revRows.length}</strong></span>}
              </div>
            )}
          </div>
        );
      })()}

      {tab==="complypro"&&(()=>{
        const cpEntries=monthEntries.filter(e=>e.isComplyPro||e.rep==="DDJ");
        const cpTotal=cpEntries.reduce((s,e)=>s+e.subTotal,0);
        const cpPrinters=cpEntries.filter(e=>e.isPrinter);const cpSupplies=cpEntries.filter(e=>!e.isPrinter);
        const cpPct=monthTotal>0?(cpTotal/monthTotal)*100:0;
        const byRep=Object.keys(REPS).map(code=>{const es=cpEntries.filter(e=>e.rep===code);if(!es.length)return null;return{code,total:es.reduce((s,e)=>s+e.subTotal,0),count:es.length,printers:es.filter(e=>e.isPrinter).length};}).filter(Boolean).sort((a,b)=>b.total-a.total);
        return(
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:"1rem"}}>
              <MetricCard label="CP Monthly Total" value={fmt(cpTotal)}/><MetricCard label="% of Total Sales" value={cpPct.toFixed(1)+"%"}/>
              <MetricCard label="Printer Sales" value={cpPrinters.length}/><MetricCard label="Supply Orders" value={cpSupplies.length}/>
            </div>
            <div style={C.card}>
              <div style={C.sec}>By rep</div>
              {byRep.length===0?<div style={{fontSize:13,color:"#888"}}>No ComplyPro orders this month</div>
                :byRep.map((d,i)=>(<div key={d.code} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:i<byRep.length-1?"0.5px solid #f0f0f0":"none"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}><span style={{fontWeight:500}}>{REPS[d.code]?.name||d.code}</span><span style={{fontSize:12,color:"#888"}}>{d.code}</span>{d.printers>0&&<span style={{background:"#E6F1FB",color:"#185FA5",padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:500}}>{d.printers} printer{d.printers!==1?"s":""}</span>}</div>
                  <div style={{textAlign:"right"}}><div style={{fontWeight:500}}>{fmt(d.total)}</div><div style={{fontSize:12,color:"#888"}}>{d.count} orders</div></div>
                </div>))
              }
            </div>
            <div style={C.card}>
              <div style={C.sec}>Order detail</div>
              {cpEntries.length===0?<div style={{fontSize:13,color:"#888"}}>No ComplyPro orders this month</div>
                :<>
                  <div style={{display:"grid",gridTemplateColumns:"85px 1fr 70px 100px 90px",gap:8,fontSize:11,color:"#888",fontWeight:500,textTransform:"uppercase",paddingBottom:6,borderBottom:"0.5px solid #e0e0e0"}}>
                    <span>Invoice</span><span>Customer</span><span>Rep</span><span>Amount</span><span>Type</span>
                  </div>
                  {cpEntries.sort((a,b)=>b.subTotal-a.subTotal).map(e=>(<div key={e.id} style={{display:"grid",gridTemplateColumns:"85px 1fr 70px 100px 90px",gap:8,padding:"7px 0",borderBottom:"0.5px solid #f0f0f0",fontSize:13,alignItems:"center"}}>
                    <span style={{fontFamily:"monospace",fontSize:12}}>#{e.invoice}</span>
                    <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.customer}</span>
                    <span style={{color:"#888"}}>{e.rep}</span><span>{fmt(e.subTotal)}</span>
                    <span>{e.isPrinter?<span style={{background:"#E6F1FB",color:"#185FA5",padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:500}}>Printer</span>:e.isComplyPro?<span style={{background:"#EAF3DE",color:"#3B6D11",padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:500}}>Supplies</span>:null}</span>
                  </div>))}
                  <div style={{marginTop:10,fontSize:13,color:"#666",display:"flex",gap:20}}><span>Total: <strong>{fmt(cpTotal)}</strong></span><span>Printers: <strong>{cpPrinters.length}</strong></span><span>Supply orders: <strong>{cpSupplies.length}</strong></span></div>
                </>
              }
            </div>
          </div>
        );
      })()}

      {tab==="action"&&(()=>{
        const allFlagged=unhandledActions;
        const flagged=actionFilter==="all"
          ?allFlagged
          :allFlagged.filter(e=>(e.actionType||"3rd_party")===actionFilter);
        const counts={
          all:allFlagged.length,
          "3rd_party":allFlagged.filter(e=>(e.actionType||"3rd_party")==="3rd_party").length,
          duplicate:allFlagged.filter(e=>e.actionType==="duplicate").length,
          credit_memo:allFlagged.filter(e=>e.actionType==="credit_memo").length,
        };
        const totalValue=flagged.reduce((s,e)=>s+(e.subTotal||e.amount||0),0);
        const visibleHandled=handledActions.filter(isHandledVisible);

        // The comment row — a third line under the action row, because there is
        // no detail view to put it in. A Needs Action item is two lines in a
        // flat list and nothing opens.
        //
        // Collapsed to a plain text affordance when the row has no comment, so
        // an untouched item stays exactly as quiet as it is today and the
        // presence of the block is itself half the signal.
        const commentBlock=(e)=>{
          const stored=e.statusComment||"";
          const draft=statusDrafts[e.id];
          const value=resolveCommentValue(e,draft);
          const state=statusSaving[e.id];
          const open=openComments[e.id]||stored!==""||draft!==undefined;

          if(!open) return(
            <div style={{marginTop:6}}>
              <button onClick={()=>setOpenComments(prev=>({...prev,[e.id]:true}))}
                style={{background:"none",border:"none",padding:0,fontSize:11,color:"#bbb",cursor:"pointer"}}>
                + Add comment
              </button>
            </div>
          );

          // Byline and save state share a slot. Who is waiting on what matters
          // more often than whether a keystroke landed, so the transient save
          // marker borrows the slot and gives it back.
          const byline=e.statusCommentAt
            ?commentAuthorLabel(e.statusCommentBy)+" · "+new Date(e.statusCommentAt).toLocaleDateString([],{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})
            :"";

          return(
            <div style={{marginTop:6,paddingLeft:10,borderLeft:"2px solid "+COMMENT_META.border}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:8,marginBottom:3}}>
                <span style={{fontSize:10,fontWeight:500,color:COMMENT_META.color,textTransform:"uppercase",letterSpacing:".04em"}}>Comment</span>
                <span style={{fontSize:10,color:"#bbb",whiteSpace:"nowrap"}}>
                  {state==="saving"?"Saving…"
                    :state==="saved"?<span style={{color:"#3B6D11"}}>Saved ✓</span>
                    :state==="error"?<span style={{color:"#c00"}}>Not saved — retry</span>
                    :byline}
                </span>
              </div>
              <textarea
                rows={2}
                value={value}
                onChange={ev=>onStatusCommentChange(e.id,ev.target.value)}
                onBlur={ev=>onStatusCommentBlur(e.id,ev.target.value)}
                placeholder="Add comment here"
                style={{...C.inp,fontSize:12,padding:"5px 10px",background:COMMENT_META.bg,border:"0.5px solid "+COMMENT_META.border,resize:"vertical",fontFamily:"inherit",lineHeight:1.4}}
              />
            </div>
          );
        };

        const pillBtn=(key,label)=>{
          const active=actionFilter===key;
          const count=counts[key]||0;
          const meta=key==="all"?{bg:"#111",color:"#fff",border:"#111"}:ACTION_TYPE_META[key];
          return(
            <button key={key} onClick={()=>setActionFilter(key)}
              style={{
                padding:"5px 12px",fontSize:12,fontWeight:500,cursor:"pointer",
                border:`0.5px solid ${active?meta.border:"#ddd"}`,
                borderRadius:20,
                background:active?(key==="all"?"#111":meta.bg):"#fff",
                color:active?(key==="all"?"#fff":meta.color):"#888",
                whiteSpace:"nowrap",
              }}>
              {label}
              <span style={{
                marginLeft:6,fontSize:11,opacity:0.8,
                background:active?"rgba(255,255,255,0.2)":"#f0f0f0",
                color:active?(key==="all"?"#fff":meta.color):"#888",
                padding:"0 6px",borderRadius:10,
              }}>{count}</span>
            </button>
          );
        };

        return(
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:"1rem"}}>
              <div style={{background:"#FAECE7",borderRadius:8,padding:"12px 16px",textAlign:"center",border:"0.5px solid #F0997B"}}>
                <div style={{fontSize:12,color:"#993C1D",marginBottom:4}}>3rd Party</div>
                <div style={{fontSize:22,fontWeight:500,color:"#993C1D"}}>{counts["3rd_party"]}</div>
              </div>
              <div style={{background:"#E6F1FB",borderRadius:8,padding:"12px 16px",textAlign:"center",border:"0.5px solid #B5D4F4"}}>
                <div style={{fontSize:12,color:"#185FA5",marginBottom:4}}>Duplicates</div>
                <div style={{fontSize:22,fontWeight:500,color:"#185FA5"}}>{counts.duplicate}</div>
              </div>
              <div style={{background:"#f5f5f3",borderRadius:8,padding:"12px 16px",textAlign:"center"}}>
                <div style={{fontSize:12,color:"#888",marginBottom:4}}>Alert Email</div>
                <div style={{fontSize:12,fontWeight:500,color:"#555",marginTop:4}}>ccollard + tracy@</div>
                <div style={{fontSize:11,color:"#888"}}>via Zapier</div>
              </div>
            </div>

            <div style={{display:"flex",gap:6,marginBottom:"1rem",flexWrap:"wrap"}}>
              {pillBtn("all","All")}
              {pillBtn("3rd_party","3rd Party")}
              {pillBtn("duplicate","Duplicate")}
            </div>

            {flagged.length===0
              ?<div style={{...C.card,textAlign:"center",color:"#888",fontSize:14,padding:"2rem"}}>
                  {actionFilter==="all"?"No pending items — all clear! ✓":`No pending ${ACTION_TYPE_META[actionFilter]?.label.toLowerCase()} items.`}
                </div>
              :<div style={C.card}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={C.sec}>
                    {actionFilter==="all"?"All pending items":`${ACTION_TYPE_META[actionFilter]?.label} items`}
                    {flagged.length>0&&<span style={{marginLeft:8,color:"#888",fontSize:11,textTransform:"none",letterSpacing:0}}>· total value {fmt(totalValue)}</span>}
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"100px 85px 1fr 60px 100px 80px 1fr",gap:8,fontSize:11,color:"#888",fontWeight:500,textTransform:"uppercase",paddingBottom:6,borderBottom:"0.5px solid #e0e0e0"}}>
                  <span>Type</span><span>Invoice</span><span>Customer</span><span>Rep</span><span>Amount</span><span>Month</span><span>Details</span>
                </div>
                {flagged.map((e,i)=>{
                  const t=e.actionType||"3rd_party";
                  const meta=ACTION_TYPE_META[t]||ACTION_TYPE_META["3rd_party"];
                  return(
                    <div key={e.id} style={{padding:"10px 0",borderBottom:i<flagged.length-1?"0.5px solid #f0f0f0":"none"}}>
                      <div style={{display:"grid",gridTemplateColumns:"100px 85px 1fr 60px 100px 80px 1fr",gap:8,fontSize:13,alignItems:"center",marginBottom:6}}>
                        <span>
                          <span style={{background:meta.bg,color:meta.color,padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:500,whiteSpace:"nowrap"}}>{meta.label}</span>
                        </span>
                        {/* Show the REAL invoice number. The -DUP suffix exists
                            only to clear the unique index; the person reading
                            this is looking for the invoice they were asked
                            about. The suffix trails in grey so the stored row
                            is still findable. */}
                        <span style={{fontFamily:"monospace",fontSize:12}}>
                          #{t==="duplicate"?realInvoiceOf(e.invoice):e.invoice}
                          {t==="duplicate"&&realInvoiceOf(e.invoice)!==e.invoice&&
                            <span style={{color:"#bbb",fontSize:10,marginLeft:2}}>{String(e.invoice).slice(realInvoiceOf(e.invoice).length)}</span>}
                        </span>
                        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.customer}</span>
                        <span style={{color:"#888"}}>{e.rep}</span>
                        <span style={t==="duplicate"?{color:"#aaa"}:{}}>{fmt(e.subTotal||e.amount||0)}{(e.feeTotal||0)>0&&<span style={{fontSize:10,color:"#888",marginLeft:4}}>(−{fmt(e.feeTotal)} fees)</span>}{e.isCredit&&<span style={{marginLeft:4,fontSize:10,background:"#FEE2E2",color:"#991B1B",padding:"1px 5px",borderRadius:10,fontWeight:500}}>CR</span>}</span>
                        <span style={{fontSize:11,color:"#aaa"}}>{e.month}</span>
                        <div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
                          {(e.flaggedItems||[]).map((item,idx)=>(
                            <span key={idx} style={{background:meta.bg,color:meta.color,padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:500}}>{item}</span>
                          ))}
                          {/* Reads the STORED comment, never the draft — the chip
                              means "there is a saved comment on this row", which
                              is what makes the list scannable and what makes it
                              double as confirmation that the save landed. Full
                              text on hover; the third line has it in full. */}
                          {hasStatusComment(e)&&(
                            <span title={e.statusComment}
                              style={{background:COMMENT_META.bg,color:COMMENT_META.color,border:"0.5px solid "+COMMENT_META.border,padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:500,whiteSpace:"nowrap"}}>
                              💬 Comment
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{display:"flex",gap:8,alignItems:"center",paddingLeft:0}}>
                        <input
                          value={actionNotes[e.id]||""}
                          onChange={ev=>setActionNotes(prev=>({...prev,[e.id]:ev.target.value}))}
                          placeholder={t==="duplicate"?"Note (e.g. Adjusted total from $900 to $450)":"Note / PO# (optional)"}
                          style={{...C.inp,flex:1,fontSize:12,padding:"5px 10px"}}
                        />
                        {t==="duplicate"
                          ?<>
                            {/* Two outcomes, never one. "Mark handled" clears
                                actionType, which for a duplicate promotes it
                                into the Sales Log — the opposite of dismissing
                                it. Splitting the button is what keeps a
                                dismissal from silently counting as a sale. */}
                            <button onClick={()=>dismissDuplicate(e.id)} title="Redundant second email. Stays excluded from sales and commission."
                              style={{background:"#f5f5f3",color:"#555",border:"0.5px solid #d0d0d0",borderRadius:8,padding:"5px 12px",fontSize:12,fontWeight:500,cursor:"pointer",whiteSpace:"nowrap"}}>Dismiss</button>
                            <button onClick={()=>openPromoteDialog(e.id)} title={`Real sale. Choose the full amount or just the increase over #${realInvoiceOf(e.invoice)}.`}
                              style={{background:"#EAF3DE",color:"#3B6D11",border:"0.5px solid #3B6D11",borderRadius:8,padding:"5px 12px",fontSize:12,fontWeight:500,cursor:"pointer",whiteSpace:"nowrap"}}>Promote to sale →</button>
                          </>
                          :<button onClick={()=>markActioned(e.id)} style={{background:"#EAF3DE",color:"#3B6D11",border:"0.5px solid #3B6D11",borderRadius:8,padding:"5px 12px",fontSize:12,fontWeight:500,cursor:"pointer",whiteSpace:"nowrap"}}>Mark handled ✓</button>}
                      </div>
                      {commentBlock(e)}
                    </div>
                  );
                })}
              </div>
            }

            {visibleHandled.length>0&&(
              <div style={{marginTop:"1rem"}}>
                <button onClick={()=>setShowHandled(v=>!v)}
                  style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 16px",background:"#f9f9f8",border:"0.5px solid #e0e0e0",borderRadius:showHandled?"12px 12px 0 0":12,cursor:"pointer",fontSize:13,color:"#888"}}>
                  <span style={{fontWeight:500,color:"#555"}}>Completed / handled <span style={{background:"#e0e0e0",color:"#555",borderRadius:20,padding:"1px 8px",fontSize:11,marginLeft:6}}>{visibleHandled.length}</span></span>
                  <span style={{fontSize:12}}>{showHandled?"▲ Hide":"▼ Show"}</span>
                </button>
                {showHandled&&(
                  <div style={{border:"0.5px solid #e0e0e0",borderTop:"none",borderRadius:"0 0 12px 12px",overflow:"hidden"}}>
                    <div style={{display:"grid",gridTemplateColumns:"100px 85px 1fr 60px 100px 110px 120px 1fr 80px",gap:8,fontSize:11,color:"#888",fontWeight:500,textTransform:"uppercase",padding:"8px 16px",background:"#fafaf9",borderBottom:"0.5px solid #e0e0e0"}}>
                      <span>Type</span><span>Invoice</span><span>Customer</span><span>Rep</span><span>Amount</span><span>Handled</span><span>By</span><span>Notes / Items</span><span></span>
                    </div>
                    {visibleHandled.map((ha,i)=>{
                      const t=ha.action_type||"3rd_party";
                      const meta=ACTION_TYPE_META[t]||ACTION_TYPE_META["3rd_party"];
                      return(
                        <div key={ha.id} style={{display:"grid",gridTemplateColumns:"100px 85px 1fr 60px 100px 110px 120px 1fr 80px",gap:8,padding:"9px 16px",borderBottom:i<visibleHandled.length-1?"0.5px solid #f0f0f0":"none",fontSize:13,alignItems:"center",background:"#fff"}}>
                          <span>
                            <span style={{background:meta.bg,color:meta.color,padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:500,opacity:0.7}}>{meta.label}</span>
                          </span>
                          <span style={{fontFamily:"monospace",fontSize:12,color:"#aaa"}}>#{ha.invoice||"—"}</span>
                          <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:"#888"}}>{ha.customer||"—"}</span>
                          <span style={{color:"#aaa"}}>{ha.rep}</span>
                          <span style={{color:"#aaa"}}>{ha.amount?fmt(ha.amount):"—"}</span>
                          <span style={{fontSize:11,color:"#bbb"}}>{ha.handled_at?new Date(ha.handled_at).toLocaleDateString([],{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}):"—"}</span>
                          <span style={{fontSize:11,color:"#888",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ha.handled_by?ha.handled_by.split("@")[0]:"—"}</span>
                          <div style={{fontSize:12,color:"#555"}}>
                            {ha.notes&&<span style={{display:"block",color:"#185FA5",marginBottom:2}}>{ha.notes}</span>}
                            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                              {(ha.flagged_items||[]).map((item,idx)=>(<span key={idx} style={{background:"#f5f5f3",color:"#888",padding:"2px 6px",borderRadius:20,fontSize:10}}>{item}</span>))}
                            </div>
                          </div>
                          <button onClick={()=>undoHandledAction(ha)}
                            style={{background:"none",border:"0.5px solid #e0e0e0",borderRadius:8,padding:"3px 10px",fontSize:11,color:"#888",cursor:"pointer",whiteSpace:"nowrap"}}>
                            ↩ Undo
                          </button>
                        </div>
                      );
                    })}
                    <div style={{padding:"8px 16px",background:"#fafaf9",fontSize:11,color:"#bbb",borderTop:"0.5px solid #f0f0f0"}}>
                      Showing items handled within the current and previous month. Older items are hidden but retained in the database.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {tab==="commreview"&&(()=>{
        const pending=commFlags.filter(f=>f.status==="pending");
        const reviewed=commFlags.filter(f=>f.status==="reviewed");
        // Per-rep confirmation status for the selected month. Computed from the
        // same repConfirmStatus the rep's own badge uses — if these two ever
        // disagreed there would be no way to tell which was right.
        const repRows=Object.keys(REPS)
          .filter(code=>code!=="CRC")
          .map(code=>({
            code,
            name:REPS[code]?.name||code,
            status:repConfirmStatus(code,(monthConfirmations||[]).find(c=>c.rep===code)?.confirmed_at||null),
            hasEntries:entries.some(e=>e.month===month&&e.rep===code),
          }))
          .filter(r=>r.hasEntries||r.status.state!==CONFIRM_STATE.NONE);
        const staleCount=repRows.filter(r=>r.status.state===CONFIRM_STATE.STALE).length;
        return(
          <div>
            {/* ── Confirmation status ─────────────────────────────────────
                A rep's green badge used to survive any correction made after
                they clicked. This grid is the admin side of the same computed
                check, so "confirmed" here means confirmed against the numbers
                as they stand right now. */}
            <div style={C.card}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:10,flexWrap:"wrap",gap:8}}>
                <div style={C.sec}>Confirmation status — {month}</div>
                {staleCount>0&&(
                  <span style={{fontSize:12,color:"#991B1B",fontWeight:500}}>
                    {staleCount} rep{staleCount===1?"":"s"} confirmed against numbers that have since changed
                  </span>
                )}
              </div>
              {repRows.length===0
                ?<div style={{color:"#888",fontSize:13,padding:"1rem 0"}}>No rep activity for {month}.</div>
                :<div style={{display:"grid",gridTemplateColumns:"120px 1fr 240px 110px",gap:8,fontSize:13,alignItems:"center"}}>
                  <span style={{fontSize:11,color:"#888",fontWeight:500,textTransform:"uppercase",letterSpacing:".04em"}}>Rep</span>
                  <span style={{fontSize:11,color:"#888",fontWeight:500,textTransform:"uppercase",letterSpacing:".04em"}}>Confirmed</span>
                  <span style={{fontSize:11,color:"#888",fontWeight:500,textTransform:"uppercase",letterSpacing:".04em"}}>Status</span>
                  <span/>
                  {repRows.map(r=>{
                    const st=r.status.state;
                    const meta=
                      st===CONFIRM_STATE.STALE?{bg:"#FEE2E2",color:"#991B1B",
                        label:`⚠ Changed since confirmation (${r.status.diff.count})`}
                      :st===CONFIRM_STATE.CONFIRMED?{bg:"#EAF3DE",color:"#3B6D11",label:"✓ Confirmed"}
                      :st===CONFIRM_STATE.LEGACY?{bg:"#EAF3DE",color:"#3B6D11",label:"✓ Confirmed (pre-snapshot)"}
                      :{bg:"#F1EFE8",color:"#6b7280",label:"Not confirmed"};
                    return(
                      <Fragment key={r.code}>
                        <span style={{fontWeight:500}}>{r.name} <span style={{color:"#aaa",fontSize:11}}>{r.code}</span></span>
                        <span style={{color:"#888",fontSize:12}}>
                          {r.status.confirmedAt?new Date(r.status.confirmedAt).toLocaleString([],{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}):"—"}
                        </span>
                        <span>
                          <span
                            onClick={st===CONFIRM_STATE.STALE?()=>setDiffModal({rep:r.code,name:r.name,diff:r.status.diff,confirmedAt:r.status.confirmedAt}):undefined}
                            style={{background:meta.bg,color:meta.color,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:500,whiteSpace:"nowrap",
                              cursor:st===CONFIRM_STATE.STALE?"pointer":"default",
                              border:st===CONFIRM_STATE.STALE?`0.5px solid ${meta.color}`:"none"}}
                            title={st===CONFIRM_STATE.STALE?"Show what changed":undefined}>
                            {meta.label}
                          </span>
                        </span>
                        <span>
                          {st===CONFIRM_STATE.STALE&&(
                            <button style={C.btnSm} onClick={()=>setDiffModal({rep:r.code,name:r.name,diff:r.status.diff,confirmedAt:r.status.confirmedAt})}>
                              View diff
                            </button>
                          )}
                        </span>
                      </Fragment>
                    );
                  })}
                </div>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:"1rem"}}>
              <div style={{background:"#FEF3C7",borderRadius:8,padding:"12px 16px",textAlign:"center",border:"0.5px solid #F59E0B"}}>
                <div style={{fontSize:12,color:"#92400E",marginBottom:4}}>Pending Review</div>
                <div style={{fontSize:24,fontWeight:500,color:"#92400E"}}>{pending.length}</div>
              </div>
              <MetricCard label="Total Reviewed" value={reviewed.length}/>
              <MetricCard label="All Flags" value={commFlags.length}/>
            </div>
            {commFlagsLoading&&<div style={{textAlign:"center",color:"#888",padding:"2rem"}}><Spinner/></div>}
            {!commFlagsLoading&&pending.length===0&&(
              <div style={{...C.card,textAlign:"center",color:"#888",fontSize:14,padding:"2rem"}}>No pending commission flags — all clear! ✓</div>
            )}
            {pending.length>0&&(
              <div style={C.card}>
                <div style={C.sec}>Pending flags</div>
                {pending.map((f,i)=>(
                  <div key={f.id} style={{padding:"14px 0",borderBottom:i<pending.length-1?"0.5px solid #f0f0f0":"none"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,flexWrap:"wrap",gap:8}}>
                      <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
                        {/* The badge is the first thing read, because it decides
                            what the admin does next. An entry flag is a
                            correction to a row we already have; a missing
                            invoice is an investigation into whether a sale
                            exists and who was paid for it. */}
                        {(()=>{const m=FLAG_TYPE_META[flagTypeOf(f)];return(
                          <span style={{background:m.bg,color:m.color,border:`0.5px solid ${m.border}`,padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:500,whiteSpace:"nowrap"}}>{m.label}</span>
                        );})()}
                        <span style={{fontFamily:"monospace",fontSize:12,color:"#888"}}>#{displayInvoice(f)}</span>
                        <span style={{fontWeight:500,fontSize:13}}>{displayText(f.customer)}</span>
                        <span style={{fontSize:12,color:"#888"}}>{f.rep}</span>
                        {/* fmt(null) is "$0.00" — a confident number that is not
                            true. A missing-invoice flag often has no amount at
                            all, because the rep is claiming rather than reading
                            off a record. An em dash asks a question; $0.00
                            answers one wrongly. A real disputed zero still
                            renders as $0.00, which is why this tests presence
                            rather than truthiness. */}
                        <span style={{fontSize:12,color:hasAmount(f.amount)?"#555":"#bbb"}}>{hasAmount(f.amount)?fmt(f.amount):"—"}</span>
                        <span style={{fontSize:11,color:"#aaa"}}>{displayText(f.month)}</span>
                      </div>
                      <span style={{fontSize:11,color:"#aaa"}}>{new Date(f.created_at).toLocaleDateString([],{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}</span>
                    </div>
                    {/* What the ledger says, beside what the rep says. Only on
                        PENDING flags — this is decision support, and the
                        decision has already been made on a reviewed one. */}
                    {(()=>{
                      if(isMissingInvoiceFlag(f)){
                        const matches=flagContext.byInvoice[String(f.invoice||"").trim()]||[];
                        // The whole question a missing-invoice flag asks.
                        if(matches.length===0) return(
                          <div style={{fontSize:12,color:"#6B21A8",background:"#FAF5FF",border:"0.5px solid #E9D5FF",borderRadius:8,padding:"7px 10px",marginBottom:8}}>
                            <strong>#{displayInvoice(f)}</strong> is not recorded in the ledger at all — no entry under any rep.
                          </div>
                        );
                        return(
                          <div style={{fontSize:12,color:"#6B21A8",background:"#FAF5FF",border:"0.5px solid #E9D5FF",borderRadius:8,padding:"7px 10px",marginBottom:8,lineHeight:1.6}}>
                            <strong>#{displayInvoice(f)}</strong> is in the ledger:
                            {matches.map(e=>(
                              <div key={e.id} style={{marginTop:2}}>
                                {/* Recorded to someone else is the answer the
                                    flag was raised to get, so it is said
                                    outright rather than left to be inferred
                                    from a rep code in a list. */}
                                {e.rep===f.rep
                                  ?<span>recorded to <strong>{e.rep}</strong> (the rep who raised this)</span>
                                  :<span>recorded to <strong>{e.rep}</strong>, not to {f.rep}</span>}
                                {" · "}{displayText(e.customer)}
                                {" · "}{hasAmount(e.sub_total??e.amount)?fmt(e.sub_total??e.amount):"—"}
                                {" · "}{displayText(e.month)}
                                {e.action_type&&<span style={{color:"#92400E"}}>{" · flagged "}{e.action_type}</span>}
                              </div>
                            ))}
                          </div>
                        );
                      }
                      const led=flagContext.byEntryId[f.entry_id];
                      if(!led) return(
                        <div style={{fontSize:12,color:"#92400E",background:"#FFFBEB",border:"0.5px solid #FDE68A",borderRadius:8,padding:"7px 10px",marginBottom:8}}>
                          The entry this flag points at is no longer in the ledger — it may have been deleted since.
                        </div>
                      );
                      const ledAmt=led.sub_total??led.amount;
                      const differs=hasAmount(ledAmt)&&hasAmount(f.amount)&&Number(ledAmt)!==Number(f.amount);
                      return(
                        <div style={{fontSize:12,color:"#555",background:"#f9f9f8",border:"0.5px solid #e8e8e4",borderRadius:8,padding:"7px 10px",marginBottom:8,display:"flex",gap:14,flexWrap:"wrap",alignItems:"center"}}>
                          <span>Ledger <strong>{hasAmount(ledAmt)?fmt(ledAmt):"—"}</strong></span>
                          {/* Labelled as the rep's figure, never reconciled
                              against the ledger. The two differing IS the
                              dispute — constraining them to match would defeat
                              the feature, which is why GAP-3's amount clause
                              was closed as not-a-gap rather than enforced. */}
                          <span>Rep's figure <strong>{hasAmount(f.amount)?fmt(f.amount):"—"}</strong></span>
                          {differs&&<span style={{color:"#92400E",fontWeight:500}}>differs by {fmt(Math.abs(Number(ledAmt)-Number(f.amount)))}</span>}
                          {led.rep!==f.rep&&<span style={{color:"#92400E",fontWeight:500}}>entry belongs to {led.rep}</span>}
                        </div>
                      );
                    })()}
                    <div style={{background:"#FFFBEB",border:"0.5px solid #F59E0B",borderRadius:8,padding:"10px 12px",fontSize:13,color:"#78350F",marginBottom:10}}>
                      <span style={{fontSize:11,fontWeight:500,color:"#92400E",textTransform:"uppercase",letterSpacing:".04em",display:"block",marginBottom:4}}>{REPS[f.rep]?.name||f.rep}'s note</span>
                      {f.rep_note}
                    </div>
                    {/* Said here rather than in a banner, because this is the
                        moment the expectation forms. Resolving writes status
                        and admin_note and nothing else — it cannot create or
                        reassign an entry, and the database will not let it
                        attach one (comm_flags_shape). Without this line an
                        admin reasonably assumes marking it reviewed did
                        something to the ledger, and the rep goes unpaid. */}
                    {isMissingInvoiceFlag(f)&&(
                      <div style={{fontSize:11,color:"#6B21A8",background:"#F5ECFA",border:"0.5px solid #D8B4FE",borderRadius:8,padding:"7px 10px",marginBottom:8,lineHeight:1.5}}>
                        Resolving this <strong>does not change the ledger</strong>. It records your reply and closes the flag.
                        If the sale is real, add or correct the entry in <strong>Sales Log</strong> first — that is a separate, manual step.
                      </div>
                    )}
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <input
                        value={reviewNotes[f.id]||""}
                        onChange={e=>setReviewNotes(prev=>({...prev,[f.id]:e.target.value}))}
                        placeholder="Admin reply / note (optional)"
                        style={{...C.inp,flex:1,fontSize:12,padding:"5px 10px"}}
                      />
                      <button
                        onClick={()=>markFlagReviewed(f)}
                        style={{background:"#EAF3DE",color:"#3B6D11",border:"0.5px solid #3B6D11",borderRadius:8,padding:"5px 12px",fontSize:12,fontWeight:500,cursor:"pointer",whiteSpace:"nowrap"}}>
                        Mark reviewed ✓
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {reviewed.length>0&&(
              <div style={{marginTop:"1rem"}}>
                <button onClick={()=>setShowReviewedFlags(v=>!v)}
                  style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 16px",background:"#f9f9f8",border:"0.5px solid #e0e0e0",borderRadius:showReviewedFlags?"12px 12px 0 0":12,cursor:"pointer",fontSize:13,color:"#888"}}>
                  <span style={{fontWeight:500,color:"#555"}}>Reviewed <span style={{background:"#e0e0e0",color:"#555",borderRadius:20,padding:"1px 8px",fontSize:11,marginLeft:6}}>{reviewed.length}</span></span>
                  <span style={{fontSize:12}}>{showReviewedFlags?"▲ Hide":"▼ Show"}</span>
                </button>
                {showReviewedFlags&&(
                  <div style={{border:"0.5px solid #e0e0e0",borderTop:"none",borderRadius:"0 0 12px 12px",overflow:"hidden"}}>
                    <div style={{display:"grid",gridTemplateColumns:"110px 85px 1fr 60px 100px 80px 1fr",gap:8,fontSize:11,color:"#888",fontWeight:500,textTransform:"uppercase",padding:"8px 16px",background:"#fafaf9",borderBottom:"0.5px solid #e0e0e0"}}>
                      <span>Type</span><span>Invoice</span><span>Customer</span><span>Rep</span><span>Amount</span><span>Month</span><span>Notes</span>
                    </div>
                    {reviewed.map((f,i)=>(
                      <div key={f.id} style={{padding:"10px 16px",borderBottom:i<reviewed.length-1?"0.5px solid #f0f0f0":"none",background:"#fff"}}>
                        <div style={{display:"grid",gridTemplateColumns:"110px 85px 1fr 60px 100px 80px 1fr",gap:8,fontSize:13,alignItems:"start",marginBottom:f.rep_note?4:0}}>
                          {/* History needs the type as much as the live list —
                              "why was this closed with no ledger change?" is
                              only answerable if you can see it was never an
                              entry flag. */}
                          {(()=>{const m=FLAG_TYPE_META[flagTypeOf(f)];return(
                            <span><span style={{background:m.bg,color:m.color,border:`0.5px solid ${m.border}`,padding:"1px 7px",borderRadius:20,fontSize:10,fontWeight:500,whiteSpace:"nowrap",opacity:0.85}}>{m.label}</span></span>
                          );})()}
                          <span style={{fontFamily:"monospace",fontSize:12,color:"#aaa"}}>#{displayInvoice(f)}</span>
                          <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:"#888"}}>{displayText(f.customer)}</span>
                          <span style={{color:"#aaa"}}>{f.rep}</span>
                          <span style={{color:"#aaa"}}>{hasAmount(f.amount)?fmt(f.amount):"—"}</span>
                          <span style={{fontSize:11,color:"#bbb"}}>{displayText(f.month)}</span>
                          <span style={{fontSize:12,color:"#aaa",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.admin_note||"—"}</span>
                        </div>
                        {f.rep_note&&<div style={{fontSize:12,color:"#bbb",fontStyle:"italic",marginTop:2}}>"{f.rep_note}"</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

    </div>
  );
}

function ReversalPanel({reversalForm,setReversalForm,saveReversal,reversalSaving,setShowReversalPanel,C}){
  const inp={...C.inp,background:"#fff"};
  return(
    <div style={{...C.card,background:"#FEF2F2",border:"0.5px solid #FECACA",marginBottom:"1rem"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:14,fontWeight:500,color:"#991B1B"}}>📉 Add Sales Reversal</div>
        <button style={{...C.btnSm,color:"#aaa"}} onClick={()=>setShowReversalPanel(false)}>Cancel</button>
      </div>
      <div style={{fontSize:12,color:"#B91C1C",marginBottom:12,background:"#FEE2E2",borderRadius:6,padding:"8px 10px"}}>
        A sales reversal reduces the rep's sales total AND claws back the corresponding commission. Both adjustments are saved and shown in the Sales Log and Dashboard.
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
        <div>
          <div style={{fontSize:11,color:"#888",marginBottom:4}}>Rep *</div>
          <select value={reversalForm.rep} onChange={e=>setReversalForm(f=>({...f,rep:e.target.value}))} style={inp}>
            <option value="" disabled>Select rep…</option>
            {Object.keys(REPS).map(c=><option key={c} value={c}>{c} – {REPS[c].name}</option>)}
          </select>
        </div>
        <div>
          <div style={{fontSize:11,color:"#888",marginBottom:4}}>Sale amount to reverse ($) *</div>
          <input type="number" step="0.01" placeholder="7000.00" value={reversalForm.amount}
            onChange={e=>setReversalForm(f=>({...f,amount:e.target.value}))}
            style={inp}/>
        </div>
      </div>
      <div style={{marginBottom:10}}>
        <div style={{fontSize:11,color:"#888",marginBottom:4}}>Original account tier (for auto commission calc)</div>
        <select value={reversalForm.tier} onChange={e=>setReversalForm(f=>({...f,tier:e.target.value}))} style={inp}>
          <option value="new">New account (20%)</option>
          <option value="0-12">0–12 months (20%)</option>
          <option value="1-2">1–2 years (15%)</option>
          <option value="2-3">2–3 years (10%)</option>
          <option value="3plus">3+ years (10%)</option>
          <option value="key">KEY account (10%)</option>
          <option value="inherited">Inherited DDJ (10%)</option>
          <option value="generated">Generated DDJ (20%)</option>
          <option value="flat">Flat 2% (JRC)</option>
          <option value="zerocomm">Zero comm ($0 commission)</option>
        </select>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <div>
          <div style={{fontSize:11,color:"#888",marginBottom:4}}>Note / reason *</div>
          <input placeholder="e.g. Feb order fell apart, customer cancelled" value={reversalForm.note}
            onChange={e=>setReversalForm(f=>({...f,note:e.target.value}))}
            style={inp}/>
        </div>
        <div>
          <div style={{fontSize:11,color:"#888",marginBottom:4}}>Original invoice # (optional)</div>
          <input placeholder="e.g. 23450" value={reversalForm.invoiceRef}
            onChange={e=>setReversalForm(f=>({...f,invoiceRef:e.target.value}))}
            style={inp}/>
        </div>
      </div>
      {reversalForm.rep&&reversalForm.amount&&(
        <div style={{background:"#FEE2E2",borderRadius:8,padding:"10px 12px",fontSize:13,marginBottom:12,color:"#7F1D1D"}}>
          <strong>Summary:</strong> Will reduce {REPS[reversalForm.rep]?.name}'s sales by {fmt(parseFloat(reversalForm.amount)||0)}. Commission impact flows automatically from the reduced sales total.
        </div>
      )}
      <div style={{display:"flex",gap:8}}>
        <button style={C.btnRed} onClick={saveReversal} disabled={reversalSaving}>
          {reversalSaving?"Saving…":"Save reversal"}
        </button>
        <button style={C.btn} onClick={()=>setShowReversalPanel(false)}>Cancel</button>
      </div>
    </div>
  );
}

function ExportPanel({entries,month,adjustments,exportReviewRep,setExportReviewRep,exportAdjOverrides,setExportAdjOverrides,openExportReview,doExport,doAllRepsSummary,batchExport,batching,xlsxReady,setShowExport,setAdjs,C}){
  const adjTypeLabel=(t)=>t==="bonus"?"Bonus (+)":t==="withholding"?"Withholding (–)":t==="return"?"Return/credit (–)":t==="sales_reversal"?"Sales reversal (–)":t==="zero_comm"?"Zero comm override":t==="reduced_comm"?"Reduced comm":t;
  if(exportReviewRep){
    const code=exportReviewRep;
    const repName=REPS[code]?.name||code;
    const commEs=entries.filter(e=>e.month===month&&e.rep===code&&e.tier!=="loaner"&&e.tier!=="nocomm"&&e.amount!==0&&e.actionType!=="duplicate"&&e.actionType!=="credit_memo");
    const baseComm=commEs.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
    const nonReversalAdjs=exportAdjOverrides.filter(a=>a.type!=="sales_reversal");
    const salesReversalAdjs=exportAdjOverrides.filter(a=>a.type==="sales_reversal");
    const adjTotal=nonReversalAdjs.reduce((s,a)=>s+parseFloat(a.amount||0),0);
    // Reversal commission via the shared resolver — same formula as
    // Dashboard / Payroll so the export preview matches what gets paid.
    const {commTotal:reversalCommTotal}=aggregateReversals(salesReversalAdjs, commEs);
    const totalComp=baseComm+adjTotal+reversalCommTotal;
    return(
      <div style={{...C.card,background:"#f9f9f8",border:"0.5px solid #e0e0e0"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div style={{fontSize:14,fontWeight:500}}>Review before exporting — {repName} ({code})</div>
          <button style={{...C.btnSm,color:"#aaa"}} onClick={()=>setExportReviewRep(null)}>← Back</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:14}}>
          <div style={{background:"#fff",borderRadius:8,padding:"10px 14px",border:"0.5px solid #e0e0e0",textAlign:"center"}}>
            <div style={{fontSize:11,color:"#888",marginBottom:3}}>Base commission</div>
            <div style={{fontSize:16,fontWeight:500}}>{fmt(baseComm)}</div>
          </div>
          <div style={{background:"#fff",borderRadius:8,padding:"10px 14px",border:"0.5px solid #e0e0e0",textAlign:"center"}}>
            <div style={{fontSize:11,color:"#888",marginBottom:3}}>Adjustments</div>
            <div style={{fontSize:16,fontWeight:500,color:adjTotal>=0?"#3B6D11":"#c00"}}>{fmt(adjTotal)}</div>
          </div>
          <div style={{background:"#111",borderRadius:8,padding:"10px 14px",textAlign:"center"}}>
            <div style={{fontSize:11,color:"#aaa",marginBottom:3}}>Total comp</div>
            <div style={{fontSize:16,fontWeight:500,color:"#fff"}}>{fmt(totalComp)}</div>
          </div>
        </div>
        <div style={{fontSize:12,fontWeight:500,color:"#888",textTransform:"uppercase",letterSpacing:".04em",marginBottom:8}}>Adjustments — edit before export</div>
        {exportAdjOverrides.length===0&&<div style={{fontSize:13,color:"#aaa",marginBottom:10}}>No adjustments for this rep this month.</div>}
        {exportAdjOverrides.map((a,i)=>{
          const isNegType=a.type==="withholding"||a.type==="return"||a.type==="sales_reversal";
          const displayVal=isNegType?Math.abs(a.amount):a.amount;
          return(
          <div key={i} style={{display:"flex",gap:8,alignItems:"center",marginBottom:8,flexWrap:"wrap"}}>
            <span style={{fontSize:12,color:"#555",minWidth:160}}>{adjTypeLabel(a.type)}</span>
            <input style={{...C.inp,width:110,fontSize:13}} type="number" step="0.01" value={displayVal}
              onChange={e=>{
                const v=parseFloat(e.target.value)||0;
                const stored=isNegType?-Math.abs(v):Math.abs(v);
                setExportAdjOverrides(prev=>prev.map((x,j)=>j===i?{...x,amount:stored}:x));
              }}/>
            <input style={{...C.inp,flex:1,minWidth:140,fontSize:13}} value={a.note||""}
              onChange={e=>setExportAdjOverrides(prev=>prev.map((x,j)=>j===i?{...x,note:e.target.value}:x))}
              placeholder="Note"/>
            <button style={C.btnDanger} onClick={async()=>{
              if(a.id){try{await deleteAdjustment(a.id);setAdjs(prev=>prev.filter(x=>x.id!==a.id));}catch(e){console.error(e);}}
              setExportAdjOverrides(prev=>prev.filter((_,j)=>j!==i));
            }}>✕</button>
          </div>
          );
        })}
        <div style={{display:"flex",gap:8,marginTop:4,marginBottom:14,flexWrap:"wrap"}}>
          <button style={{...C.btnSm,fontSize:12}} onClick={()=>setExportAdjOverrides(prev=>[...prev,{type:"bonus",amount:0,note:"",rep:code,month}])}>+ Bonus</button>
          <button style={{...C.btnSm,fontSize:12}} onClick={()=>setExportAdjOverrides(prev=>[...prev,{type:"withholding",amount:0,note:"",rep:code,month}])}>+ Withholding</button>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button style={C.btnPri} onClick={()=>doExport(code,exportAdjOverrides)}>{xlsxReady?"Download ↓":"Loading…"}</button>
          <button style={{...C.btnPri,background:"#185FA5"}} onClick={()=>{
            const commEs=entries.filter(e=>e.month===month&&e.rep===code&&e.tier!=="loaner"&&e.tier!=="nocomm"&&e.amount!==0&&e.actionType!=="duplicate"&&e.actionType!=="credit_memo");
            const nonReversalAdjs=exportAdjOverrides.filter(a=>a.type!=="sales_reversal");
            const salesRevAdjs=exportAdjOverrides.filter(a=>a.type==="sales_reversal");
            const adjTotal=nonReversalAdjs.reduce((s,a)=>s+parseFloat(a.amount||0),0);
            const baseComm=commEs.reduce((s,e)=>s+getComm(e.amount,e.rep,e.tier),0);
            // Reversal commission via shared resolver, so the emailed total
            // matches Dashboard/Payroll/Excel statement.
            const {commTotal:reversalCommTotal}=aggregateReversals(salesRevAdjs, commEs);
            const totalComp=baseComm+adjTotal+reversalCommTotal;
            const rawSales=entries.filter(e=>e.month===month&&e.rep===code&&e.tier!=="loaner"&&e.tier!=="nocomm"&&e.actionType!=="duplicate"&&e.actionType!=="credit_memo").reduce((s,e)=>s+e.subTotal,0);
            const salesRevTotal=salesRevAdjs.reduce((s,a)=>s+parseFloat(a.amount||0),0);
            const totalSales=rawSales+salesRevTotal;
            const newEs=commEs.filter(isNewAccount);
            const newCount=newEs.length;
            const newTotal=newEs.reduce((s,e)=>s+e.subTotal,0);
            const fmt2=(n)=>"$"+Number(n).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
            const monthParts=month.split(" ");
            const shortMonth=monthParts[0].slice(0,3)+" "+monthParts[1];
            const subject=encodeURIComponent(`${code} Comms - ${shortMonth}`);
            const body=encodeURIComponent(
`Please find monthly commissions attached.

Total Commissions:  ${fmt2(totalComp)}
Total Sales:  ${fmt2(totalSales)}
# New Accounts:  ${newCount}
Total New Accounts:  ${fmt2(newTotal)}

Please let me know if you have any questions or need modifications.

Thank you,
Chris`
            );
            const email=REPS[code]?.email||"";
            window.open(`mailto:${email}?subject=${subject}&body=${body}`);
          }}>Email ✉</button>
          <button style={C.btn} onClick={()=>setExportReviewRep(null)}>Cancel</button>
          <button style={{...C.btnSm,color:"#3B6D11",borderColor:"#3B6D11"}} onClick={()=>{setExportReviewRep(null);setShowExport(false);}}>Done ✓</button>
        </div>
      </div>
    );
  }
  const repsWithData=Object.keys(REPS).filter(code=>code!=="CRC"&&entries.some(e=>e.month===month&&e.rep===code));
  return(
    <div style={{...C.card,background:"#f9f9f8"}}>
      <div style={{fontSize:13,fontWeight:500,marginBottom:12}}>Export {month} commission sheets</div>
      <div style={{marginBottom:14}}>
        <div style={{fontSize:11,color:"#888",textTransform:"uppercase",letterSpacing:".04em",marginBottom:8}}>Per rep — review adjustments before download</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
          {repsWithData.map(code=>(
            <button key={code} style={{...C.btn,fontSize:13}} onClick={()=>openExportReview(code)}>
              {REPS[code].name} ({code}) ↗
            </button>
          ))}
        </div>
      </div>
      <div style={{borderTop:"0.5px solid #e0e0e0",paddingTop:12,marginBottom:12}}>
        <div style={{fontSize:11,color:"#888",textTransform:"uppercase",letterSpacing:".04em",marginBottom:8}}>Batch — download all reps at once (uses saved adjustments, no review)</div>
        <button style={C.btnPri} onClick={batchExport} disabled={batching}>{batching?"Downloading…":"Download all reps ↓"}</button>
      </div>
      <div style={{borderTop:"0.5px solid #e0e0e0",paddingTop:12}}>
        <div style={{fontSize:11,color:"#888",textTransform:"uppercase",letterSpacing:".04em",marginBottom:8}}>Internal summary (all reps combined)</div>
        <button style={C.btn} onClick={doAllRepsSummary}>{xlsxReady?"Download summary ↓":"Loading…"}</button>
      </div>
      <div style={{marginTop:12}}><button style={{...C.btn,fontSize:12,color:"#aaa"}} onClick={()=>setShowExport(false)}>Close</button></div>
    </div>
  );
}
